import { isUploadControlUnavailable, readUploadKnownSizeControl, uploadObjectControlColumns, type UploadObjectControlRow, type SmallFileObjectControl } from "@uploads";
import Dexie, { type DBCoreTransaction, Table } from "dexie";
import {
	createIndexedDbQuotaErrorPredicate,
	indexedDbErrorChainLogDetails,
	isEvidenceTerminalFailure,
	isPresignedUrlAgeValid,
	isUploadTerminalCompleted,
	isUploadTerminalFailure,
	newUploadId,
	PRESIGNED_URL_SAFETY_MARGIN_MS,
	PRESIGNED_URL_TTL_MS,
	R0_SLOW_TIER_BASE_DELAY_MS,
	R0_SLOW_TIER_MAX_DELAY_MS,
	releasedExpiry,
	type DurableLeaseRecord,
	type DurableLeaseToken,
	type SmallFileCompletionReceiptCheckpoint,
	type SmallFileTargetCheckpoint,
	uploadFailureLogDetails,
	writerLeaseKey
} from "@uploads";
import api from "@/api";
import { getUploadContextDiagnostics, type ObservedUploadContext } from "@/services/uploads/uploadContextDiagnostics";
import { adminUploadTelemetry } from "@/services/uploads/adminUploadTelemetry";
import { hasNoServerUploadTarget } from "@/services/uploads/pendingUploadTarget";
import type {
	AdminUploadAdoptionFence,
	AdminUploadMigrationSource,
	AdminUploadMigrationWriterLease
} from "@/services/uploads/adminUploadMigration";

export { PRESIGNED_URL_SAFETY_MARGIN_MS, PRESIGNED_URL_TTL_MS };

export type FileUploadSessionStatus =
	| "pending"
	| "queued"
	| "uploading"
	| "complete"
	| "cancelled"
	| "needs-attention"
	| "recovery-blocked";

export const UPLOAD_SESSION_INVALIDATION_STORAGE_KEY = "ls-contact-upload-session-invalidated";
// Same name reused for both — this is one logical invalidation channel delivered over two independent
// transports (see below), not two separate channels.
const UPLOAD_SESSION_INVALIDATION_CHANNEL_NAME = UPLOAD_SESSION_INVALIDATION_STORAGE_KEY;
// Keep one publisher channel alive so implementations that queue delivery do not drop a message
// when a freshly-created channel is closed immediately after postMessage().
let uploadSessionInvalidationPublisherChannel: BroadcastChannel | undefined;
export const MAX_UPLOAD_LAST_ERROR_LENGTH = 500;

function parseUploadSessionInvalidationPayload(
	rawPayload: string
): { sessionId: string; uploadId: string | undefined; pageId: string | undefined } | undefined {
	const rawValue = rawPayload.slice(0, 500);
	try {
		const message = JSON.parse(rawPayload) as unknown;
		const parsedSessionId = message && typeof message === "object" ? (message as { sessionId?: unknown }).sessionId : undefined;
		if (typeof parsedSessionId !== "string" || parsedSessionId.trim().length === 0) {
			console.warn("[Upload] Ignoring upload session invalidation without a valid sessionId", { rawValue });
			return undefined;
		}
		const { uploadId: parsedUploadId, pageId: parsedPageId } = message as { uploadId?: unknown; pageId?: unknown };
		return {
			sessionId: parsedSessionId,
			uploadId: typeof parsedUploadId === "string" ? parsedUploadId : undefined,
			pageId: typeof parsedPageId === "string" ? parsedPageId : undefined
		};
	} catch (error) {
		console.warn("[Upload] Ignoring malformed upload session invalidation message", { rawValue, error });
		return undefined;
	}
}

function publishFileUploadSessionInvalidation(sessionId: string, uploadId?: string): void {
	let payload: string;
	try {
		// LSP-23399: BroadcastChannel delivers this page's own message back to it, so name the page that sent it.
		payload = JSON.stringify({ sessionId, uploadId, nonce: newUploadId(), pageId: getCurrentFileUploadLaneId() });
	} catch (error) {
		console.warn("[Upload] Could not notify other tabs about a removed upload session", error);
		return;
	}
	try {
		window.localStorage.setItem(UPLOAD_SESSION_INVALIDATION_STORAGE_KEY, payload);
	} catch (error) {
		console.warn("[Upload] Could not notify other tabs via localStorage about a removed upload session", error);
	}
	// BroadcastChannel is a second, independent delivery path for the SAME event — private-browsing
	// modes and storage quota/security errors that silently break localStorage.setItem do not
	// necessarily block BroadcastChannel, so this keeps cross-tab notification working in more
	// environments. A tab that receives the same invalidation twice (once per transport) is safe:
	// every consumer of this event (registerUploadSessionInvalidation, invalidateUploadSession) is
	// idempotent against a sessionId it has already processed.
	try {
		if (typeof BroadcastChannel !== "undefined") {
			uploadSessionInvalidationPublisherChannel ??= new BroadcastChannel(UPLOAD_SESSION_INVALIDATION_CHANNEL_NAME);
			uploadSessionInvalidationPublisherChannel.postMessage(payload);
		}
	} catch (error) {
		console.warn("[Upload] Could not notify other tabs via BroadcastChannel about a removed upload session", error);
	}
}

export function subscribeFileUploadSessionInvalidations(
	listener: (sessionId: string, uploadId: string | undefined, fromThisPage: boolean) => void
): () => void {
	const deliver = (parsed: ReturnType<typeof parseUploadSessionInvalidationPayload>, rawValue: string) => {
		if (!parsed) return;
		try {
			// A message without a pageId comes from a page loaded before LSP-23399, so it is another page.
			listener(parsed.sessionId, parsed.uploadId, parsed.pageId === getCurrentFileUploadLaneId());
		} catch (error) {
			console.warn("[Upload] Upload session invalidation listener threw", { sessionId: parsed.sessionId, rawValue, error });
		}
	};

	const handleStorage = (event: StorageEvent) => {
		if (event.storageArea !== window.localStorage || event.key !== UPLOAD_SESSION_INVALIDATION_STORAGE_KEY || !event.newValue) return;
		deliver(parseUploadSessionInvalidationPayload(event.newValue), event.newValue.slice(0, 500));
	};
	window.addEventListener("storage", handleStorage);

	let channel: BroadcastChannel | undefined;
	const handleChannelMessage = (event: MessageEvent) => {
		if (typeof event.data !== "string") return;
		deliver(parseUploadSessionInvalidationPayload(event.data), event.data.slice(0, 500));
	};
	try {
		if (typeof BroadcastChannel !== "undefined") {
			channel = new BroadcastChannel(UPLOAD_SESSION_INVALIDATION_CHANNEL_NAME);
			channel.addEventListener("message", handleChannelMessage);
		}
	} catch (error) {
		console.warn("[Upload] Could not subscribe to BroadcastChannel upload session invalidations", error);
	}

	return () => {
		window.removeEventListener("storage", handleStorage);
		channel?.removeEventListener("message", handleChannelMessage);
		channel?.close();
	};
}

function isTerminalFileUploadSessionStatus(status: FileUploadSessionStatus): boolean {
	return status === "complete" || status === "cancelled" || status === "needs-attention" || status === "recovery-blocked";
}
export type FileCategory = "image" | "document" | "video";

/**
 * IDB can throw UnknownError / InternalError when storage is corrupted,
 * quota is exceeded, or the browser encounters an internal issue.
 * These are unrecoverable for the affected data — the session should
 * be cleaned up so it doesn't block the resume loop forever.
 *
 * LSP-16528: DatabaseClosedError is intentionally NOT classified here. On iOS/mobile WebKit the
 * browser force-closes the IndexedDB connection mid-session (tab backgrounded, memory pressure,
 * storage eviction) — that is a transient, recoverable connection drop, not data corruption.
 * Treating it as internal disabled upload resume entirely for the affected tab. The recovery path
 * is withIdbReopen() below, which reopens the connection and retries the operation once.
 */
interface IdbErrorEvidence {
	name: string;
	message: string;
}

export function readIdbErrorEvidence(error: unknown): IdbErrorEvidence[] | null {
	try {
		if (!(error instanceof Error)) return null;
		const evidence: IdbErrorEvidence[] = [];
		const visitedErrors = new Set<object>();
		let candidate: unknown = error;
		while (candidate !== null && typeof candidate === "object" && !visitedErrors.has(candidate)) {
			const errorLike = candidate as { name?: unknown; message?: unknown; inner?: unknown };
			evidence.push({
				name: (errorLike.name ?? "").toString(),
				message: (errorLike.message ?? "").toString().toLowerCase()
			});
			visitedErrors.add(candidate);
			candidate = errorLike.inner;
		}
		return evidence;
	} catch {
		return null;
	}
}

function isConnectionDropEvidence(evidence: IdbErrorEvidence): boolean {
	return (
		evidence.name === "DatabaseClosedError" ||
		evidence.message.includes("database has been closed") ||
		evidence.message.includes("connection to indexed database server lost") ||
		evidence.message.includes("without an in-progress transaction") ||
		evidence.message.includes("transaction aborted")
	);
}

export function isIdbInternalError(error: unknown): boolean {
	const evidence = readIdbErrorEvidence(error);
	if (!evidence) return false;

	// LSP-16528: a dropped connection is recoverable (handled by withIdbReopen), never internal —
	// even though DatabaseClosedError's message ("Connection to Indexed Database server lost") would
	// otherwise trip the "indexed database server" substring below.
	if (evidence.some(isConnectionDropEvidence)) return false;

	return evidence.some(
		(candidate) =>
			candidate.name === "UnknownError" ||
			candidate.name === "InternalError" ||
			candidate.message.includes("internal error") ||
			candidate.message.includes("indexed database server") ||
			candidate.message.includes("failed to read large indexeddb value")
	);
}

/**
 * LSP-16528: errors Dexie throws once iOS/mobile WebKit force-closes the IndexedDB connection
 * mid-session — DatabaseClosedError, or a transaction error caused by the close ("Attempt to get a
 * record without an in-progress transaction" / "Transaction aborted"). These are recoverable by
 * reopening the connection (withIdbReopen), unlike the corruption cases in isIdbInternalError.
 *
 * AbortError is matched only alongside the "transaction aborted" message, NOT by name alone:
 * chunkedUploadService raises a bare AbortError for upload stall-timeouts, which is unrelated to a
 * dropped IDB connection and must not trigger a reopen-and-retry.
 */
function isIdbConnectionDropError(error: unknown): boolean {
	return readIdbErrorEvidence(error)?.some(isConnectionDropEvidence) ?? false;
}

export class IdbStallError extends Error {
	constructor(message: string) {
		super(message);
		this.name = "IdbStallError";
	}
}

/** Stall budget for metadata-sized IndexedDB operations (reads, status writes, lease rows). */
export const IDB_STALL_TIMEOUT_MS = 10_000;
/**
 * LSP-19863: a chunk-DATA write carries megabytes, and under a concurrent large upload in the same
 * origin a 5 MB blob write routinely outlives the 10 s metadata budget above. The budget for a
 * payload-carrying operation is therefore sized from the payload: a base plus a per-256 KiB term,
 * capped so a genuinely dead store still fails within two minutes. 5 MiB → 50 s.
 */
export const IDB_PAYLOAD_STALL_BASE_MS = 30_000;
export const IDB_PAYLOAD_STALL_PER_256KIB_MS = 1_000;
export const IDB_PAYLOAD_STALL_MAX_MS = 120_000;
let idbReopenInFlight: Promise<void> | null = null;
let idbConnectionGeneration = 0;
let consecutiveReopenFailures = 0;
let firstReopenFailureAt: number | null = null;
let connectionLostEventEmitted = false;
const IDB_DIAGNOSTIC_INTERVAL_MS = 60_000;
let totalReopenAttempts = 0;
let totalReopenFailures = 0;
// Fixed platform messages only: error text must never create a counter key.
const IDB_RECOVERY_MESSAGES = [
	"[Upload] IndexedDB reopen failed — surfacing original error",
	"[Upload] IndexedDB recovery skipped — connection generation already recovered",
	"[Upload] IndexedDB connection closed — opening a fresh connection before retry",
	"[Upload] IndexedDB reopen succeeded — retrying stalled operation once",
	"[Upload] IndexedDB operation stalled — aborted its transactions",
	"[Upload] IndexedDB operation stalled while the connection was closed — reopening and retrying once",
	"[Upload] IndexedDB connection dropped (iOS WebKit) — closing and retrying once"
] as const;
type IdbRecoveryMessage = (typeof IDB_RECOVERY_MESSAGES)[number];
const idbWarningCounts = new Map(
	IDB_RECOVERY_MESSAGES.map((message) => [
		message,
		{
			lastLoggedAt: null as number | null,
			suppressedSinceLastLog: 0,
			occurrencesOnPage: 0
		}
	])
);

function readIdbDiagnostic<T>(read: () => T): T | null {
	try {
		return read();
	} catch {
		return null;
	}
}

function idbPageDiagnostics(): Record<string, unknown> {
	return {
		idbConnectionGeneration,
		totalReopenAttempts,
		totalReopenFailures,
		consecutiveReopenFailures,
		secondsSinceFirstFailure:
			firstReopenFailureAt === null ? 0 : readIdbDiagnostic(() => Math.max(0, Date.now() - firstReopenFailureAt!) / 1000),
		visibilityState: readIdbDiagnostic(() => document.visibilityState),
		onLine: readIdbDiagnostic(() => navigator.onLine),
		pageAgeMs: readIdbDiagnostic(() => performance.now())
	};
}

/** Existing recovery warnings are paced per message, including the callers sharing a reopen. */
function warnIdbRecovery(message: IdbRecoveryMessage, details?: Record<string, unknown>): void {
	try {
		const counts = idbWarningCounts.get(message);
		if (!counts) return;
		counts.occurrencesOnPage++;
		// Retain an occurrence even if the clock or sink throws; only a delivered line resets it.
		counts.suppressedSinceLastLog++;
		const now = Date.now();
		if (counts.lastLoggedAt !== null && now - counts.lastLoggedAt < IDB_DIAGNOSTIC_INTERVAL_MS) return;
		counts.lastLoggedAt = now;
		console.warn(message, {
			...idbPageDiagnostics(),
			...details,
			suppressedSinceLastLog: counts.suppressedSinceLastLog - 1,
			occurrencesOnPage: counts.occurrencesOnPage
		});
		counts.suppressedSinceLastLog = 0;
	} catch {
		// Browser globals and diagnostic sinks must never affect reopen/retry control flow.
	}
}

function reportIdbReopenFailure(error: unknown, openStartedAt: number | null): void {
	try {
		totalReopenFailures++;
		const now = Date.now();
		consecutiveReopenFailures++;
		firstReopenFailureAt ??= now;
		const chain = indexedDbErrorChainLogDetails(error);
		const details = {
			...idbPageDiagnostics(),
			...chain,
			errorName: chain.idbError0Name ?? "Error",
			errorMessage: chain.idbError0Message ?? "Upload operation failed",
			msToReject: openStartedAt === null ? null : Math.max(0, now - openStartedAt),
			consecutiveReopenFailures,
			secondsSinceFirstFailure: Math.max(0, now - firstReopenFailureAt) / 1000
		};
		warnIdbRecovery("[Upload] IndexedDB reopen failed — surfacing original error", details);
		const serverLost = readIdbErrorEvidence(error)?.some((link) => link.message.includes("connection to indexed database server lost"));
		if (!connectionLostEventEmitted && (serverLost || consecutiveReopenFailures >= 3)) {
			connectionLostEventEmitted = true;
			adminUploadTelemetry.logWarn("idb.connection.lost", details);
		}
	} catch {
		// Diagnostics are best-effort even for malformed errors or unavailable platform APIs.
	}
}

export interface IdbReopenOptions {
	/** A scan materializing payloads of unknown total size uses the existing bounded payload cap. */
	payloadScan?: boolean;
	/**
	 * Bytes the operation writes to IndexedDB, when it carries a payload. Sizes the stall budget via
	 * `getIdbStallTimeoutMs`; omitted or zero means the metadata budget.
	 */
	payloadBytes?: number;
	/**
	 * `payloadBytes` is an upper bound the caller cannot see past (an enclosing transaction that MAY
	 * carry a chunk write), not the bytes this operation itself wrote. Changes only the stall message,
	 * so a metadata-only commit that stalls is not reported as a payload write.
	 */
	payloadIsUpperBound?: boolean;
	/** LSP-23090: names the operation in the stall diagnostics; the stores and mode are recorded automatically. */
	operationName?: string;
}

export function getIdbStallTimeoutMs(payloadBytes?: number): number {
	if (payloadBytes === undefined || !Number.isFinite(payloadBytes) || payloadBytes <= 0) return IDB_STALL_TIMEOUT_MS;
	const perPayload = IDB_PAYLOAD_STALL_BASE_MS + Math.ceil(payloadBytes / (256 * 1024)) * IDB_PAYLOAD_STALL_PER_256KIB_MS;
	return Math.max(IDB_STALL_TIMEOUT_MS, Math.min(IDB_PAYLOAD_STALL_MAX_MS, perPayload));
}

/**
 * The one place an IdbStallError message is worded (LSP-23004: tests and the upload logs parse it, so it must not
 * drift). The base wording is unchanged so the existing RUM issue keeps grouping the metadata-budget stalls.
 */
export function formatIdbStallMessage(phase: string, timeoutMs: number, payloadBytes?: number, payloadIsUpperBound?: boolean): string {
	// The payload suffix is diagnostic (LSP-19863 AC 3). An upper-bound budget names itself as such so a
	// metadata-only fenced commit is not misread as a chunk write.
	const payloadSuffix =
		payloadBytes !== undefined && payloadBytes > 0
			? payloadIsUpperBound
				? ` (enclosing transaction may carry up to ${payloadBytes} bytes)`
				: ` (payload ${payloadBytes} bytes)`
			: "";
	return `[Upload] IndexedDB ${phase} stalled after ${timeoutMs}ms${payloadSuffix}`;
}

/** Reads back what formatIdbStallMessage wrote: which wrapper phase stalled, its budget, and any payload size. */
export function parseIdbStallMessage(
	message: string
): { phase: string; timeoutMs: number; payloadBytes?: number; payloadIsUpperBound: boolean } | undefined {
	const match = /IndexedDB (.+?) stalled after (\d+)ms(?: \((payload|enclosing transaction may carry up to) (\d+) bytes\))?$/.exec(
		message
	);
	if (!match) return undefined;
	return {
		phase: match[1],
		timeoutMs: Number(match[2]),
		payloadBytes: match[4] === undefined ? undefined : Number(match[4]),
		payloadIsUpperBound: match[3] === "enclosing transaction may carry up to"
	};
}

/**
 * LSP-23090: the IndexedDB transactions one bounded attempt opened. A stalled attempt used to be abandoned while its
 * transaction kept its store locks, so every later transaction on those stores (the retry included) queued behind it.
 * The attempt runs inside a Dexie zone carrying this scope, and the dbcore middleware on AdminFileUploadDatabase
 * records each transaction opened from that zone so the stall path can abort them. A nested withIdbReopen attempt
 * owns its own transactions under its own budget: a parent's stall never aborts them.
 */
interface IdbStallScope {
	readonly transactions: ScopedIdbTransaction[];
}

interface ScopedIdbTransaction {
	readonly transaction: DBCoreTransaction;
	readonly stores: readonly string[];
	readonly mode: string;
	/**
	 * Set once the transaction commits or aborts (a request "error" can be handled and the transaction continues).
	 * Unknown liveness (no event target) counts as live.
	 */
	finished: boolean;
}

const IDB_STALL_SCOPE_ZONE_KEY = "adminIdbStallScope";

/**
 * Dexie's zone API (`Dexie.Promise.PSD` / `newPSD`) is public at runtime but absent from its typings. Dexie carries the
 * zone across `await`s of its own promises; a transaction opened after awaiting a non-Dexie promise may land outside
 * the zone, and then simply is not aborted (the pre-LSP-23090 behaviour). Concurrent and nested operations are covered
 * against mis-attribution by tests/unit/db/fileUploadDb.lsp23090-breakit.spec.ts (1a-1d, 4a-4c).
 */
interface DexieZoneApi {
	readonly PSD?: Record<string, unknown>;
	newPSD?: <R>(scopeFunction: () => R, zoneProperties: Record<string, unknown>) => R;
}

function dexieZoneApi(): DexieZoneApi {
	return Dexie.Promise as unknown as DexieZoneApi;
}

function currentIdbStallScope(): IdbStallScope | undefined {
	return readIdbDiagnostic(() => dexieZoneApi().PSD?.[IDB_STALL_SCOPE_ZONE_KEY] as IdbStallScope | undefined) ?? undefined;
}

function recordScopedIdbTransaction(transaction: DBCoreTransaction, stores: readonly string[], mode: string): void {
	const scope = currentIdbStallScope();
	if (!scope) return;
	const record: ScopedIdbTransaction = { transaction, stores, mode, finished: false };
	const events = transaction as Partial<Pick<EventTarget, "addEventListener">>;
	if (typeof events.addEventListener === "function") {
		const markFinished = () => {
			record.finished = true;
		};
		for (const type of ["complete", "abort"]) events.addEventListener.call(transaction, type, markFinished);
	}
	scope.transactions.push(record);
}

function runInIdbStallScope<T>(scope: IdbStallScope, operation: () => Promise<T>): Promise<T> {
	const newPSD = dexieZoneApi().newPSD;
	if (typeof newPSD !== "function") return Promise.resolve().then(operation);
	// The operation still starts a microtask later, as before LSP-23090: callers' interleavings depend on it. Inside the
	// zone the global Promise is Dexie's own, exactly as inside any Dexie transaction callback; that is what carries the
	// zone across the operation's awaits. A promise the operation itself creates and rejects with a raw IndexedDB-named
	// DOMException is therefore wrapped in the matching Dexie error (same name, original as `inner`), as Dexie already
	// does for every rejection from its own requests; other errors keep their identity.
	return Promise.resolve().then(() => newPSD(operation, { [IDB_STALL_SCOPE_ZONE_KEY]: scope }));
}

/** Aborts every transaction the stalled attempt opened; one that already committed or aborted refuses, which is fine. */
function abortIdbStallScope(scope: IdbStallScope): { abortedTransactions: number; finishedTransactions: number } {
	let abortedTransactions = 0;
	let finishedTransactions = 0;
	for (const { transaction, finished } of scope.transactions) {
		if (finished) {
			finishedTransactions++;
			continue;
		}
		try {
			transaction.abort();
			abortedTransactions++;
		} catch {
			finishedTransactions++;
		}
	}
	return { abortedTransactions, finishedTransactions };
}

/**
 * A metadata-budget attempt still holding the chunk-data store readwrite may be deleting a whole file's bytes, which can
 * legitimately outlive the 10 s metadata budget. Before LSP-23090 such a slow delete still committed in the
 * background after its caller gave up; now that a stall aborts the transaction, aborting it at 10 s would roll the
 * delete back on every attempt. Its budget extends once, to the payload cap, before it is treated as wedged.
 */
function chunkDataWriteBudgetExtensionMs(scope: IdbStallScope, timeoutMs: number): number {
	if (timeoutMs !== IDB_STALL_TIMEOUT_MS) return 0;
	const holdsChunkDataForWrite = scope.transactions.some(
		({ stores, mode, finished }) => !finished && mode === "readwrite" && stores.includes("fileUploadChunkData")
	);
	return holdsChunkDataForWrite ? IDB_PAYLOAD_STALL_MAX_MS - timeoutMs : 0;
}

/** One bounded attempt of a withIdbReopen operation: on a stall its transactions are aborted before IdbStallError. */
function runIdbAttempt<T>(operation: () => Promise<T>, phase: string, timeoutMs: number, options?: IdbReopenOptions): Promise<T> {
	const scope: IdbStallScope = { transactions: [] };
	const startedAt = Date.now();
	let budgetMs = timeoutMs;
	let timeoutId: ReturnType<typeof globalThis.setTimeout> | undefined;
	const stallDetails = () => ({
		...idbPageDiagnostics(),
		operationName: options?.operationName ?? "unnamed",
		phase,
		// Joined strings: the upload telemetry sink keeps scalar fields only.
		stores: [...new Set(scope.transactions.flatMap(({ stores }) => stores))].join(","),
		modes: [...new Set(scope.transactions.map(({ mode }) => mode))].join(","),
		transactionCount: scope.transactions.length,
		payloadBytes: options?.payloadBytes ?? 0,
		payloadIsUpperBound: options?.payloadIsUpperBound ?? false,
		timeoutMs: budgetMs,
		elapsedMs: Date.now() - startedAt
	});
	const timeout = new Promise<never>((_, reject) => {
		const onDeadline = () => {
			try {
				const extensionMs = budgetMs === timeoutMs ? chunkDataWriteBudgetExtensionMs(scope, timeoutMs) : 0;
				if (extensionMs > 0) {
					budgetMs += extensionMs;
					adminUploadTelemetry.logInfo("idb.operation.budgetExtended", { ...stallDetails(), initialTimeoutMs: timeoutMs });
					timeoutId = globalThis.setTimeout(onDeadline, extensionMs);
					return;
				}
				const details = { ...stallDetails(), ...abortIdbStallScope(scope), dbOpen: db.isOpen() };
				// Every stall reaches Datadog with searchable fields (the console line below is paced per minute).
				adminUploadTelemetry.logWarn("idb.operation.stalled", details);
				warnIdbRecovery("[Upload] IndexedDB operation stalled — aborted its transactions", details);
			} catch {
				// Abort and diagnostics are best-effort; the stall still rejects.
			}
			reject(new IdbStallError(formatIdbStallMessage(phase, budgetMs, options?.payloadBytes, options?.payloadIsUpperBound)));
		};
		timeoutId = globalThis.setTimeout(onDeadline, timeoutMs);
	});
	return Promise.race([runInIdbStallScope(scope, operation), timeout]).finally(() => {
		if (timeoutId !== undefined) globalThis.clearTimeout(timeoutId);
	});
}

function withIdbStallTimeout<T>(promise: Promise<T>, phase: string, timeoutMs = IDB_STALL_TIMEOUT_MS): Promise<T> {
	let timeoutId: ReturnType<typeof globalThis.setTimeout> | undefined;
	const timeout = new Promise<never>((_, reject) => {
		timeoutId = globalThis.setTimeout(() => {
			reject(new IdbStallError(formatIdbStallMessage(phase, timeoutMs)));
		}, timeoutMs);
	});

	return Promise.race([promise, timeout]).finally(() => {
		if (timeoutId !== undefined) globalThis.clearTimeout(timeoutId);
	});
}

async function recoverIdbConnection(observedGeneration: number): Promise<void> {
	// The operation may report its failure after another caller has already repaired the shared
	// connection. That error belongs to the older connection lifetime; closing here would tear down
	// the fresh handle and can start the close/open storm seen in Safari during LSP-19645.
	if (idbConnectionGeneration !== observedGeneration && db.isOpen()) {
		warnIdbRecovery("[Upload] IndexedDB recovery skipped — connection generation already recovered", {
			observedGeneration,
			currentGeneration: idbConnectionGeneration
		});
		return;
	}
	if (!idbReopenInFlight) {
		idbReopenInFlight = (async () => {
			let openStartedAt: number | null = null;
			try {
				readIdbDiagnostic(() => totalReopenAttempts++);
				db.close();
				warnIdbRecovery("[Upload] IndexedDB connection closed — opening a fresh connection before retry");
				openStartedAt = readIdbDiagnostic(() => Date.now());
				await withIdbStallTimeout(db.open(), "reopen");
				idbConnectionGeneration++;
				consecutiveReopenFailures = 0;
				firstReopenFailureAt = null;
				warnIdbRecovery("[Upload] IndexedDB reopen succeeded — retrying stalled operation once");
			} catch (error) {
				reportIdbReopenFailure(error, openStartedAt);
				throw error;
			}
		})().finally(() => {
			idbReopenInFlight = null;
		});
	}
	await idbReopenInFlight;
}

/**
 * LSP-16528: iOS/mobile WebKit force-closes the IndexedDB connection mid-session (tab backgrounded,
 * memory pressure, storage eviction). The connection is fine to reopen — we just lost the live
 * handle. This wrapper runs `operation`; if it trips a closed-connection error OR the db is
 * observably closed, it reopens the singleton and retries exactly once. Any other error rethrows
 * untouched. The single-attempt cap means a genuinely dead connection can't spin.
 *
 * LSP-23090: an attempt that outlives its stall budget has the transactions it opened aborted (releasing their store
 * locks) and is retried once on the same connection; only a closed connection is reopened first.
 *
 * Apply at the IDB call sites that drive the upload progress/resume layer so a backgrounded mobile
 * tab recovers instead of disabling resume for the rest of the session.
 */
export async function withIdbReopen<T>(operation: () => Promise<T>, options?: IdbReopenOptions): Promise<T> {
	// LSP-19863: inside an enclosing Dexie transaction (the fenced writer commit wraps the chunk
	// write, the identity re-read and the heartbeat restamp in one transaction) this wrapper must
	// not race its own timer or touch the connection. The enclosing transaction owns the lifetime,
	// and `db.close()` from inside it aborts that transaction AND every other live transaction in
	// the tab — the "Database has been closed" storms and the "writer identity changed before
	// fenced commit" failures in the LSP-19645 incident were exactly that.
	if (Dexie.currentTransaction) return operation();
	const operationGeneration = idbConnectionGeneration;
	const timeoutMs = options?.payloadScan ? IDB_PAYLOAD_STALL_MAX_MS : getIdbStallTimeoutMs(options?.payloadBytes);
	try {
		return await runIdbAttempt(operation, "operation", timeoutMs, options);
	} catch (error) {
		const stalled = error instanceof IdbStallError;
		// LSP-23090: a stall is a slow or wedged transaction, not a dropped connection. runIdbAttempt already aborted
		// the stalled attempt's transactions, so the retry runs on the same connection. Closing and reopening here
		// aborted nothing, and the reopen itself waited behind the wedged transaction.
		const connectionDropped = isIdbConnectionDropError(error) || !db.isOpen();
		if (!stalled && !connectionDropped) throw error;
		if (connectionDropped) {
			const details = {
				...indexedDbErrorChainLogDetails(error),
				operationName: options?.operationName ?? "unnamed",
				payloadBytes: options?.payloadBytes ?? 0,
				timeoutMs,
				observedGeneration: operationGeneration
			};
			warnIdbRecovery(
				stalled
					? "[Upload] IndexedDB operation stalled while the connection was closed — reopening and retrying once"
					: "[Upload] IndexedDB connection dropped (iOS WebKit) — closing and retrying once",
				details
			);
			try {
				await recoverIdbConnection(operationGeneration);
			} catch {
				throw error;
			}
		}
		return await runIdbAttempt(operation, "operation retry", timeoutMs, options);
	}
}

/**
 * Distinguishes IDB QuotaExceededError from other storage errors.
 * Thrown when the browser's storage quota for the origin is exhausted.
 */
export const isIdbQuotaError = createIndexedDbQuotaErrorPredicate(Dexie.errnames.QuotaExceeded);

export interface FileUploadSession extends ObservedUploadContext, UploadObjectControlRow {
	/** LSP-19521: source is persisted before the first server target is minted. */
	targetPending?: boolean;
	/**
	 * LSP-23105: `false` once the row's first target is attached, `true` once a later target replaced it
	 * (R-1 repoint). A tray row holding the `pending-target:` placeholder may cancel only a row stamped
	 * `false`; a row attached before this field existed carries neither and is refused. Not indexed.
	 */
	targetReattached?: boolean;
	/** Set before initiation; cancellation must reconcile an ambiguous server response. */
	targetInitiationStarted?: boolean;
	sourcePrepared?: boolean;
	retainBatchHistory?: boolean;
	batchOrganizationId?: string;
	/** LSP-22950 O8: the user who added the batch; the drain holds the row while a different user is signed in. Not indexed. */
	batchUserId?: string;
	batchFileIndex?: number;
	completedAt?: number;
	id: string;
	/** Durable grouping for files selected in one user upload action. Not indexed. */
	batchId?: string;
	conversationId: string;
	fileName: string;
	fileSize: number;
	fileType: string;
	uploadMethod: "multipart";
	fileCategory: FileCategory;
	uploadId: string;
	s3Key: string;
	bucket: string;
	totalChunks: number;
	chunkUrlsJson: string;
	urlCreatedAt: number;
	status: FileUploadSessionStatus;
	createdAt: number;
	cloudHosted: boolean;
	/** Immutable wire-family pin. Uploads-family rows retain only opaque target metadata. */
	endpointFamily?: "legacy" | "uploads";
	registryId?: string;
	attemptId?: string;
	generation?: number;
	takeGeneration?: string;
	targetEnvelopesJson?: string;
	/**
	 * The transport the `/uploads` facade minted for this attempt (LSP-17653 plan 3a). Absent on every
	 * row persisted before it, which reads as "multipart". Declared here because the shared
	 * `MultipartApiPort` target Picks include it.
	 */
	transport?: "multipart" | "chunked-object";
	/**
	 * Chunk size this row's chunks were sliced with. A chunked-object re-mint rebuilds its manifest
	 * from (fileSize, chunkSizeBytes) alone — a reloaded tab has no File to walk again.
	 */
	chunkSizeBytes?: number;
	/** JSON array of 0-based chunk indexes in upload priority order (header-first). */
	priorityChunkIndexesJson?: string;
	/** Source strategy selected before persistence; absent on pre-WO-3 rows. */
	sourceReadStrategy?: "slice" | "arrayBuffer" | "stream";
	retryAt?: number | null;
	retryAttempt?: number;
	/**
	 * LSP-19912: when this session was FIRST answered with the server's settling verdict
	 * (`UPLOAD_OPERATION_IN_PROGRESS`) by the `/uploads` admission gate. That verdict is retryable
	 * forever by classification, and `adminChunkedUploadDriver.classifyFailure` answers it with
	 * `retry` — which releases the row to the paced drain WITHOUT rethrowing — so a permanently
	 * wedged owner would keep this durable row re-initiating indefinitely. The instant has to live
	 * on the row rather than in the driver instance: the loop being bounded is precisely one that
	 * outlives the tab that started it. Mirrors `AdminSmallFileUpload.settlingConflictSince` and
	 * contact-async's `FileUploadSession.settlingConflictSince`. Not indexed.
	 */
	settlingConflictSince?: number;
	projectChecklistItemId?: string;
	photoTime?: string;
	captureEvidence?: Record<string, unknown>;
	// LSP-16326: identifies the tab that owns an active ("uploading"/"queued") session,
	// plus a liveness heartbeat. Lets resetUploadingSessions() avoid stealing another
	// live tab's in-flight upload while still recovering genuinely orphaned rows.
	// Not indexed — no Dexie schema version bump needed.
	uploadOwnerId?: string;
	uploadOwnerHeartbeatAt?: number;
	uploadOwnerClaimId?: string;
	// LSP-19963: monotonic cancellation epoch. An ownerless pending snapshot captured before a
	// cancellation carries the preceding value and cannot use the cancelled-row re-entry carve-out.
	// Absent on legacy rows means generation zero. Not indexed.
	uploadCancellationGeneration?: number;
	// LSP-19537: "a live upload lane in some tab still intends to upload this pending row". Set by
	// the pre-persist handoff (adminSessionStore.finishPrePersist), cleared when that lane finishes
	// with or gives up the row (releaseFileUploadLaneIntent). NOT an owner: the row stays
	// ownerless/pending and the durable writer fence (claim()) is untouched by these two cells alone.
	// Liveness of the lane is answered by getFileUploadLaneIntentLiveness, never by these cells in
	// isolation. Absent on every row written before this ticket, which reads as "no lane intends
	// it" — today's behavior. Not indexed — no Dexie schema version bump needed, same footing as
	// uploadOwnerId / retryCeilingReachedAt.
	laneIntentId?: string;
	laneIntentAt?: number;
	// LSP-17823: last terminal failure message the driver supplied for this session.
	// Not indexed — the value is persisted for diagnostics and recovery triage only.
	lastError?: string;
	// LSP-19384: the instant this row stopped being retried AUTOMATICALLY. A marker on a row that
	// stays `needs-attention`, deliberately NOT a new status: five guards enumerate the terminal
	// statuses by name, and a sixth status would fall outside the user-cancel carve-out (making the
	// row uncancellable) and outside deleteChunkDataForSession's carve-out (making its chunk bytes
	// unreclaimable — a permanent IndexedDB hold). The row, its bytes, and Retry/Remove all stay.
	// Not indexed. Absent on every row written before this ticket, which reads as "not terminal".
	retryCeilingReachedAt?: number;
	// LSP-19384: failures that DEMONSTRABLY reached the network, and the instant the first one did.
	// Deliberately NOT `retryAttempt`, which is not a monotonic failure count — four sites reset it
	// (markFileUploadSessionPendingForResume writes `undefined` when called with no options, and three
	// callers do exactly that, including the exhausted-retry release; reinitializeTarget sets 0), so a
	// ceiling on it fires on the wrong population. These two only ever move forward, and only for a
	// failure the connectivity gate accepted.
	evidencedFailureCount?: number;
	firstEvidencedFailureAt?: number;
	/** Immutable migration provenance; populated by the bridge before a new adapter adopts a row. */
	uploadEngineFamily?: "legacy" | "unified";
	uploadSchemaVersion?: number;
	uploadAdapterContractVersion?: number;
	uploadAdoptionState?: "legacy" | "adopted";
}

export interface CaptureEvidenceManifestRecord {
	id: string;
	conversationId: string;
	sourceType: "snapshot" | "recording";
	sourceClientId: string;
	manifest: Record<string, unknown>;
	createdAt: number;
	attemptCount: number;
	retryAt: number;
	lastError: string | null;
}

export interface FileUploadMigrationChunk {
	meta: FileUploadChunkMeta;
	data?: ArrayBuffer;
}

/**
 * Lightweight chunk metadata — safe to bulk-query without memory concerns.
 */
export interface FileUploadChunkMeta {
	sessionId: string;
	chunkIndex: number;
	isUploaded: number;
	etag: string | null;
	size: number;
}

/**
 * Heavy chunk data — contains the actual ArrayBuffer payload.
 * Only read one record at a time to avoid crashing IDB on large files.
 */
export interface FileUploadChunkData {
	sessionId: string;
	chunkIndex: number;
	buffer: ArrayBuffer;
}

/** Durable single-PUT bytes used by the Admin snapshot/annotation/rotation adapter. */
export interface AdminSmallFileUpload extends SmallFileObjectControl {
	id: string;
	conversationId?: string;
	organizationId?: string;
	/** Legacy rows may contain a Blob; new writes persist only payloadBytes for WebKit. */
	payload?: Blob;
	/** Structured-clone-safe image bytes. */
	payloadBytes?: ArrayBuffer;
	fileName: string;
	fileType: string;
	category: "image" | "document";
	operation: "snapshot" | "annotation" | "rotation" | "logo" | "virtual-background";
	snapshotId?: string;
	parentId?: string | null;
	idempotencyId?: string;
	metadata?: Record<string, unknown>;
	/** This v5 store is unshipped: every row is created through the uploads facade. */
	endpointFamily?: "uploads";
	/** Exact immutable uploads-family target, persisted before the PUT. */
	target?: SmallFileTargetCheckpoint;
	/** Exact ready/current receipt for a targetless idempotent create replay. */
	completionReceipt?: SmallFileCompletionReceiptCheckpoint;
	createdAt: number;
	retryAt?: number | null;
	retryAttempt?: number;
	/**
	 * LSP-19912: when this row was FIRST answered with the server's settling verdict
	 * (`UPLOAD_OPERATION_IN_PROGRESS`). That verdict is retryable forever by classification, so the
	 * lane needs a durable — reload-surviving — start instant to bound how long it keeps
	 * optimistically retrying before parking the row for attention. Cleared whenever the row is
	 * re-enqueued.
	 */
	settlingConflictSince?: number;
	status?: "pending" | "uploaded" | "needs-attention";
	lastError?: string;
}

function warnUploadCasMismatch(message: string, context?: Record<string, unknown>): void {
	try {
		// Callers that already carry every detail in the message pass no context; keeping the argument list
		// identical to a bare console.warn matters because refusal-path assertions pin the exact call shape.
		if (context === undefined) console.warn(message);
		else console.warn(message, context);
	} catch {
		// A diagnostic failure must not turn a stale CAS no-op into a rejected write.
	}
}

function getSafeUploadOwnerMismatchContext(
	context: Record<string, unknown>,
	expectedOwnerId: unknown,
	actualOwnerId: unknown
): Record<string, unknown> {
	const expectedOwnerIsInvalid = expectedOwnerId !== undefined && !isValidFileUploadOwnerId(expectedOwnerId);
	const actualOwnerIsInvalid = actualOwnerId !== undefined && !isValidFileUploadOwnerId(actualOwnerId);
	return {
		...context,
		...(expectedOwnerIsInvalid ? { expectedOwnerIdLength: getFileUploadOwnerIdLength(expectedOwnerId) } : { expectedOwnerId }),
		...(actualOwnerIsInvalid ? { actualOwnerIdLength: getFileUploadOwnerIdLength(actualOwnerId) } : { actualOwnerId }),
		// Never clobber a reason the caller already resolved (markFileUploadSessionPendingForResume passes a
		// more specific one); an invalid identity only fills the gap when nothing else named the cause.
		...((expectedOwnerIsInvalid || actualOwnerIsInvalid) && context.reason === undefined ? { reason: "invalid-owner-id" } : {})
	};
}

function hasInvalidUploadOwnerMismatch(expectedOwnerId: unknown, actualOwnerId: unknown): boolean {
	return (
		(expectedOwnerId !== undefined && !isValidFileUploadOwnerId(expectedOwnerId)) ||
		(actualOwnerId !== undefined && !isValidFileUploadOwnerId(actualOwnerId))
	);
}

/**
 * Combined type for backward-compat in tests and callers that still
 * need both metadata + buffer together for a single chunk.
 */
export interface FileUploadChunk extends FileUploadChunkMeta, FileUploadChunkData {}

export class AdminFileUploadDatabase extends Dexie {
	fileUploadSessions!: Table<FileUploadSession>;
	largeFileUploadSessions!: Table<FileUploadSession>;
	fileUploadChunks!: Table<FileUploadChunkMeta>;
	fileUploadChunkData!: Table<FileUploadChunkData>;
	smallFileUploads!: Table<AdminSmallFileUpload>;
	captureEvidenceManifests!: Table<CaptureEvidenceManifestRecord>;

	constructor(databaseName = "AdminFileUploads") {
		super(databaseName);

		this.version(1).stores({
			fileUploadSessions: "id, conversationId, status, createdAt, uploadMethod, uploadId",
			fileUploadChunks: "[sessionId+chunkIndex], sessionId, isUploaded, [sessionId+isUploaded]"
		});

		this.version(2)
			.stores({
				fileUploadChunkData: "[sessionId+chunkIndex], sessionId"
			})
			.upgrade(async (tx) => {
				await tx.table("fileUploadChunks").clear();
				await tx.table("fileUploadSessions").clear();
			});

		// P-8 coordination records intentionally live beside the durable upload
		// rows. That lets a writer epoch be checked in the exact transaction that
		// persists a chunk/checkpoint or deletes local bytes.
		this.version(3).stores({
			leases: "key, originKey, itemId, expiresAt",
			work: "originKey, workGeneration",
			poison: "key, itemId, lastFailureAt"
		});

		// Additive WO-3 fields. Existing session rows and chunk bytes are retained; legacy
		// sessions default to the safe random-access strategy rather than being re-read.
		this.version(4)
			.stores({
				fileUploadSessions: "id, conversationId, status, createdAt, uploadMethod, uploadId"
			})
			.upgrade(async (tx) => {
				await tx
					.table("fileUploadSessions")
					.toCollection()
					.modify((session: FileUploadSession) => {
						if (session.sourceReadStrategy === undefined) session.sourceReadStrategy = "slice";
					});
			});

		this.version(5).stores({
			smallFileUploads: "id, conversationId, status, createdAt, operation, idempotencyId"
		});

		// >1,000-part manifests live in a control store old quadratic bundles cannot discover.
		// Existing/active rows stay in the original store, so an upgrade cannot interrupt pre-persist.
		this.version(6).stores({ largeFileUploadSessions: "id, conversationId, status, createdAt, uploadMethod, uploadId" });

		// LSP-19963: give pre-deployment cancellation tombstones a durable fence. Any stale in-memory
		// snapshot remains generation-less while a fresh post-upgrade read carries generation 1.
		this.version(7)
			.stores({})
			.upgrade(async (transaction) => {
				for (const tableName of ["fileUploadSessions", "largeFileUploadSessions"]) {
					await transaction
						.table(tableName)
						.where("status")
						.equals("cancelled")
						.modify((session: FileUploadSession) => {
							if (session.uploadCancellationGeneration === undefined) session.uploadCancellationGeneration = 1;
						});
				}
			});

		// LSP-18592: capture-evidence manifests queue for sidecar metadata awaiting upload.
		this.version(8).stores({ captureEvidenceManifests: "id, conversationId, sourceType, sourceClientId, retryAt, createdAt" });

		// LSP-23090: lets a stalled withIdbReopen attempt find, and abort, the transactions it opened.
		this.use({
			stack: "dbcore",
			name: "AdminIdbStallScope",
			create: (down) => ({
				...down,
				transaction: (stores, mode, options) => {
					const transaction = down.transaction(stores, mode, options);
					recordScopedIdbTransaction(transaction, stores, mode);
					return transaction;
				}
			})
		});
	}
}

export const db = new AdminFileUploadDatabase();

const LEGACY_MULTIPART_PART_CAPACITY = 1_000;
export const fileUploadSessionControlTables = [db.fileUploadSessions, db.largeFileUploadSessions] as const;

export async function enqueueCaptureEvidenceManifest(
	input: Omit<CaptureEvidenceManifestRecord, "id" | "createdAt" | "attemptCount" | "retryAt" | "lastError">
): Promise<void> {
	const id = `${input.sourceType}:${input.sourceClientId}:1`;
	await db.transaction("rw", db.captureEvidenceManifests, async () => {
		if (await db.captureEvidenceManifests.get(id)) return;
		await db.captureEvidenceManifests.put({ ...input, id, createdAt: Date.now(), attemptCount: 0, retryAt: 0, lastError: null });
	});
}

export async function listDueCaptureEvidenceManifests(now = Date.now()): Promise<CaptureEvidenceManifestRecord[]> {
	return db.captureEvidenceManifests.where("retryAt").belowOrEqual(now).toArray();
}

export async function markCaptureEvidenceManifestSubmitted(id: string): Promise<void> {
	await db.captureEvidenceManifests.delete(id);
}

export async function clearCaptureEvidenceManifest(sourceClientId: string): Promise<void> {
	await db.captureEvidenceManifests.where("sourceClientId").equals(sourceClientId).delete();
}

export async function markCaptureEvidenceManifestRetry(id: string, error: unknown): Promise<void> {
	const current = await db.captureEvidenceManifests.get(id);
	if (!current) return;
	const attemptCount = current.attemptCount + 1;
	await db.captureEvidenceManifests.update(id, {
		attemptCount,
		retryAt: Date.now() + Math.min(R0_SLOW_TIER_MAX_DELAY_MS, 2 ** Math.min(attemptCount, 8) * 1_000),
		lastError: error instanceof Error ? error.message.slice(0, 500) : String(error).slice(0, 500)
	});
}

export async function readFileUploadSession(sessionId: string): Promise<FileUploadSession | undefined> {
	return (await db.largeFileUploadSessions.get(sessionId)) ?? db.fileUploadSessions.get(sessionId);
}

export async function readFileUploadSessionByUploadId(uploadId: string): Promise<FileUploadSession | undefined> {
	return (await db.largeFileUploadSessions.where({ uploadId }).first()) ?? (await db.fileUploadSessions.where({ uploadId }).first());
}

export async function putFileUploadSession(session: FileUploadSession): Promise<void> {
	const large =
		(session.totalChunks ?? 0) > LEGACY_MULTIPART_PART_CAPACITY || (await db.largeFileUploadSessions.get(session.id)) !== undefined;
	await (large ? db.largeFileUploadSessions : db.fileUploadSessions).put(session);
}

async function addFileUploadSession(session: FileUploadSession): Promise<void> {
	// Preserve the original store's put-by-id semantics when an application reuses a session id.
	const target = session.totalChunks > LEGACY_MULTIPART_PART_CAPACITY ? db.largeFileUploadSessions : db.fileUploadSessions;
	const alternate = target === db.largeFileUploadSessions ? db.fileUploadSessions : db.largeFileUploadSessions;
	if (await target.get(session.id)) await target.put(session);
	else {
		await alternate.delete(session.id);
		await target.add(session);
	}
}

export async function updateFileUploadSession(sessionId: string, changes: Partial<FileUploadSession>): Promise<number> {
	const largeUpdated = await db.largeFileUploadSessions.update(sessionId, changes);
	return largeUpdated || db.fileUploadSessions.update(sessionId, changes);
}

async function modifyFileUploadSession(
	sessionId: string,
	predicate: (session: FileUploadSession) => boolean,
	modifier: (session: FileUploadSession) => void
): Promise<number> {
	const largeUpdated = await db.largeFileUploadSessions.where("id").equals(sessionId).and(predicate).modify(modifier);
	return largeUpdated || db.fileUploadSessions.where("id").equals(sessionId).and(predicate).modify(modifier);
}

async function deleteFileUploadSessionControl(sessionId: string): Promise<void> {
	await db.largeFileUploadSessions.delete(sessionId);
	await db.fileUploadSessions.delete(sessionId);
}

export async function listFileUploadSessions(): Promise<FileUploadSession[]> {
	const [small, large] = await Promise.all([db.fileUploadSessions.toArray(), db.largeFileUploadSessions.toArray()]);
	return [...small, ...large];
}

export async function listFileUploadSessionsByStatus(...statuses: FileUploadSessionStatus[]): Promise<FileUploadSession[]> {
	const [small, large] = await Promise.all([
		db.fileUploadSessions
			.where("status")
			.anyOf(...statuses)
			.toArray(),
		db.largeFileUploadSessions
			.where("status")
			.anyOf(...statuses)
			.toArray()
	]);
	return [...small, ...large];
}

// LSP-16326: per-tab ownership for active upload sessions.
export const FILE_UPLOAD_SESSION_OWNER_STALE_MS = 5 * 60 * 1000; // a heartbeat older than this means the owning tab is gone

// LSP-19537: the single shared TTL for BOTH the durable cross-tab lane-intent lease (below) and
// FileUploadDialog's in-memory batch-stall guard (formerly a locally-defined BATCH_LANE_STALL_MS).
// The two must never drift apart: a lane the in-memory guard still considers alive but the durable
// lease has let expire (or vice versa) is exactly the split-brain this design exists to prevent.
export const ADMIN_LANE_INTENT_TTL_MS = 5 * 60 * 1000;

// LSP-19537: one UUID per page load, memoized for the page's lifetime. Deliberately NOT
// uploadOwnerId (sessionStorage-backed, so a duplicated or reloaded tab shares/reuses it) — a lane
// must be exactly one page load, the same trick adminUploadDrain's startupOwnerClaimId already uses.
let currentFileUploadLaneId: string | undefined;

export function getCurrentFileUploadLaneId(): string {
	if (currentFileUploadLaneId === undefined) {
		currentFileUploadLaneId = newUploadId();
	}
	return currentFileUploadLaneId;
}

export function getFileUploadDiagnosticContext() {
	return getUploadContextDiagnostics(getCurrentFileUploadSessionOwnerId(), getCurrentFileUploadLaneId());
}

const FILE_UPLOAD_SESSION_OWNER_ID_KEY = "contact-admin:file-upload-owner-id";
const FILE_UPLOAD_OWNER_LOCK_GRACE_MS = 60_000;
const FILE_UPLOAD_OWNER_LOCK_PREFIX = "ls-admin-upload-owner:";
const NIL_FILE_UPLOAD_OWNER_ID = "00000000-0000-0000-0000-000000000000";
const FILE_UPLOAD_OWNER_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const activeUploadStatuses: FileUploadSessionStatus[] = ["uploading", "queued"];
// Memoizes the resolved per-tab owner id so sessionStorage is read at most once per tab.
let cachedUploadOwnerId: string | null = null;
let uploadOwnerLockManagerForTests: Pick<LockManager, "request"> | null | undefined;
const requestedUploadOwnerLivenessLocks = new Set<string>();
const acquiredUploadOwnerLivenessLocks = new Set<string>();
const inFlightLaneIntentLockProbes = new Map<string, Promise<boolean | null>>();
// LSP-22113: rows a sweep proved to be orphans of a dead page load with this owner id, whose fenced
// reset the dead page's unexpired writer lease refused. Keyed by session id; the value is the row and
// the refusing writer lease as they were when proven. See `isStillProvenPriorLoadOrphan`.
const provenPriorLoadOrphans = new Map<string, { session: FileUploadSession; writerLease: string }>();
let currentPageInitiatedFileUpload = false;
const currentPageInitiatedFileUploadSessionIdentities = new Map<string, Map<string, number>>();

export function markCurrentPageInitiatedFileUploadSession(session: Pick<FileUploadSession, "id" | "uploadId">): void {
	const uploadIdCounts = currentPageInitiatedFileUploadSessionIdentities.get(session.id) ?? new Map<string, number>();
	uploadIdCounts.set(session.uploadId, (uploadIdCounts.get(session.uploadId) ?? 0) + 1);
	currentPageInitiatedFileUploadSessionIdentities.set(session.id, uploadIdCounts);
}

export function forgetCurrentPageInitiatedFileUploadSession(session: Pick<FileUploadSession, "id" | "uploadId">): void {
	const uploadIdCounts = currentPageInitiatedFileUploadSessionIdentities.get(session.id);
	const nextCount = (uploadIdCounts?.get(session.uploadId) ?? 0) - 1;
	if (nextCount > 0) uploadIdCounts?.set(session.uploadId, nextCount);
	else uploadIdCounts?.delete(session.uploadId);
	if (uploadIdCounts?.size === 0) currentPageInitiatedFileUploadSessionIdentities.delete(session.id);
}

function isCurrentPageInitiatedFileUploadSession(session: Pick<FileUploadSession, "id" | "uploadId">): boolean {
	return (currentPageInitiatedFileUploadSessionIdentities.get(session.id)?.get(session.uploadId) ?? 0) > 0;
}

/**
 * Retires every current-page admission after a terminal transition or row deletion. Lease release
 * must decrement only its own refcount so a replacement scope remains protected.
 */
export function forgetCurrentPageInitiatedFileUploadSessions(sessionId: string): void {
	currentPageInitiatedFileUploadSessionIdentities.delete(sessionId);
}

export function setUploadOwnerLockManagerForTests(lockManager: Pick<LockManager, "request"> | null | undefined): void {
	uploadOwnerLockManagerForTests = lockManager;
	requestedUploadOwnerLivenessLocks.clear();
	acquiredUploadOwnerLivenessLocks.clear();
	inFlightLaneIntentLockProbes.clear();
	provenPriorLoadOrphans.clear();
	currentPageInitiatedFileUpload = false;
	currentPageInitiatedFileUploadSessionIdentities.clear();
}

function getUploadOwnerLockManager(): Pick<LockManager, "request"> | undefined {
	if (uploadOwnerLockManagerForTests !== undefined) return uploadOwnerLockManagerForTests ?? undefined;
	if (typeof navigator === "undefined") return undefined;
	return navigator.locks;
}

function getUploadOwnerLockName(ownerId: string): string {
	return `${FILE_UPLOAD_OWNER_LOCK_PREFIX}${ownerId}`;
}

/** LSP-18217: persisted owner identity is an authorization boundary, not a display value. */
export function isValidFileUploadOwnerId(value: unknown): value is string {
	return typeof value === "string" && FILE_UPLOAD_OWNER_ID_PATTERN.test(value) && value.toLowerCase() !== NIL_FILE_UPLOAD_OWNER_ID;
}

// IndexedDB and sessionStorage are untyped at runtime, so a persisted owner may not even be a
// string. Diagnostics report only its length so a corrupt value is never echoed into the logs.
export function getFileUploadOwnerIdLength(value: unknown): number | undefined {
	return typeof value === "string" ? value.length : undefined;
}

// IndexedDB structured-clones every read, so two reads of the same non-primitive cell are never `===`.
// A corrupt owner or claim can hold such a value, which made every CAS over it fail forever and stranded
// the row. Collapsing non-primitives to one sentinel makes two reads of the same corrupt row compare
// equal, while two DIFFERENT string values still compare unequal — so a real claim or owner moving under
// us is still detected. `undefined` keeps its own identity: ownerless is a distinct state, not corruption.
const NON_STRING_UPLOAD_OWNER_CELL = Symbol("non-string upload owner cell");

export function toComparableUploadOwnerCell(value: unknown): unknown {
	if (value === undefined || typeof value === "string") return value;
	return NON_STRING_UPLOAD_OWNER_CELL;
}

// A persisted heartbeat is as untrusted as the CORRUPT owner id beside it: the same bad write, or a
// device whose clock jumped forward and was then corrected, can leave a non-finite or future timestamp
// that reads as permanently fresh. On a corrupt owner nothing can restamp it (refreshOwnedSessionsHeartbeat
// filters on a valid current owner) and it can never hold the Web Lock, so a future heartbeat protects
// nothing and would strand the row forever — it counts as no heartbeat instead.
//
// This deliberately does NOT apply to a VALID owner: there, a backwards clock jump must not kill a live
// owner, and its lock probe can still prove liveness (adminUploadLease.cas-and-identity.spec.ts pins it).
function getUsableFileUploadOwnerHeartbeatAt(heartbeatAt: number | undefined, now: number): number | undefined {
	if (typeof heartbeatAt !== "number" || !Number.isFinite(heartbeatAt) || heartbeatAt > now) return undefined;
	return heartbeatAt;
}

function hasValidExpectedFileUploadOwnerId(ownerId: string | undefined): boolean {
	return ownerId === undefined || isValidFileUploadOwnerId(ownerId);
}

// A tab-family id: stable across reloads, but sessionStorage can be copied into a duplicated tab.
// Falls back to a module-level id when sessionStorage is unavailable (private mode / tests).
export function getCurrentFileUploadSessionOwnerId(): string {
	if (cachedUploadOwnerId) return cachedUploadOwnerId;

	try {
		if (typeof sessionStorage !== "undefined") {
			const storedOwnerId = sessionStorage.getItem(FILE_UPLOAD_SESSION_OWNER_ID_KEY);
			if (isValidFileUploadOwnerId(storedOwnerId)) {
				cachedUploadOwnerId = storedOwnerId;
				return storedOwnerId;
			}
			if (storedOwnerId !== null) {
				// This tab's own root of trust was corrupt: every row it stamped under that value is now
				// orphaned from this tab, so the replacement must leave a trace (redacted length only).
				warnUploadCasMismatch("[Upload] replaced an invalid persisted upload owner identity", {
					ownerIdLength: getFileUploadOwnerIdLength(storedOwnerId)
				});
			}
			const ownerId = newUploadId();
			sessionStorage.setItem(FILE_UPLOAD_SESSION_OWNER_ID_KEY, ownerId);
			cachedUploadOwnerId = ownerId;
			return ownerId;
		}
	} catch {
		// sessionStorage can throw in locked-down/SSR-like contexts — fall through to the module id.
	}

	cachedUploadOwnerId = newUploadId();
	return cachedUploadOwnerId;
}

type FileUploadOwnerLiveness = "alive" | "dead" | "indeterminate";

/**
 * Maps admin's Web-Locks ownership and heartbeat grace windows onto the shared
 * owner-liveness vocabulary without changing the LSP-16326 reclaim timing.
 */
async function resolveFileUploadOwnerLiveness(
	session: FileUploadSession,
	now: number,
	matchingCurrentOwnerIsAlive: boolean
): Promise<FileUploadOwnerLiveness> {
	if (session.uploadOwnerId !== undefined && !isValidFileUploadOwnerId(session.uploadOwnerId)) {
		warnUploadCasMismatch("[Upload] upload-owner liveness is indeterminate — invalid owner identity", {
			sessionId: session.id,
			ownerIdLength: getFileUploadOwnerIdLength(session.uploadOwnerId)
		});
		return "indeterminate";
	}
	if (session.uploadOwnerId === undefined) return "dead";
	if (matchingCurrentOwnerIsAlive && session.uploadOwnerId === getCurrentFileUploadSessionOwnerId()) return "alive";

	const lockManager = getUploadOwnerLockManager();
	if (!session.uploadOwnerHeartbeatAt) {
		if (!lockManager) return "dead";
		const ownerLockFree = await isUploadOwnerLockFree(session.uploadOwnerId);
		if (ownerLockFree === false) return "alive";
		if (ownerLockFree === true) return "dead";
		return "indeterminate";
	}

	const heartbeatAge = now - session.uploadOwnerHeartbeatAt;
	if (!lockManager) return heartbeatAge > FILE_UPLOAD_SESSION_OWNER_STALE_MS ? "dead" : "alive";

	const ownerLockFree = await isUploadOwnerLockFree(session.uploadOwnerId);
	if (ownerLockFree === false) return "alive";
	if (ownerLockFree === true) return heartbeatAge > FILE_UPLOAD_OWNER_LOCK_GRACE_MS ? "dead" : "alive";
	return heartbeatAge > FILE_UPLOAD_SESSION_OWNER_STALE_MS ? "dead" : "indeterminate";
}

export async function getFileUploadOwnerLiveness(session: FileUploadSession, now: number = Date.now()): Promise<FileUploadOwnerLiveness> {
	return resolveFileUploadOwnerLiveness(session, now, true);
}

export async function getCompetingFileUploadOwnerLiveness(
	session: FileUploadSession,
	now: number = Date.now()
): Promise<FileUploadOwnerLiveness> {
	return resolveFileUploadOwnerLiveness(session, now, false);
}

function isActiveUploadSessionStatus(status: FileUploadSessionStatus): boolean {
	return activeUploadStatuses.includes(status);
}

function clearUploadOwnerFields(session: FileUploadSession): FileUploadSession {
	const {
		uploadOwnerId: _uploadOwnerId,
		uploadOwnerHeartbeatAt: _uploadOwnerHeartbeatAt,
		uploadOwnerClaimId: _uploadOwnerClaimId,
		uploadOwnerProfileId: _uploadOwnerProfileId,
		uploadOwnerTabFamilyId: _uploadOwnerTabFamilyId,
		uploadOwnerPageLoadId: _uploadOwnerPageLoadId,
		uploadOwnerPurpose: _uploadOwnerPurpose,
		...unowned
	} = session;
	return unowned;
}

export function nextFileUploadCancellationGeneration(session: Pick<FileUploadSession, "uploadCancellationGeneration">): number {
	return typeof session.uploadCancellationGeneration === "number" && Number.isSafeInteger(session.uploadCancellationGeneration)
		? session.uploadCancellationGeneration + 1
		: 1;
}

/** Bounds a failure reason so one pathological error string cannot bloat the persisted row or the console. */
export function truncateUploadFailureReason(reason: string | undefined): string | undefined {
	return reason?.slice(0, MAX_UPLOAD_LAST_ERROR_LENGTH);
}

function resolveNextLastError(
	session: FileUploadSession,
	nextStatus: FileUploadSessionStatus,
	lastError: string | undefined
): string | undefined {
	// LSP-17823: a retried needs-attention transition must keep the FIRST reason recorded, matching
	// async's markFileUploadSessionNeedsAttention idempotent-return semantics — a second, different
	// reason is diagnostic-logged by the caller but never overwrites the row.
	if (nextStatus === session.status && (nextStatus === "needs-attention" || nextStatus === "recovery-blocked")) return session.lastError;
	if (lastError !== undefined) return truncateUploadFailureReason(lastError);
	if (isActiveUploadSessionStatus(nextStatus) || nextStatus === "pending") return undefined;
	return session.lastError;
}

/**
 * LSP-19384 — the two halves of the retry ceiling, which must BOTH be satisfied (never either/or).
 *
 * Each alone is unsafe, in opposite directions:
 *  - A count alone terminalizes a laptop that was closed for the weekend, because the drain wakes,
 *    fails instantly while offline, and burns the whole budget in seconds. The connectivity gate on
 *    the counter is the first defence; this backstop is the second.
 *  - A clock alone terminalizes a genuinely healthy 4 GB upload over a slow hotel connection.
 *
 * ANDed, a row must have failed against a reachable server this many times AND have been failing for
 * this long. `MIN_AGE_MS` exceeds the requeue backoff's 15-minute ceiling by enough that the count
 * cannot be reached inside the window by pacing alone — the clock is the binding constraint for a
 * fast-failing row, and the count is the binding constraint for a slow one.
 *
 * Terminalizing means "stop retrying AUTOMATICALLY", never "discard": the row stays, its bytes stay,
 * and Retry / Remove stay available to the user.
 */
export const FILE_UPLOAD_RETRY_CEILING_EVIDENCED_FAILURES = 10;
export const FILE_UPLOAD_RETRY_CEILING_MIN_AGE_MS = 4 * 60 * 60 * 1000;

/**
 * LSP-19384: the ONE reader of the three durable ceiling cells, so no consumer ever sees a raw one.
 *
 * IndexedDB is untyped at runtime and these cells outlive the code that wrote them, so every one of
 * them can come back as something that is not a plausible count or instant. Break-it round 1 got
 * past three separate per-field checks with `-0`, a negative, a fraction, a string, `null`, an
 * object, `NaN`, `Infinity`, `MAX_SAFE_INTEGER`, and a timestamp in the future. Adding a fourth and
 * fifth guard beside them is the wrong answer; canonicalising once, here, is what closes the class.
 *
 * Two rules, both deliberately biased toward NOT terminalizing:
 *  - Any cell that is not a plausible value is treated as absent. A count must be a safe positive
 *    integer with room to grow; an instant must be a positive finite number that is not in the
 *    future (a future stamp means the clock moved, and a stamp that has not happened yet cannot be
 *    evidence that something already did).
 *  - The count and the anchor stand or fall TOGETHER. Keeping a valid-looking anchor beside a
 *    corrupt count would leave a row whose four-hour half is already satisfied and whose count
 *    restarts at one — the easier-to-terminalize combination, assembled out of a value we just
 *    decided we could not trust.
 *
 * The bias matters because the cost is asymmetric: wrongly forgetting the budget costs one more
 * retry cycle, while wrongly terminalizing strands the upload AND holds its bytes in IndexedDB.
 */
function readFileUploadRetryCeiling(
	session: Pick<FileUploadSession, "evidencedFailureCount" | "firstEvidencedFailureAt" | "retryCeilingReachedAt">,
	now: number
): { evidencedFailureCount?: number; firstEvidencedFailureAt?: number; retryCeilingReachedAt?: number } {
	// An instant is only ever written here as `Date.now()`, so a whole-millisecond safe integer is the
	// only shape this code can have produced. A fraction is corruption like any other; treating it as
	// an instant would let a value we cannot account for anchor the four-hour half of the ceiling.
	const plausibleInstant = (value: unknown): number | undefined =>
		typeof value === "number" && Number.isSafeInteger(value) && value > 0 && value <= now ? value : undefined;
	const count = session.evidencedFailureCount;
	const rawCount =
		typeof count === "number" && Number.isSafeInteger(count) && count > 0 && count < Number.MAX_SAFE_INTEGER ? count : undefined;
	const anchor = plausibleInstant(session.firstEvidencedFailureAt);
	// Together or not at all: one usable half beside one corrupt half is not partial evidence.
	const coherent = rawCount !== undefined && anchor !== undefined;
	return {
		// Absent, never `0`. "No evidenced failures yet" already has a spelling in this store, and
		// inventing a second one would write a counter onto every offline park — rows this pass has
		// just decided not to count at all.
		evidencedFailureCount: coherent ? rawCount : undefined,
		firstEvidencedFailureAt: coherent ? anchor : undefined,
		retryCeilingReachedAt: plausibleInstant(session.retryCeilingReachedAt)
	};
}

/**
 * LSP-19384: the retry-ceiling bookkeeping for one park, computed inside the caller's transaction so
 * the counters and the row's status can never disagree.
 *
 * Only a park counts, and only one the caller's connectivity gate accepted (`evidencedFailure`). An
 * offline failure is free — it proves nothing about the upload — which is the same contract the
 * shared lanes already apply to their own attempt budgets (`isOfflineUploadFailure`).
 *
 * `retryCeilingReachedAt` is stamped at most once and never moved: it is the instant automatic retry
 * stopped, and the user's explicit Retry is the only thing that clears it.
 */
function resolveNextRetryCeilingFields(
	session: FileUploadSession,
	nextStatus: FileUploadSessionStatus,
	evidencedFailure: boolean,
	now: number
): Pick<FileUploadSession, "evidencedFailureCount" | "firstEvidencedFailureAt" | "retryCeilingReachedAt"> {
	const current = readFileUploadRetryCeiling(session, now);
	// Every return path writes the CANONICAL values, not the raw ones: a pass that declines to count
	// is still the right moment to retire a cell we have just decided we cannot trust, and leaving it
	// in place would make the next pass re-derive the same verdict forever.
	if (!evidencedFailure || nextStatus !== "needs-attention") return current;
	const evidencedFailureCount = (current.evidencedFailureCount ?? 0) + 1;
	const firstEvidencedFailureAt = current.firstEvidencedFailureAt ?? now;
	const reachesCeiling =
		evidencedFailureCount >= FILE_UPLOAD_RETRY_CEILING_EVIDENCED_FAILURES &&
		now - firstEvidencedFailureAt >= FILE_UPLOAD_RETRY_CEILING_MIN_AGE_MS;
	const stampingNow = reachesCeiling && current.retryCeilingReachedAt === undefined;
	if (stampingNow) {
		// Silent-failure rule: this is the moment the app gives up on its own, and it is invisible in
		// the UI until the user opens the tray. It must be visible in Datadog immediately.
		console.warn("[Upload] Upload session reached the retry ceiling; automatic retry has stopped", {
			sessionId: session.id,
			fileName: session.fileName,
			evidencedFailureCount,
			failingForMs: now - firstEvidencedFailureAt
		});
	}
	return {
		evidencedFailureCount,
		firstEvidencedFailureAt,
		retryCeilingReachedAt: stampingNow ? now : current.retryCeilingReachedAt
	};
}

function isObservedFileUploadSessionRelease(
	session: FileUploadSession | undefined,
	status: FileUploadSessionStatus,
	expectedUploadId?: string
): boolean {
	return (
		session?.status === status &&
		(expectedUploadId === undefined || session.uploadId === expectedUploadId) &&
		session.uploadOwnerId === undefined &&
		session.uploadOwnerHeartbeatAt === undefined &&
		session.uploadOwnerClaimId === undefined
	);
}

/**
 * Exported (LSP-19088) so the upload drain can hold a per-page-load liveness lock for its scheduler
 * owner through THIS module's single `navigator.locks` call site, rather than adding a second one — the
 * Safari/iOS 15 support-floor gate baselines that API here and refuses new call sites elsewhere.
 * Same helper, same lock namespace, same fail-open behaviour when Web Locks are unavailable.
 */
export function ensureUploadOwnerLivenessLockHeldForOwner(ownerId: string): void {
	if (requestedUploadOwnerLivenessLocks.has(ownerId)) return;
	const lockManager = getUploadOwnerLockManager();
	if (!lockManager) return;

	requestedUploadOwnerLivenessLocks.add(ownerId);
	void lockManager
		.request(getUploadOwnerLockName(ownerId), () => {
			acquiredUploadOwnerLivenessLocks.add(ownerId);
			return new Promise(() => {});
		})
		.catch((error) => {
			requestedUploadOwnerLivenessLocks.delete(ownerId);
			acquiredUploadOwnerLivenessLocks.delete(ownerId);
			console.info(`[Upload] Could not hold upload-owner liveness lock for owner ${ownerId}`, error);
		});
}

export function ensureUploadOwnerLivenessLockHeld(): void {
	ensureUploadOwnerLivenessLockHeldForOwner(getCurrentFileUploadSessionOwnerId());
}

/**
 * Whether this page has a Web Locks manager at all. `isUploadOwnerLockFree` answers `false`
 * ("treat as held") when there is none, which is the right default for its ownership callers but
 * must not be read as "a live page holds this lock" — a caller that needs PROOF of liveness (the
 * scheduler handover in adminUploadDrain) checks this first. Bugbot, PR #4603: without it a
 * Safari/iOS 15 reload never handed over its own previous lease and waited out the full TTL.
 */
export function hasUploadOwnerLockManager(): boolean {
	return getUploadOwnerLockManager() !== undefined;
}

export async function isUploadOwnerLockFree(ownerId: string): Promise<boolean | null> {
	if (!isValidFileUploadOwnerId(ownerId)) {
		warnUploadCasMismatch("[Upload] upload-owner liveness lock probe skipped — invalid owner identity", {
			ownerIdLength: getFileUploadOwnerIdLength(ownerId)
		});
		return null;
	}
	const lockManager = getUploadOwnerLockManager();
	if (!lockManager) return false;

	try {
		return await lockManager.request(getUploadOwnerLockName(ownerId), { ifAvailable: true }, (lock) => lock !== null);
	} catch (error) {
		console.warn(`[Upload] upload-owner liveness lock probe failed for owner ${ownerId}; falling back to heartbeat staleness`, error);
		return null;
	}
}

/**
 * Revalidates that a foreign owner's Web Lock is still free and keeps that lock
 * held until the ownership handoff has committed. The callback starts its Dexie
 * transaction only after Web Locks invokes us, so no external wait can make an
 * already-live IndexedDB transaction inactive.
 */
export async function runWhileUploadOwnerLockIsFree<T>(ownerId: string, operation: () => Promise<T>): Promise<T | undefined> {
	if (!isValidFileUploadOwnerId(ownerId)) {
		warnUploadCasMismatch("[Upload] upload-owner lock handoff skipped — invalid owner identity", {
			ownerIdLength: getFileUploadOwnerIdLength(ownerId)
		});
		return undefined;
	}
	const lockManager = getUploadOwnerLockManager();
	if (!lockManager) return operation();

	type LockOperationOutcome = { value: T } | { operationError: unknown } | undefined;
	let outcome: LockOperationOutcome;
	try {
		outcome = await lockManager.request(getUploadOwnerLockName(ownerId), { ifAvailable: true }, async (lock) => {
			if (!lock) return undefined;
			try {
				return { value: await operation() };
			} catch (operationError) {
				return { operationError };
			}
		});
	} catch (error) {
		console.warn(`[Upload] upload-owner lock revalidation failed for owner ${ownerId}; refusing ownership handoff`, error);
		return undefined;
	}

	if (outcome && "operationError" in outcome) throw outcome.operationError;
	return outcome?.value;
}

export function hasCurrentPageInitiatedFileUpload(): boolean {
	return currentPageInitiatedFileUpload;
}

// Stamps this tab as the owner with a fresh heartbeat. Ownership is kept fresh by checkpoint
// restamps in markFileUploadChunkUploaded(). Spread into any write that lands an active
// ("uploading"/"queued") status.
export function getFileUploadSessionOwnerFields(
	ownerId: string = getCurrentFileUploadSessionOwnerId(),
	ownerClaimId?: string
): Pick<FileUploadSession, "uploadOwnerId" | "uploadOwnerHeartbeatAt"> & ObservedUploadContext {
	if (!isValidFileUploadOwnerId(ownerId)) {
		throw new Error("Cannot get file upload session owner fields with an invalid owner identity");
	}
	currentPageInitiatedFileUpload = true;
	ensureUploadOwnerLivenessLockHeldForOwner(ownerId);
	return {
		...getFileUploadDiagnosticContext().ownerFields(),
		uploadOwnerId: ownerId,
		uploadOwnerHeartbeatAt: Date.now(),
		...(ownerClaimId === undefined ? {} : { uploadOwnerClaimId: ownerClaimId })
	};
}

// Refresh every active row this tab owns at upload checkpoints. This includes queued waiters in
// a serial batch while another file is uploading.
export async function refreshOwnedSessionsHeartbeat(ownerClaimId?: string): Promise<void> {
	const ownerId = getCurrentFileUploadSessionOwnerId();
	ensureUploadOwnerLivenessLockHeldForOwner(ownerId);
	await db.transaction("rw", fileUploadSessionControlTables, async () => {
		const active = await listFileUploadSessionsByStatus("uploading", "queued");
		const owned = active.filter(
			(session) =>
				session.uploadOwnerId === ownerId &&
				(session.uploadOwnerClaimId === undefined || session.uploadOwnerClaimId === ownerClaimId)
		);
		const excludedClaimCount = active.filter(
			(session) =>
				session.uploadOwnerId === ownerId && session.uploadOwnerClaimId !== undefined && session.uploadOwnerClaimId !== ownerClaimId
		).length;
		if (excludedClaimCount > 0) {
			console.warn(`[Upload] heartbeat excluded ${excludedClaimCount} same-owner session(s) claimed by a different lease`);
		}
		if (owned.length === 0) return;
		const now = Date.now();
		for (const session of owned) {
			await updateFileUploadSession(session.id, { uploadOwnerHeartbeatAt: now });
		}
	});
}

// LSP-16326: best-effort owner-heartbeat restamp tied to chunk DB activity. Both persisting a chunk
// (saveFileUploadChunk, before upload) and completing one (markFileUploadChunkUploaded, after upload)
// are "this tab is alive" signals, so each restamps all owned active rows (incl. queued waiters).
// This keeps ownership fresh across a long up-front persist of a large multi-GB file AND during the
// upload, so a second tab's resetUploadingSessions can't see the rows as stale and steal them. The
// heartbeat is a liveness signal — a transient IDB hiccup here must NEVER fail the chunk write (the
// removed setInterval path also swallowed-and-logged), so failures are swallowed with a diagnostic.
async function restampOwnerHeartbeatBestEffort(sessionId: string, chunkIndex: number, phase: string, ownerClaimId?: string): Promise<void> {
	try {
		await refreshOwnedSessionsHeartbeat(ownerClaimId);
	} catch (error) {
		console.warn(
			`[Upload] Could not refresh owned upload-session heartbeats at ${phase} (session ${sessionId}, chunk ${chunkIndex})`,
			error
		);
	}
}

export async function createFileUploadSessionWithOutcome(
	session: FileUploadSession
): Promise<{ session: FileUploadSession; created: boolean }> {
	if (session.uploadOwnerId !== undefined && !isValidFileUploadOwnerId(session.uploadOwnerId)) {
		throw new Error("Cannot create file upload session with an invalid owner identity");
	}
	// LSP-16528: this is the first write of the persist phase, immediately before the chunk loop; a
	// connection drop here must recover in-place like the chunk writes (reopen-and-retry) rather than
	// fall through chunkedUploadService's persist catch, which — now that DatabaseClosedError is no
	// longer "internal" — would bare-rethrow without the corruption-cleanup branch.
	return withIdbReopen(() =>
		db.transaction("rw", fileUploadSessionControlTables, async () => {
			const existingSession = await readFileUploadSessionByUploadId(session.uploadId);
			if (existingSession) {
				if (existingSession.status === "cancelled" && existingSession.uploadCancellationGeneration === undefined) {
					const uploadCancellationGeneration = nextFileUploadCancellationGeneration(existingSession);
					await updateFileUploadSession(existingSession.id, { uploadCancellationGeneration });
					return {
						session: { ...existingSession, uploadCancellationGeneration },
						created: false
					};
				}
				return { session: existingSession, created: false };
			}
			if (session.uploadOwnerId !== undefined && !isValidFileUploadOwnerId(session.uploadOwnerId)) {
				throw new Error("Invalid upload owner identity");
			}

			await addFileUploadSession(session);
			if (isActiveUploadSessionStatus(session.status) && session.uploadOwnerId === getCurrentFileUploadSessionOwnerId()) {
				markCurrentPageInitiatedFileUploadSession(session);
			}
			return { session, created: true };
		})
	);
}

export async function createFileUploadSession(session: FileUploadSession): Promise<FileUploadSession> {
	return (await createFileUploadSessionWithOutcome(session)).session;
}

/**
 * Write a chunk's metadata and buffer to IDB as two separate records
 * so metadata can be queried without loading the heavy buffer.
 * Empty buffers (already-uploaded chunks) skip the data table write.
 */
export async function saveFileUploadChunk(
	chunk: FileUploadChunk,
	expectedSessionIdentity?: Pick<FileUploadSession, "uploadOwnerId" | "uploadId" | "uploadOwnerClaimId">
): Promise<void> {
	// LSP-16528: reopen-and-retry once if the connection dropped mid-persist (iOS WebKit backgrounding).
	// LSP-19863: the stall budget is sized from the chunk payload, not the 10 s metadata budget.
	const chunkPersisted = await withIdbReopen(
		() =>
			db.transaction("rw", [...fileUploadSessionControlTables, db.fileUploadChunks, db.fileUploadChunkData], async () => {
				if (expectedSessionIdentity !== undefined) {
					const session = await readFileUploadSession(chunk.sessionId);
					if (
						!hasValidExpectedFileUploadOwnerId(expectedSessionIdentity.uploadOwnerId) ||
						!session ||
						hasInvalidUploadOwnerMismatch(expectedSessionIdentity.uploadOwnerId, session?.uploadOwnerId) ||
						session.uploadOwnerId !== expectedSessionIdentity.uploadOwnerId ||
						session.uploadId !== expectedSessionIdentity.uploadId ||
						session.uploadOwnerClaimId !== expectedSessionIdentity.uploadOwnerClaimId ||
						// extended architecture campaign: matching ownership cannot authorize a post-terminal chunk write.
						isTerminalFileUploadSessionStatus(session.status)
					) {
						warnUploadCasMismatch(
							"[Upload] Cannot save file upload chunk: owner identity mismatch",
							getSafeUploadOwnerMismatchContext(
								{
									sessionId: chunk.sessionId,
									chunkIndex: chunk.chunkIndex,
									expectedOwnerClaimId: expectedSessionIdentity.uploadOwnerClaimId,
									actualOwnerClaimId: session?.uploadOwnerClaimId,
									expectedUploadId: expectedSessionIdentity.uploadId,
									actualUploadId: session?.uploadId
								},
								expectedSessionIdentity.uploadOwnerId,
								session?.uploadOwnerId
							)
						);
						return false;
					}
				}
				await db.fileUploadChunks.put({
					sessionId: chunk.sessionId,
					chunkIndex: chunk.chunkIndex,
					isUploaded: chunk.isUploaded,
					etag: chunk.etag,
					size: chunk.size
				});
				if (chunk.buffer.byteLength > 0) {
					await db.fileUploadChunkData.put({
						sessionId: chunk.sessionId,
						chunkIndex: chunk.chunkIndex,
						buffer: chunk.buffer
					});
				}
				return true;
			}),
		{ payloadBytes: chunk.buffer.byteLength }
	);
	if (!chunkPersisted) return;
	// Persist checkpoint: a large file's up-front chunk persist can take minutes; restamp here so the
	// owner heartbeat (stamped once at session create) cannot go stale mid-persist and be stolen. The
	// per-chunk restamp is a tiny sessions-table write, dwarfed by the chunk buffer write just above.
	await restampOwnerHeartbeatBestEffort(chunk.sessionId, chunk.chunkIndex, "chunk persist", expectedSessionIdentity?.uploadOwnerClaimId);
}

export async function adoptFileUploadChunkMetadataIfOwned(
	chunk: FileUploadChunkMeta,
	expectedSessionIdentity: Pick<FileUploadSession, "uploadOwnerId" | "uploadId" | "uploadOwnerClaimId">,
	options?: { ifAbsent?: boolean }
): Promise<boolean> {
	return withIdbReopen(() =>
		db.transaction("rw", [...fileUploadSessionControlTables, db.fileUploadChunks], async () => {
			const session = await readFileUploadSession(chunk.sessionId);
			if (
				!hasValidExpectedFileUploadOwnerId(expectedSessionIdentity.uploadOwnerId) ||
				!session ||
				hasInvalidUploadOwnerMismatch(expectedSessionIdentity.uploadOwnerId, session?.uploadOwnerId) ||
				session.uploadOwnerId !== expectedSessionIdentity.uploadOwnerId ||
				session.uploadOwnerClaimId !== expectedSessionIdentity.uploadOwnerClaimId ||
				session.uploadId !== expectedSessionIdentity.uploadId ||
				// extended architecture campaign: adopted metadata must not mutate a terminal session.
				isTerminalFileUploadSessionStatus(session.status)
			) {
				warnUploadCasMismatch(
					"[Upload] Cannot adopt file upload chunk metadata: owner identity mismatch",
					getSafeUploadOwnerMismatchContext(
						{
							sessionId: chunk.sessionId,
							expectedUploadId: expectedSessionIdentity.uploadId,
							actualUploadId: session?.uploadId,
							expectedOwnerClaimId: expectedSessionIdentity.uploadOwnerClaimId,
							actualOwnerClaimId: session?.uploadOwnerClaimId
						},
						expectedSessionIdentity.uploadOwnerId,
						session?.uploadOwnerId
					)
				);
				return false;
			}
			if (options?.ifAbsent && (await db.fileUploadChunks.get([chunk.sessionId, chunk.chunkIndex]))) return true;
			await db.fileUploadChunks.put(chunk);
			return true;
		})
	);
}

/**
 * Mark a chunk as uploaded while retaining the buffer until the whole file completes.
 */
export async function markFileUploadChunkUploaded(
	sessionId: string,
	chunkIndex: number,
	etag: string,
	expectedOwnerId: string | undefined,
	expectedUploadId: string,
	expectedOwnerClaimId?: string
): Promise<void> {
	// LSP-16528: this completion checkpoint runs after every uploaded chunk during resume; reopen-and-retry.
	const checkpointApplied = await withIdbReopen(() =>
		db.transaction("rw", [...fileUploadSessionControlTables, db.fileUploadChunks], async () => {
			const session = await readFileUploadSession(sessionId);
			if (
				!hasValidExpectedFileUploadOwnerId(expectedOwnerId) ||
				!session ||
				hasInvalidUploadOwnerMismatch(expectedOwnerId, session?.uploadOwnerId) ||
				session.uploadOwnerId !== expectedOwnerId ||
				session.uploadOwnerClaimId !== expectedOwnerClaimId ||
				session.uploadId !== expectedUploadId ||
				// extended architecture campaign: a delayed chunk checkpoint cannot land after a terminal verdict.
				isTerminalFileUploadSessionStatus(session.status)
			) {
				if (hasInvalidUploadOwnerMismatch(expectedOwnerId, session?.uploadOwnerId)) {
					warnUploadCasMismatch(
						"[Upload] stale chunk checkpoint ignored",
						getSafeUploadOwnerMismatchContext(
							{
								sessionId,
								chunkIndex,
								expectedUploadId,
								actualUploadId: session?.uploadId,
								expectedOwnerClaimId,
								actualOwnerClaimId: session?.uploadOwnerClaimId
							},
							expectedOwnerId,
							session?.uploadOwnerId
						)
					);
				} else {
					// ls-heroku-ci review: this sibling of the invalid-owner branch above was the one bare
					// console.warn left on a CAS refusal path, so a throwing console would have turned a stale
					// no-op into a rejected chunk checkpoint. Routed through the same guarded wrapper.
					warnUploadCasMismatch(
						`[Upload] stale chunk checkpoint ignored for session ${sessionId}, chunk ${chunkIndex}: ` +
							`expected owner ${expectedOwnerId ?? "<none>"}, actual owner ${session?.uploadOwnerId ?? "<missing>"}; ` +
							"persisted owner/upload identity changed"
					);
				}
				return false;
			}
			await db.fileUploadChunks.update([sessionId, chunkIndex], {
				isUploaded: 1,
				etag
			});
			return true;
		})
	);
	if (!checkpointApplied) return;
	// Completion checkpoint: every chunk completion proves this tab is alive and restamps all owned
	// active rows, including queued waiters (also covers the App.vue already-uploaded resume path).
	await restampOwnerHeartbeatBestEffort(sessionId, chunkIndex, "chunk checkpoint", expectedOwnerClaimId);
}

/**
 * Flip the named chunks back to not-uploaded after a chunked-object completion named them as
 * missing or disagreeing (UPLOAD_PARTS_INCOMPLETE, plan 3a D7). Only those indexes change; the
 * chunk buffers in `fileUploadChunkData` are untouched, so the ordinary chunk loop re-sends exactly
 * these instead of re-uploading the whole file. Twin of contact-async's
 * `resetFileUploadChunkUploads` — the shared driver calls both through `markChunksNotUploaded`.
 *
 * CAS-guarded on the SAME ladder as `markFileUploadChunkUploaded`, not on `uploadId`
 * alone. `uploadId` survives everything that happens to a row short of a re-mint: a second tab that
 * took the owner claim, and — the destructive one — a writer that already drove the row to a TERMINAL
 * verdict. `complete` / `cancelled` / `needs-attention` / `recovery-blocked` all keep the uploadId, so
 * the uploadId-only guard let a delayed D7 refusal flip a finished upload's chunks back to
 * not-uploaded and hand the driver bytes to re-send for an upload that was already over. The two
 * writes are exact mirrors of one another — one sets `isUploaded`, the other clears it — so any
 * condition that must refuse one must refuse the other.
 */
export async function resetFileUploadChunkUploads(
	sessionId: string,
	chunkIndexes: readonly number[],
	expectedUploadId: string,
	expectedOwnerId?: string,
	expectedOwnerClaimId?: string
): Promise<number> {
	if (chunkIndexes.length === 0) return 0;
	// LSP-17653 (review Tier 3): the MODIFIED ROW COUNT, not void. Every refusal arm below is a silent
	// no-op, and the shared parts-incomplete bound needs to know that the ledger did not change — a
	// `void` return made a refused CAS indistinguishable from a successful re-mark, so the bound read
	// the round as progress and reset its streak forever.
	return await withIdbReopen(() =>
		db.transaction("rw", [...fileUploadSessionControlTables, db.fileUploadChunks], async () => {
			const session = await readFileUploadSession(sessionId);
			if (session?.uploadId !== expectedUploadId) {
				warnUploadCasMismatch("[Upload] Cannot reset file upload chunks: uploadId mismatch", {
					sessionId,
					chunkIndexes,
					expectedUploadId,
					actualUploadId: session?.uploadId
				});
				return 0;
			}
			if (
				!hasValidExpectedFileUploadOwnerId(expectedOwnerId) ||
				hasInvalidUploadOwnerMismatch(expectedOwnerId, session.uploadOwnerId) ||
				session.uploadOwnerId !== expectedOwnerId ||
				session.uploadOwnerClaimId !== expectedOwnerClaimId ||
				isTerminalFileUploadSessionStatus(session.status)
			) {
				// Diagnostic, one line naming EVERY condition in the gate rather than only the one that
				// refused this call (CLAUDE.md "Diagnostic logging when fixing silent failures"): the write
				// is a silent no-op either way, so without the state values a "why were these chunks never
				// re-sent" triage has nothing to read.
				warnUploadCasMismatch(
					"[Upload] Cannot reset file upload chunks: owner/claim/status CAS refused",
					getSafeUploadOwnerMismatchContext(
						{
							sessionId,
							chunkIndexes,
							expectedUploadId,
							expectedOwnerClaimId,
							actualOwnerClaimId: session.uploadOwnerClaimId,
							status: session.status,
							statusIsTerminal: isTerminalFileUploadSessionStatus(session.status)
						},
						expectedOwnerId,
						session.uploadOwnerId
					)
				);
				return 0;
			}
			return await db.fileUploadChunks
				.where("[sessionId+chunkIndex]")
				.anyOf(chunkIndexes.map((chunkIndex) => [sessionId, chunkIndex]))
				.modify({ isUploaded: 0, etag: null });
		})
	);
}

/**
 * Delete the session, all chunk metadata, and all chunk data from IndexedDB.
 */
export async function completeFileUploadSession(
	sessionId: string,
	expectedUploadId?: string,
	expectedOwnerId?: string,
	expectedOwnerClaimId?: string,
	notifyOtherTabs?: boolean
): Promise<boolean> {
	return deleteFileUploadSession(sessionId, expectedUploadId, expectedOwnerId, expectedOwnerClaimId, {
		requireExactOwnerIdentity: expectedUploadId !== undefined,
		notifyOtherTabs
	});
}

export async function getPendingFileUploadSessions(): Promise<FileUploadSession[]> {
	// LSP-16528: this drives the resume poller; a backgrounded mobile tab's dropped connection here
	// previously skipped the whole cycle. Reopen-and-retry so resume survives the connection loss.
	return withIdbReopen(() => listFileUploadSessionsByStatus("pending"), { operationName: "list-pending-sessions" });
}

/**
 * LSP-19384: rows the app has stopped retrying on its own, for the tray to SHOW.
 *
 * Read separately from `getPendingFileUploadSessions` on purpose. The pending snapshot is the
 * drain's work queue — every row it returns gets an AbortController and an attempt — so folding
 * parked rows into it would re-attempt the very rows that just terminated. This snapshot drives
 * presentation only, and nothing downstream of it starts an upload.
 */
export async function getTerminallyParkedFileUploadSessions(): Promise<FileUploadSession[]> {
	const parked = await withIdbReopen(() => listFileUploadSessionsByStatus("needs-attention", "recovery-blocked"));
	return parked.filter(isTerminallyParkedFileUploadSession);
}

export type FileUploadRetryOutcome = "retrying" | "no-local-bytes" | "not-parked" | "server-terminal";

/**
 * LSP-19384: the user's explicit Retry on a terminally parked row — the only thing that re-arms
 * automatic retry, and the only writer that clears the ceiling.
 *
 * The counters reset with the marker rather than surviving it. A user who retries is asserting that
 * something changed on their side (they reconnected, freed space, fixed the file); re-arming with
 * the budget already spent would park the row again on the very next failure, which reads as "Retry
 * does nothing" — the same shape as the bug this ticket is fixing.
 *
 * Refuses when the local bytes are gone: there is nothing left to send, so returning the row to
 * `pending` would only feed the drain a row it must immediately re-park. The caller says so instead.
 *
 * LSP-23172: refuses, for the same reason, a row whose stored failure is the server's permanent verdict
 * (`isUploadTerminalFailure`). The server answers every re-armed attempt on that upload with 404
 * UPLOAD_TERMINAL, so the drain re-parks it at once; a crowd tester re-armed 147 such rows 278 times.
 */
export async function retryTerminallyParkedFileUploadSession(
	sessionId: string,
	expectedUploadId?: string
): Promise<FileUploadRetryOutcome> {
	// LSP-22008: a placeholder's partial copy is not sendable bytes — its File is gone — so re-arming it only
	// feeds the drain a row it can loop on. Same answer as having no bytes at all.
	const parked = await getFileUploadSessionById(sessionId);
	if (parked && isUnpreparedPlaceholderSession(parked)) {
		console.warn("[Upload] Retry refused: the parked placeholder never finished saving its bytes", { sessionId });
		return "no-local-bytes";
	}
	const hasLocalBytes = await withIdbReopen(async () => (await db.fileUploadChunkData.where({ sessionId }).count()) > 0);
	if (!hasLocalBytes) {
		console.warn("[Upload] Retry refused: the parked upload has no local bytes left to send", { sessionId });
		return "no-local-bytes";
	}
	// Decided inside the write, not from the read above: another tab can park this upload with the server's
	// permanent verdict while the byte count is awaited, and re-arming that row is the doomed request.
	const refusal: { serverTerminal?: Pick<FileUploadSession, "uploadId" | "conversationId"> } = {};
	const updated = await withIdbReopen(() => {
		refusal.serverTerminal = undefined;
		return modifyFileUploadSession(
			sessionId,
			(current) => {
				if (current.status !== "needs-attention" || !isTerminallyParkedFileUploadSession(current)) return false;
				if (expectedUploadId !== undefined && current.uploadId !== expectedUploadId) return false;
				if (isUploadTerminalFailure(current.lastError)) {
					refusal.serverTerminal = { uploadId: current.uploadId, conversationId: current.conversationId };
					return false;
				}
				return true;
			},
			(current) => {
				current.status = "pending";
				current.lastError = undefined;
				current.retryCeilingReachedAt = undefined;
				current.evidencedFailureCount = undefined;
				current.firstEvidencedFailureAt = undefined;
				// LSP-19912: the settling window is spent budget in exactly the sense this function's
				// header describes. Leaving the old instant armed would re-mint UPLOAD_SETTLING_EXPIRED
				// on the very first conflict of the user's own attempt — "Retry does nothing" again. The
				// small-file lane discards its window on re-enqueue for the same reason.
				current.settlingConflictSince = undefined;
				// Due now: the user asked for this attempt, so it must not sit behind the backoff window
				// the last automatic failure stamped.
				current.retryAt = null;
				current.retryAttempt = undefined;
				// A parked row is not owned; clear any stale lease stamp so the drain does not read this
				// re-arm as a live foreign owner and skip it.
				current.uploadOwnerId = undefined;
				current.uploadOwnerHeartbeatAt = undefined;
				current.uploadOwnerClaimId = undefined;
			}
		);
	});
	if (refusal.serverTerminal) {
		console.warn("[Upload] Retry refused: the server has permanently failed this upload", { sessionId, ...refusal.serverTerminal });
		return "server-terminal";
	}
	if (updated === 0) {
		console.warn("[Upload] Retry refused: the upload is no longer parked", { sessionId, expectedUploadId });
		return "not-parked";
	}
	console.info("[Upload] Parked upload re-armed by the user", { sessionId });
	return "retrying";
}

/**
 * R-0 (owner directive 2026-08-26) — the admin half of "attempt counts never terminalize".
 *
 * Admin already writes a durable `retryAt` when the driver defers a session (`releasePending`), but
 * nothing ever READ it: every pending row was re-attempted on every pass, and a session that ran out
 * of the dialog's `maxRetryCount` ladder was marked `needs-attention` and then only re-queued by
 * `resetUploadingSessions(true, ...)` — the STARTUP pass. A long-lived admin tab therefore parked
 * forever (F-17). Async solved the same problem with a durable retry-backoff requeue that runs on the
 * recurring cycle (`requeueNeedsAttentionFileUploadSessions` / `computeNextDrainDelayMs` in
 * `apps/contact-async/src/db/db.ts`); this is that shape, for admin.
 *
 * LSP-18742 is the trap this must not fall into. The recurring resume cycle deliberately never
 * re-picked `needs-attention`, because doing so without a backoff produced a 2h38m re-pick spin.
 * PACING, not parking, is what prevents that spin here: each requeue escalates the row's own
 * `retryAt` (1min -> 15min, the R-0 slow tier), the drain skips rows whose `retryAt` has not elapsed,
 * and the drain schedules exactly one follow-up wake at the earliest due instant.
 */
/**
 * Escalating, minutes-capped delay for the Nth automatic requeue of one parked session.
 *
 * The 1min -> 15min bounds are R-0's slow tier, so they come from the shared `@uploads` constants
 * (`R0_SLOW_TIER_BASE_DELAY_MS` / `R0_SLOW_TIER_MAX_DELAY_MS`) rather than from a second local pair —
 * admin's requeue window and the shared lanes' slow-tier backoff are the same directive, and two
 * copies of "1min -> 15min" is how one of them silently stops matching.
 *
 * Deliberately NOT `computeSlowTierRetryAt`: that curve is the same exponential but jittered, and this
 * value is a DURABLE window that both `requeueNeedsAttentionFileUploadSessions` and the drain's wake
 * scheduler must agree on to the millisecond (`fileUploadDb.r0-requeue.spec.ts` asserts
 * `nextRetryDelayMs === computeFileUploadRequeueDelayMs(n)`, which a per-call random factor cannot
 * satisfy). Jitter exists to de-synchronise many concurrent retriers; there is exactly one requeue
 * scan per tab, and the CAS on `retryAttempt` already prevents two tabs from both escalating a row.
 */
export function computeFileUploadRequeueDelayMs(retryAttempt: number | undefined): number {
	const attempt = Number.isSafeInteger(retryAttempt) && (retryAttempt as number) > 0 ? (retryAttempt as number) : 0;
	return Math.min(R0_SLOW_TIER_MAX_DELAY_MS, R0_SLOW_TIER_BASE_DELAY_MS * 2 ** Math.min(attempt, 16));
}

/**
 * The evidence terminals R-0 keeps, as they appear in a session's persisted `lastError` string.
 * The marker set is the shared one (`@uploads` `isEvidenceTerminalFailure`) so admin, async photos
 * and Live snapshots cannot drift on what counts as evidence — a second local list is exactly how
 * one app ends up parking a row the others would have rescued.
 *
 * It fails SAFE either way: a row wrongly requeued simply re-derives its verdict on the next attempt
 * and re-parks, at the cost of one request.
 */
export function isEvidenceTerminalFileUploadError(lastError: string | undefined): boolean {
	return lastError ? isEvidenceTerminalFailure(lastError) : false;
}

/**
 * LSP-19384: whether this row has stopped being retried AUTOMATICALLY and must stay that way across
 * a reload. "Terminal" here means only that the app stops re-attempting it on its own — the row, its
 * chunk bytes, and the user's Retry / Remove actions all survive.
 *
 * Two independent grounds, either sufficient:
 *  - an evidence-terminal `lastError` (the ground the requeue loop already refuses on), and
 *  - `retryCeilingReachedAt` (the connectivity-gated ceiling).
 *
 * The reason this predicate exists rather than the requeue loop's inline check being reused: the
 * requeue loop is not the only writer. `resetUploadingSessions` runs on every page load and used to
 * return every parked row to `pending` with `lastError: undefined`, which un-parked BOTH grounds —
 * the evidence terminal that already shipped as well as the ceiling. Any future code that re-arms
 * automatic retry must consult this, or it re-opens LSP-19384 on the next reload.
 *
 * Fails OPEN on an implausible marker, via `readFileUploadRetryCeiling` — a value that is not a
 * positive finite instant at or before now carries no verdict, so the row stays retryable rather
 * than being stranded with its bytes held. See that function for why the check lives there and not
 * inline here.
 *
 * `now` is read INSIDE, and this predicate deliberately takes no second parameter. It briefly had an
 * injectable one, and `parked.filter(isTerminallyParkedFileUploadSession)` then handed it the array
 * INDEX — so `now` was 0 for the first row and every marker failed the "not in the future" test,
 * silently making every parked row look retryable and the whole feature inert. A predicate that is
 * passed to `.filter` must be unary; there is no caller that needs to choose the clock.
 */
export function isTerminallyParkedFileUploadSession(
	session: Pick<FileUploadSession, "status" | "lastError" | "evidencedFailureCount" | "firstEvidencedFailureAt" | "retryCeilingReachedAt">
): boolean {
	if (session.status === "recovery-blocked") return true;
	if (isUploadControlUnavailable(session.lastError)) return true;
	if (readFileUploadRetryCeiling(session, Date.now()).retryCeilingReachedAt !== undefined) return true;
	// LSP-23576: the server already holds this upload and no receipt matched it, so it waits for an explicit Retry.
	if (isUploadTerminalCompleted(session.lastError)) return true;
	return isEvidenceTerminalFileUploadError(session.lastError);
}

/**
 * Every session id that still has local chunk bytes to send, as ONE keys-only scan per requeue pass.
 *
 * Was an N-query loop (`where({ sessionId }).count()` per parked session) on the recurring drain
 * cycle. `uniqueKeys()` walks the same `sessionId` index once and answers for every session at
 * once, so a tab holding a backlog of parked rows stops paying one IDB round trip per row per pass.
 *
 * Returns null when the chunk store cannot be read: that fails the whole pass CLOSED, exactly as
 * the per-session probe failed one session closed — an unreadable chunk store must never requeue a
 * session whose bytes may be gone, and the next pass retries the scan.
 */
async function loadSessionIdsWithLocalChunkBytes(): Promise<Set<string> | null> {
	try {
		const sessionIds = await withIdbReopen(() => db.fileUploadChunkData.orderBy("sessionId").uniqueKeys());
		return new Set(sessionIds.map((sessionId) => String(sessionId)));
	} catch (error) {
		console.warn("[Upload] Could not probe local chunk bytes for parked sessions", error);
		return null;
	}
}

/**
 * The drain's admission rule for a pending row's durable `retryAt`, in ONE place so the filter and
 * the wake scheduler cannot disagree about a row.
 *
 * Break-it round 1 (D7): the filter was `typeof retryAt !== "number" || retryAt <= now`, and NaN is a
 * number for which `<= now` is false forever. One corrupt persisted value therefore made a row
 * permanently invisible to the drain AND to `computeNextFileUploadDrainDelayMs` — no status, no timer,
 * no log — which is exactly the unschedulable park R-0 abolished, reachable with no attempt count at
 * all. A value that is not a finite instant carries no schedule, so the honest reading is "due now".
 */
export function isFileUploadRetryDue(session: Pick<FileUploadSession, "id" | "retryAt" | "retryAttempt">, now: number): boolean {
	if (typeof session.retryAt !== "number") return true;
	if (!Number.isFinite(session.retryAt)) {
		console.warn("[Upload] Pending upload session has a non-finite retryAt; admitting it as due now", {
			sessionId: session.id,
			retryAt: session.retryAt,
			retryAttempt: session.retryAttempt
		});
		return true;
	}
	return session.retryAt <= now;
}

export interface FileUploadRequeueResult {
	reset: string[];
	/** Earliest instant, in ms from now, at which a still-parked session becomes due. */
	nextRetryDelayMs?: number;
	/** Absolute epoch-ms instant matching `nextRetryDelayMs` at the start of this requeue scan. */
	nextRetryAt?: number;
}

/**
 * "Wake at the EARLIEST of these windows" — the fold both R-0 schedulers need
 * (`requeueNeedsAttentionFileUploadSessions` over parked rows, `computeNextFileUploadDrainDelayMs`
 * over pending ones). They had transcribed the same three lines each; the drain arms ONE timer from
 * whichever of them returns sooner, so the two must round and floor a candidate identically or the
 * timer lands on the wrong row.
 *
 * A candidate is rounded UP and floored at 1ms: a sub-millisecond or already-elapsed window is still
 * a wake, and `setTimeout(0)` inside the drain's own pass would spin.
 */
function createEarliestDelayTracker(initialDelayMs?: number) {
	// The seed is used verbatim — it is another tracker's already-normalized output, not a raw window.
	let earliestDelayMs = initialDelayMs;
	return {
		note(delayMs: number): void {
			const positive = Math.max(1, Math.ceil(delayMs));
			earliestDelayMs = earliestDelayMs === undefined ? positive : Math.min(earliestDelayMs, positive);
		},
		get value(): number | undefined {
			return earliestDelayMs;
		}
	};
}

/**
 * Returns `needs-attention` sessions to the pending pool on the RUNNING app's recurring cycle, on a
 * durable escalating backoff. `recovery-blocked` is deliberately untouched: reconciliation proved
 * automatic recovery cannot reconstruct it, so only explicit cancellation may retire it.
 */
/** LSP-23095: `${sessionId}:${ground}` of every parked row this page has already reported, so each is logged once. */
const loggedParkedRequeueRows = new Set<string>();

export async function requeueNeedsAttentionFileUploadSessions(): Promise<FileUploadRequeueResult> {
	let sessions: FileUploadSession[];
	try {
		sessions = await withIdbReopen(() => listFileUploadSessionsByStatus("needs-attention"));
	} catch (error) {
		console.warn("[Upload] Could not scan needs-attention sessions for requeue", error);
		// Contribute a window anyway, or nothing schedules the follow-up that retries this scan
		// (the same silent-stall class async's requeue documents in its own catch).
		return {
			reset: [],
			nextRetryDelayMs: R0_SLOW_TIER_BASE_DELAY_MS,
			nextRetryAt: Date.now() + R0_SLOW_TIER_BASE_DELAY_MS
		};
	}
	// Nothing parked is the overwhelmingly common case on the recurring cycle; return before the chunk
	// scan below so it costs nothing at all then. Same answer the loop would produce over no rows.
	if (sessions.length === 0) {
		loggedParkedRequeueRows.clear();
		return { reset: [] };
	}
	const now = Date.now();
	const reset: string[] = [];
	const nextRetry = createEarliestDelayTracker();
	// One keys-only scan for the whole pass, read before the loop so a parked backlog costs one IDB
	// round trip instead of one per row. Null means the chunk store is unreadable: fail the pass
	// closed rather than requeue rows whose bytes may be gone, but still contribute a window so the
	// follow-up wake retries the scan (same silent-stall reasoning as the session-scan catch above).
	const sessionIdsWithLocalBytes = await loadSessionIdsWithLocalChunkBytes();
	if (sessionIdsWithLocalBytes === null)
		return {
			reset: [],
			nextRetryDelayMs: R0_SLOW_TIER_BASE_DELAY_MS,
			nextRetryAt: now + R0_SLOW_TIER_BASE_DELAY_MS
		};

	const parkedGround = (session: FileUploadSession) =>
		session.retryCeilingReachedAt !== undefined ? "retry-ceiling" : "permanent-failure-evidence";
	// A row that leaves the parked set (requeued by hand, cancelled, completed) is logged again if it re-parks.
	const parkedNow = new Set(
		sessions.filter(isTerminallyParkedFileUploadSession).map((session) => `${session.id}:${parkedGround(session)}`)
	);
	for (const latchKey of loggedParkedRequeueRows) if (!parkedNow.has(latchKey)) loggedParkedRequeueRows.delete(latchKey);
	for (const session of sessions) {
		if (isTerminallyParkedFileUploadSession(session)) {
			// Automatic retry has ended for this row — either on R-0 evidence of permanent failure, or
			// on LSP-19384's retry ceiling. Stays parked, and says why (silent-failure rule); the two
			// grounds are distinguished so a park can be triaged without reading the row.
			const ground = parkedGround(session);
			const latchKey = `${session.id}:${ground}`;
			// LSP-23095: this runs on every drain pass, so one tab with 147 parked rows logged 13,716 lines in 105
			// minutes. Each row and ground is still logged the first time this page sees it.
			if (!loggedParkedRequeueRows.has(latchKey)) {
				loggedParkedRequeueRows.add(latchKey);
				console.warn("[Upload] Parked upload session is no longer retried automatically; not requeued", {
					sessionId: session.id,
					fileName: session.fileName,
					retryAttempt: session.retryAttempt,
					ground,
					evidencedFailureCount: session.evidencedFailureCount
				});
			}
			continue;
		}
		// LSP-22008: a placeholder's partial bytes are not resumable bytes — the copy never finished and its
		// File is gone — so requeueing it only feeds the drain a row it can loop on. The next startup sweep
		// removes it.
		if (isUnpreparedPlaceholderSession(session)) {
			console.warn("[Upload] Parked placeholder upload never finished saving its bytes; not requeued", {
				sessionId: session.id,
				fileName: session.fileName
			});
			continue;
		}
		// Pacing first: a row still inside its own window is not a requeue candidate at all this pass,
		// so decide that from the value already in hand before spending anything on it.
		const dueAt = typeof session.retryAt === "number" && Number.isFinite(session.retryAt) ? session.retryAt : now;
		if (dueAt > now) {
			nextRetry.note(dueAt - now);
			continue;
		}
		if (!sessionIdsWithLocalBytes.has(session.id)) {
			console.warn("[Upload] Parked upload session has no local bytes left to send; not requeued", {
				sessionId: session.id,
				fileName: session.fileName
			});
			continue;
		}
		const retryAttempt = (session.retryAttempt ?? 0) + 1;
		// The window this requeue buys the row before its next ATTEMPT. It is stamped on the row
		// itself rather than being "due immediately", which is what keeps LSP-18742 dead: a session
		// that re-parks with no `retryAt` would otherwise be requeued and re-attempted on every pass,
		// reproducing the 2h38m spin the recurring cycle used to avoid by never re-picking at all.
		// One attempt per escalating window; the flip itself is free.
		const nextWindowMs = computeFileUploadRequeueDelayMs(retryAttempt);
		try {
			// CAS on the parked status and the exact retry generation this scan read, so two tabs
			// cannot both requeue (and both escalate) the same session.
			const updated = await withIdbReopen(() =>
				modifyFileUploadSession(
					session.id,
					(current) => current.status === "needs-attention" && (current.retryAttempt ?? 0) === (session.retryAttempt ?? 0),
					(current) => {
						current.status = "pending";
						current.lastError = undefined;
						current.retryAttempt = retryAttempt;
						current.retryAt = now + nextWindowMs;
						// A parked row is not owned; clear the stale lease stamp so the resume path does
						// not mistake this requeue for a live foreign owner.
						current.uploadOwnerId = undefined;
						current.uploadOwnerHeartbeatAt = undefined;
						current.uploadOwnerClaimId = undefined;
					}
				)
			);
			if (updated > 0) {
				reset.push(session.id);
				// The pacing decision itself, recorded at the moment it is made.
				//
				// Without it the row simply goes quiet: `Re-queued N needs-attention session(s)` says a
				// requeue happened but not that the row is now deliberately idle for `nextWindowMs`, so an
				// observer sees "pending" with no traffic and reads it as a stall. That misreading produced
				// the 57s-vs-120s report while this ticket's rework verdict was being diagnosed — a QA
				// oracle sampled for 57s a row the client had correctly paced to 120s (retryAttempt 1 =>
				// R0_SLOW_TIER_BASE_DELAY_MS * 2).
				//
				// `retryAttempt` and `nextWindowMs` together make the window reproducible from the log
				// alone. The line fires ONCE, at requeue: the skips over the rest of the window stay silent
				// because `retryAt` is recoverable from this record, and logging every drain wake for up to
				// 15 minutes would bury it.
				console.info("[Upload] Parked upload requeued with a paced retry window", {
					sessionId: session.id,
					retryAttempt,
					nextWindowMs,
					retryAt: now + nextWindowMs
				});
				// Wake the drain when this row's window elapses, so its one paced attempt actually
				// happens instead of waiting on the two-minute poll.
				nextRetry.note(nextWindowMs);
			} else {
				console.warn("[Upload] Parked upload session changed before requeue", { sessionId: session.id });
			}
		} catch (error) {
			console.warn(`[Upload] Could not requeue needs-attention upload session "${session.id}"`, error);
			nextRetry.note(computeFileUploadRequeueDelayMs(retryAttempt));
		}
	}

	if (reset.length > 0) console.info(`[Upload] Re-queued ${reset.length} needs-attention file upload session(s) for retry`);
	return {
		reset,
		nextRetryDelayMs: nextRetry.value,
		nextRetryAt: nextRetry.value === undefined ? undefined : now + nextRetry.value
	};
}

/**
 * The earliest instant the drain must wake up again for durable work that is not due yet: a pending
 * row still inside its `retryAt` backoff, or a parked row still inside its requeue window. Returns
 * undefined when nothing is waiting on a timer (live work wakes the drain by other means).
 */
export async function computeNextFileUploadDrainDelayMs(parkedDelayMs?: number): Promise<number | undefined> {
	try {
		return computeFileUploadDrainDelayMsFor(await getPendingFileUploadSessions(), Date.now(), parkedDelayMs);
	} catch (error) {
		console.warn("[Upload] Could not compute the next upload drain delay", error);
		return parkedDelayMs ?? R0_SLOW_TIER_BASE_DELAY_MS;
	}
}

/**
 * The pure half of `computeNextFileUploadDrainDelayMs`, for a caller that ALREADY holds the pending
 * snapshot — the drain's `afterDrain` folds the very array `discoverPending` read rather than paying
 * a second `status:"pending"` scan of the same rows in the same pass.
 */
export function computeFileUploadDrainDelayMsFor(
	pendingSessions: readonly Pick<FileUploadSession, "id" | "retryAt" | "retryAttempt">[],
	now: number,
	parkedDelayMs?: number
): number | undefined {
	const nextDelay = createEarliestDelayTracker(parkedDelayMs);
	for (const session of pendingSessions) {
		if (typeof session.retryAt !== "number") continue;
		// Break-it round 1 (D7): a non-finite `retryAt` was invisible to BOTH halves of the schedule —
		// the drain's `retryAt <= now` admission is false for NaN, and so was `retryAt > now` here — so
		// the row had no timer AND no admission. `isFileUploadRetryDue` calls it due (and logs it once,
		// from the drain's discovery filter); this half schedules the immediate wake that actually runs
		// the pass. Calling that predicate again HERE only re-logged the same row — its non-finite
		// branch is unconditionally true, so the wake is not conditional on anything.
		if (!Number.isFinite(session.retryAt)) {
			nextDelay.note(1);
			continue;
		}
		if (session.retryAt > now) nextDelay.note(session.retryAt - now);
	}
	return nextDelay.value;
}

/**
 * Returns a bounded snapshot of durable cancellation tombstones. Cancelled rows are intentionally
 * excluded from the normal resume queue; the Admin cancellation drain owns their server abort and
 * removes them only after that abort is acknowledged.
 */
export async function getCancelledFileUploadSessions(
	limit = 32,
	include: (session: FileUploadSession) => boolean = () => true
): Promise<FileUploadSession[]> {
	if (!Number.isSafeInteger(limit) || limit <= 0) return [];
	return withIdbReopen(async () => (await listFileUploadSessionsByStatus("cancelled")).filter(include).slice(0, limit));
}

export type FileUploadCancellationHandoffOutcome = "tombstoned" | "already-cancelled" | "stale-owner" | "missing";

export type FileUploadCancellationHandoffIdentity = Pick<
	FileUploadSession,
	| "status"
	| "uploadId"
	| "uploadOwnerId"
	| "uploadOwnerClaimId"
	| "endpointFamily"
	| "registryId"
	| "attemptId"
	| "generation"
	| "takeGeneration"
	| "s3Key"
	| "bucket"
>;

/**
 * Records an explicit user cancellation while yielding the current durable writer in the same
 * transaction. Cancellation is the one cross-owner transition that cannot wait for the existing
 * writer: the tombstone is what tells that writer's other tab to abort its in-flight request.
 *
 * The full observed row identity fences the transition. If a completion or successor claim wins
 * first, neither its lease nor its row is changed. Chunks remain until the cancellation owner has
 * received a server acknowledgement.
 */
export async function handoffFileUploadSessionToCancellation(
	sessionId: string,
	expectedSessionIdentity: FileUploadCancellationHandoffIdentity
): Promise<FileUploadCancellationHandoffOutcome> {
	const leaseKey = writerLeaseKey("contact-admin", { itemId: sessionId });
	const now = Date.now();
	const outcome = await withIdbReopen(() =>
		db.transaction("rw", [...fileUploadSessionControlTables, db.table("leases")], async () => {
			const session = await readFileUploadSession(sessionId);
			if (!session) return "missing" as const;
			if (session.status === "cancelled") return "already-cancelled" as const;
			if (
				session.status !== expectedSessionIdentity.status ||
				session.uploadId !== expectedSessionIdentity.uploadId ||
				session.uploadOwnerId !== expectedSessionIdentity.uploadOwnerId ||
				session.uploadOwnerClaimId !== expectedSessionIdentity.uploadOwnerClaimId ||
				session.endpointFamily !== expectedSessionIdentity.endpointFamily ||
				session.registryId !== expectedSessionIdentity.registryId ||
				session.attemptId !== expectedSessionIdentity.attemptId ||
				session.generation !== expectedSessionIdentity.generation ||
				session.takeGeneration !== expectedSessionIdentity.takeGeneration ||
				session.s3Key !== expectedSessionIdentity.s3Key ||
				session.bucket !== expectedSessionIdentity.bucket
			)
				return "stale-owner" as const;
			if (session.status === "complete") return "stale-owner" as const;

			const durableLease = (await db.table("leases").get(leaseKey)) as DurableLeaseRecord | undefined;
			if (durableLease) {
				const structurallyMatchesSession =
					durableLease.key === leaseKey &&
					durableLease.kind === "writer" &&
					durableLease.originKey === "contact-admin" &&
					durableLease.itemId === sessionId &&
					durableLease.takeId === undefined &&
					durableLease.acquiredAt <= now;
				if (getAdminDurableWriterLeaseState(durableLease) === "indeterminate" || !structurallyMatchesSession) {
					throw new Error(`Upload session ${sessionId} has an invalid durable writer; cancellation was preserved for retry`);
				}
				if (durableLease.expiresAt > now) {
					// Expiry fences every post-await write made with the predecessor token. The
					// cancellation coordinator acquires epoch + 1 immediately after this commit.
					//
					// LSP-19044: stamp `releasedExpiry`, never `now`. This rewrite preserves ownerId,
					// epoch and acquiredAt, so once the writer fence stopped comparing `expiresAt` the
					// ONLY thing separating a stale pre-cancellation token from this record is
					// liveness — and `now` is terminal only for a clock that never steps backwards.
					// `releasedExpiry` is `acquiredAt`, which line 1486 already required to be `<= now`.
					await db.table("leases").put({ ...durableLease, expiresAt: releasedExpiry(durableLease) }, leaseKey);
				}
			}

			await putFileUploadSession({
				...clearUploadOwnerFields(session),
				status: "cancelled",
				uploadCancellationGeneration: nextFileUploadCancellationGeneration(session)
			});
			return "tombstoned" as const;
		})
	);
	if (outcome === "tombstoned") {
		forgetCurrentPageInitiatedFileUploadSessions(sessionId);
		publishFileUploadSessionInvalidation(sessionId, expectedSessionIdentity.uploadId);
	}
	return outcome;
}

/**
 * Why a CAS-guarded status transition was declined. Resolved from the SAME transactional read the
 * CAS itself performed, so the reason can never drift between two separate reads — the caller does
 * not have to re-read the row and hope it has not moved again (PR #3450 review / LSP-17735).
 *
 * - `identityMatched: false` — a different uploadId/owner holds the row now (or it is gone). Nothing
 *   was written and nothing about the row's state can be claimed.
 * - `identityMatched: true` — the identity CAS passed (either on a valid owner, or on the exact
 *   corrupt-owner row a cancellation is allowed to release), so the only reason left is terminal
 *   precedence: `currentStatus` is the more authoritative terminal verdict that already won.
 * - `identityRefusalReason: "invalid-owner-id"` — the refusal came from an unusable owner identity on
 *   either side, not from a genuine owner change; callers log it instead of `identity-mismatch`.
 */
export type FileUploadSessionStatusUpdateOutcome =
	| { applied: true; uploadCancellationGeneration?: number }
	| {
			applied: false;
			identityMatched: boolean;
			identityRefusalReason?: "invalid-owner-id";
			currentStatus?: FileUploadSessionStatus;
	  };

export function warnFileUploadSessionStatusCasDecline(
	sessionId: string,
	status: FileUploadSessionStatus,
	outcome: Exclude<FileUploadSessionStatusUpdateOutcome, { applied: true }>
): void {
	warnUploadCasMismatch("[Upload] status CAS declined", {
		sessionId,
		status,
		reason: outcome.identityMatched ? "terminal-precedence" : (outcome.identityRefusalReason ?? "identity-mismatch"),
		currentStatus: outcome.currentStatus ?? "<row gone>"
	});
}

export async function updateFileUploadSessionStatusWithOutcome(
	sessionId: string,
	status: FileUploadSessionStatus,
	expectedSessionIdentity: Pick<FileUploadSession, "uploadId" | "uploadOwnerId" | "uploadOwnerClaimId">,
	options?: { retainChunks?: boolean; notifyOtherTabs?: boolean; lastError?: string; evidencedFailure?: boolean }
): Promise<FileUploadSessionStatusUpdateOutcome> {
	// LSP-16326: stamp the owning tab + a fresh heartbeat whenever a session enters an active
	// ("uploading"/"queued") status, refresh ownership at chunk checkpoints, and clear both when
	// it leaves active status — so resetUploadingSessions() can tell a live in-flight upload from
	// a genuinely orphaned one.
	// LSP-16528: requeue-for-retry flips status to "pending" here; a dropped connection used to surface
	// as "[Upload] failed to requeue session for retry — Transaction aborted". Reopen-and-retry.
	const outcome = await withIdbReopen(() =>
		db.transaction("rw", [...fileUploadSessionControlTables, db.fileUploadChunks, db.fileUploadChunkData], async () => {
			const session = await readFileUploadSession(sessionId);
			const validOwnerIdentityMatched =
				hasValidExpectedFileUploadOwnerId(expectedSessionIdentity.uploadOwnerId) &&
				session !== undefined &&
				session.uploadId === expectedSessionIdentity.uploadId &&
				session.uploadOwnerId === expectedSessionIdentity.uploadOwnerId &&
				session.uploadOwnerClaimId === expectedSessionIdentity.uploadOwnerClaimId;
			// A corrupt owner is never a lease, but an explicit cancellation may release the
			// exact observed row so the cancelled-session drain can abort its multipart upload.
			const exactCorruptOwnerCancellation =
				status === "cancelled" &&
				expectedSessionIdentity.uploadOwnerId !== undefined &&
				!isValidFileUploadOwnerId(expectedSessionIdentity.uploadOwnerId) &&
				session !== undefined &&
				session.uploadId === expectedSessionIdentity.uploadId &&
				// Same per-read clone problem as the orphan-reset CAS: without this the one carve-out written
				// FOR corrupt owners could never fire for the non-string subclass of corruption.
				toComparableUploadOwnerCell(session.uploadOwnerId) === toComparableUploadOwnerCell(expectedSessionIdentity.uploadOwnerId) &&
				toComparableUploadOwnerCell(session.uploadOwnerClaimId) ===
					toComparableUploadOwnerCell(expectedSessionIdentity.uploadOwnerClaimId);
			const identityMatched = validOwnerIdentityMatched || exactCorruptOwnerCancellation;
			if (
				!session ||
				!identityMatched ||
				// needs-attention is awaiting resolution, so an explicit user cancellation may
				// resolve it; other terminal transitions remain limited to idempotent writes.
				(isTerminalFileUploadSessionStatus(session.status) &&
					session.status !== status &&
					!((session.status === "needs-attention" || session.status === "recovery-blocked") && status === "cancelled"))
			) {
				return {
					applied: false,
					identityMatched,
					...(identityMatched || !hasInvalidUploadOwnerMismatch(expectedSessionIdentity.uploadOwnerId, session?.uploadOwnerId)
						? {}
						: { identityRefusalReason: "invalid-owner-id" as const }),
					currentStatus: session?.status
				};
			}
			if (status === "cancelled" && !options?.retainChunks) {
				await db.fileUploadChunkData.where({ sessionId }).delete();
				await db.fileUploadChunks.where({ sessionId }).delete();
			}
			// Idempotent cancellation must not mint a successor generation. A deliberate replacement may
			// already hold a freshly read tombstone snapshot; moving the generation under that snapshot
			// would make the supported re-entry fail even though no new cancellation occurred. Legacy
			// tombstones receive their first fence marker here so undefined can never authorize re-entry.
			if (status === "cancelled" && session.status === "cancelled") {
				const uploadCancellationGeneration = session.uploadCancellationGeneration ?? nextFileUploadCancellationGeneration(session);
				if (session.uploadCancellationGeneration === undefined) {
					await updateFileUploadSession(sessionId, { uploadCancellationGeneration });
				}
				return { applied: true, uploadCancellationGeneration } as const;
			}

			const uploadCancellationGeneration = status === "cancelled" ? nextFileUploadCancellationGeneration(session) : undefined;
			const sessionWithOwner = isActiveUploadSessionStatus(status)
				? { ...session, ...getFileUploadSessionOwnerFields() }
				: {
						...clearUploadOwnerFields(session),
						// Preserve this guarded API's legacy cleared-key shape while dropping context too.
						uploadOwnerId: undefined,
						uploadOwnerHeartbeatAt: undefined,
						uploadOwnerClaimId: undefined
					};
			await putFileUploadSession({
				...sessionWithOwner,
				status,
				...(uploadCancellationGeneration === undefined ? {} : { uploadCancellationGeneration }),
				lastError: resolveNextLastError(session, status, options?.lastError),
				...resolveNextRetryCeilingFields(session, status, options?.evidencedFailure === true, Date.now())
			});
			return { applied: true, ...(uploadCancellationGeneration === undefined ? {} : { uploadCancellationGeneration }) } as const;
		})
	);
	const updated = outcome.applied;
	if (
		!isActiveUploadSessionStatus(status) &&
		(updated || isObservedFileUploadSessionRelease(await getFileUploadSessionById(sessionId), status, expectedSessionIdentity.uploadId))
	) {
		forgetCurrentPageInitiatedFileUploadSessions(sessionId);
	}
	if (updated && status === "cancelled" && options?.notifyOtherTabs !== false) {
		publishFileUploadSessionInvalidation(sessionId, expectedSessionIdentity.uploadId);
	}
	return outcome;
}

export async function updateFileUploadSessionStatus(
	sessionId: string,
	status: FileUploadSessionStatus,
	expectedSessionIdentity?: Pick<FileUploadSession, "uploadId" | "uploadOwnerId" | "uploadOwnerClaimId">,
	options?: { retainChunks?: boolean; notifyOtherTabs?: boolean; lastError?: string; evidencedFailure?: boolean }
): Promise<boolean> {
	if (expectedSessionIdentity !== undefined) {
		const outcome = await updateFileUploadSessionStatusWithOutcome(sessionId, status, expectedSessionIdentity, options);
		if (!outcome.applied) {
			// This wrapper collapses the outcome to a boolean, so every caller that takes it (cancellation,
			// terminal tombstones) loses why the CAS declined. Log it here, once, where the outcome exists.
			warnFileUploadSessionStatusCasDecline(sessionId, status, outcome);
		}
		return outcome.applied;
	}

	if (isActiveUploadSessionStatus(status)) {
		return withIdbReopen(() =>
			db.transaction("rw", fileUploadSessionControlTables, async () => {
				const session = await readFileUploadSession(sessionId);
				if (!session || isTerminalFileUploadSessionStatus(session.status)) return false;
				await updateFileUploadSession(sessionId, {
					status,
					...getFileUploadSessionOwnerFields(),
					lastError: resolveNextLastError(session, status, options?.lastError)
				});
				return true;
			})
		);
	}

	const updated = await withIdbReopen(() =>
		db.transaction("rw", fileUploadSessionControlTables, async () => {
			const session = await readFileUploadSession(sessionId);
			if (!session || isTerminalFileUploadSessionStatus(session.status)) return false;
			await putFileUploadSession({
				...clearUploadOwnerFields(session),
				status,
				...(status === "cancelled" ? { uploadCancellationGeneration: nextFileUploadCancellationGeneration(session) } : {}),
				lastError: resolveNextLastError(session, status, options?.lastError),
				...resolveNextRetryCeilingFields(session, status, options?.evidencedFailure === true, Date.now())
			});
			return true;
		})
	);
	if (updated || isObservedFileUploadSessionRelease(await getFileUploadSessionById(sessionId), status)) {
		forgetCurrentPageInitiatedFileUploadSessions(sessionId);
	}
	if (updated && status === "cancelled" && options?.notifyOtherTabs !== false) {
		publishFileUploadSessionInvalidation(sessionId, undefined);
	}
	return updated;
}

export async function getFileUploadSessionById(sessionId: string): Promise<FileUploadSession | undefined> {
	return withIdbReopen(() => readFileUploadSession(sessionId));
}

/**
 * LSP-19609: one read for many rows. The dialog's deferred-row follow-up watches every row it has
 * handed to the background on a single timer, so a batch that deferred hundreds of rows costs one
 * `bulkGet` per tick rather than one read per row. A missing id maps to `undefined`.
 */
export async function getFileUploadSessionsByIds(sessionIds: readonly string[]): Promise<Map<string, FileUploadSession | undefined>> {
	if (sessionIds.length === 0) return new Map();
	// Same two-store union as `readFileUploadSession`, large store first: a session with more than
	// LEGACY_MULTIPART_PART_CAPACITY chunks lives in `largeFileUploadSessions`, and reading only the
	// small store would report it missing (Bugbot, PR #4812).
	const ids = [...sessionIds];
	const [largeRows, smallRows] = await withIdbReopen(() =>
		Promise.all([db.largeFileUploadSessions.bulkGet(ids), db.fileUploadSessions.bulkGet(ids)])
	);
	return new Map(sessionIds.map((sessionId, index) => [sessionId, largeRows[index] ?? smallRows[index] ?? undefined]));
}

export async function markFileUploadSessionComplete(
	sessionId: string,
	expectedUploadId: string,
	expectedOwnerId?: string,
	expectedOwnerClaimId?: string
): Promise<boolean> {
	return withIdbReopen(() =>
		db.transaction("rw", fileUploadSessionControlTables, async () => {
			const session = await readFileUploadSession(sessionId);
			if (
				!hasValidExpectedFileUploadOwnerId(expectedOwnerId) ||
				!session ||
				session.uploadId !== expectedUploadId ||
				hasInvalidUploadOwnerMismatch(expectedOwnerId, session?.uploadOwnerId) ||
				session.uploadOwnerId !== expectedOwnerId ||
				session.uploadOwnerClaimId !== expectedOwnerClaimId ||
				// extended architecture campaign: a non-complete terminal verdict wins over delayed completion.
				(isTerminalFileUploadSessionStatus(session.status) && session.status !== "complete")
			) {
				if (hasInvalidUploadOwnerMismatch(expectedOwnerId, session?.uploadOwnerId)) {
					warnUploadCasMismatch(
						"[Upload] stale terminal checkpoint ignored",
						getSafeUploadOwnerMismatchContext(
							{
								sessionId,
								expectedUploadId,
								actualUploadId: session?.uploadId,
								expectedOwnerClaimId,
								actualOwnerClaimId: session?.uploadOwnerClaimId
							},
							expectedOwnerId,
							session?.uploadOwnerId
						)
					);
				} else {
					warnUploadCasMismatch(
						`[Upload] stale terminal checkpoint ignored for session ${sessionId}: ` +
							`expected owner ${expectedOwnerId ?? "<none>"}, actual owner ${session?.uploadOwnerId ?? "<missing>"}; ` +
							"persisted owner/upload identity changed",
						{ sessionId, expectedUploadId, expectedOwnerId, expectedOwnerClaimId }
					);
				}
				return false;
			}

			// Keep the completing owner through the durable terminal marker. Resume performs a second
			// ownership gate before cleanup, and cleanup deletes this owner-stamped complete row atomically.
			await updateFileUploadSession(sessionId, { status: "complete", completedAt: session.completedAt ?? Date.now() });
			return true;
		})
	);
}

export async function markFileUploadSessionPendingForResume(
	sessionId: string,
	expectedSessionIdentity?: Pick<FileUploadSession, "uploadId" | "uploadOwnerId" | "uploadOwnerClaimId">,
	options?: {
		retryAt?: number | null;
		retryAttempt?: number;
		reason?: string;
		// LSP-19537: object sets both laneIntent* cells; null clears both; undefined (every caller
		// before this ticket) preserves whatever the row already carries via the existing spread.
		laneIntent?: { laneId: string; at: number } | null;
	}
): Promise<boolean> {
	const updatedCount = await withIdbReopen(() =>
		db.transaction("rw", fileUploadSessionControlTables, async () => {
			const session = await readFileUploadSession(sessionId);
			const refusalReason = hasInvalidUploadOwnerMismatch(expectedSessionIdentity?.uploadOwnerId, session?.uploadOwnerId)
				? "invalid-owner-id"
				: !session
					? "session-missing"
					: isTerminalFileUploadSessionStatus(session.status)
						? "terminal-session"
						: "identity-mismatch";
			if (
				!session ||
				isTerminalFileUploadSessionStatus(session.status) ||
				(expectedSessionIdentity !== undefined &&
					(!hasValidExpectedFileUploadOwnerId(expectedSessionIdentity.uploadOwnerId) ||
						session.uploadId !== expectedSessionIdentity.uploadId ||
						session.uploadOwnerId !== expectedSessionIdentity.uploadOwnerId ||
						session.uploadOwnerClaimId !== expectedSessionIdentity.uploadOwnerClaimId))
			) {
				warnUploadCasMismatch(
					"[Upload] pending-for-resume release ignored",
					getSafeUploadOwnerMismatchContext(
						{
							sessionId,
							expectedUploadId: expectedSessionIdentity?.uploadId,
							actualUploadId: session?.uploadId,
							expectedOwnerClaimId: expectedSessionIdentity?.uploadOwnerClaimId,
							actualOwnerClaimId: session?.uploadOwnerClaimId,
							currentStatus: session?.status,
							reason: refusalReason
						},
						expectedSessionIdentity?.uploadOwnerId,
						session?.uploadOwnerId
					)
				);
				return 0;
			}

			await putFileUploadSession({
				...clearUploadOwnerFields(session),
				status: "pending",
				// LSP-19088: a caller that knows WHY it is handing the row back can now say so. Releasing
				// with no reason still clears it, as it always has — a clean re-queue should not inherit a
				// stale message — but "released after a failure" and "released clean" used to be
				// indistinguishable on the durable row. A parked row observed in the field carried
				// `retryAttempt: 12` and no `lastError` at all: twelve attempts and nothing on the row to
				// say what had gone wrong twelve times.
				lastError: options?.reason,
				retryAt: options?.retryAt,
				retryAttempt: options?.retryAttempt,
				...(options && "laneIntent" in options
					? {
							laneIntentId: options.laneIntent?.laneId,
							laneIntentAt: options.laneIntent?.at
						}
					: {})
			});
			return 1;
		})
	);
	if (
		updatedCount > 0 ||
		isObservedFileUploadSessionRelease(await getFileUploadSessionById(sessionId), "pending", expectedSessionIdentity?.uploadId)
	) {
		forgetCurrentPageInitiatedFileUploadSessions(sessionId);
	}
	return updatedCount > 0;
}

/**
 * LSP-19537: clear laneIntentId/laneIntentAt for the given rows, CAS'd on the LANE ID only — never
 * on owner fields or status, because the row may legitimately be "uploading" under this same lane's
 * own claim at this exact instant, or "pending" again after a retry. Called from
 * FileUploadDialog.vue's two batch `finally` blocks so a foreign takeover that already happened
 * (this lane's row was reassigned to a different lane while this lane's cleanup was still pending)
 * is never clobbered — that CAS declines and is logged, per CLAUDE.md's silent-failure rule.
 */
export async function releaseFileUploadLaneIntent(sessionIds: readonly string[], laneId: string): Promise<void> {
	await withIdbReopen(() =>
		db.transaction("rw", fileUploadSessionControlTables, async () => {
			for (const sessionId of sessionIds) {
				const session = await readFileUploadSession(sessionId);
				if (!session) continue;
				if (session.laneIntentId !== laneId) {
					if (session.laneIntentId !== undefined) {
						console.warn("[Upload] releaseFileUploadLaneIntent declined — row now belongs to a different lane", {
							sessionId,
							requestingLaneId: laneId,
							currentLaneId: session.laneIntentId
						});
					}
					continue;
				}
				await putFileUploadSession({ ...session, laneIntentId: undefined, laneIntentAt: undefined });
			}
		}),
		{ operationName: "release-lane-intent" }
	);
}

/** The thirteen durable columns a multipart target owns. Every target write in this app persists exactly these. */
export type FileUploadSessionTargetFields = Pick<
	FileUploadSession,
	| "uploadId"
	| "s3Key"
	| "bucket"
	| "chunkUrlsJson"
	| "endpointFamily"
	| "registryId"
	| "attemptId"
	| "generation"
	| "takeGeneration"
	| "targetEnvelopesJson"
 | "contractVersion"
 | "objectUploadJson"
 | "protocolSelectionPending"
	// Plan 3a (LSP-17653): the three columns a resumed chunked-object row needs. They travel with the
	// target because they only make sense together — see the shared `chunkedObjectLaneColumns` rule.
	| "transport"
	| "chunkSizeBytes"
	| "priorityChunkIndexesJson"
>;

export type FileUploadSessionReinitializationOutcome =
	| { applied: true; session: FileUploadSession }
	| { applied: false; reason: "session-missing" }
	| {
			applied: false;
			reason: "identity-mismatch" | "invalid-owner-id" | "terminal-session" | "endpoint-family-mismatch" | "buffers-missing" | "target-already-attached";
			session: FileUploadSession;
	  };

/**
 * LSP-22008: a placeholder whose bytes were never fully saved and whose target was never requested. Its
 * bytes can only come from the original File, which dies with the page, so once its writer is gone it can
 * never finish. Whether the writer IS gone is the caller's liveness question, never this predicate's — a
 * live cloud-file read legitimately sits at zero chunks for minutes. Strict `=== true` keeps pre-LSP-19521
 * rows (no `targetPending`) out.
 */
export function isUnpreparedPlaceholderSession(session: FileUploadSession): boolean {
	return (
		session.targetPending === true &&
		hasNoServerUploadTarget(session) &&
		session.sourcePrepared !== true &&
		session.targetInitiationStarted !== true
	);
}

/** Commit only while the retained source and the existing owner/cancellation identity still match. */
export async function commitFileUploadObjectChoice(session: FileUploadSession, choice: UploadObjectControlRow): Promise<FileUploadSession> {
	return withIdbReopen(() =>
		db.transaction("rw", fileUploadSessionControlTables, async () => {
			const current = await readFileUploadSession(session.id);
			if (
				!current ||
				!current.targetPending ||
				!current.sourcePrepared ||
				isTerminalFileUploadSessionStatus(current.status) ||
				current.uploadId !== session.uploadId ||
				current.createdAt !== session.createdAt ||
				current.fileSize !== session.fileSize ||
				current.fileType !== session.fileType ||
				current.totalChunks !== session.totalChunks ||
				current.chunkSizeBytes !== session.chunkSizeBytes ||
				current.endpointFamily !== "uploads" ||
				current.uploadOwnerId !== session.uploadOwnerId ||
				current.uploadOwnerClaimId !== session.uploadOwnerClaimId ||
				current.uploadCancellationGeneration !== session.uploadCancellationGeneration
			) {
				console.warn("[Upload] Object source choice refused after source or owner changed", {
					sessionId: session.id,
					status: current?.status
				});
				throw new Error("Upload source changed before protocol selection");
			}
			readUploadKnownSizeControl(current);
			if (current.contractVersion !== undefined || !current.protocolSelectionPending) return current;
			readUploadKnownSizeControl(choice);
			await updateFileUploadSession(session.id, choice);
			return { ...current, ...choice };
		})
	);
}

/** Fence initiation before network access so cancellation can reconcile a lost response. */
export async function beginDeferredFileUploadTarget(session: FileUploadSession): Promise<boolean> {
	return withIdbReopen(() => db.transaction("rw", fileUploadSessionControlTables, async () => {
		const current = await readFileUploadSession(session.id);
		if (!current || !current.targetPending || !current.sourcePrepared || isTerminalFileUploadSessionStatus(current.status) ||
			current.uploadId !== session.uploadId || current.uploadOwnerId !== session.uploadOwnerId ||
			current.uploadOwnerClaimId !== session.uploadOwnerClaimId) {
			console.warn("[Upload] Deferred target initiation refused after session changed", {
				conversationId: session.conversationId, sessionId: session.id, status: current?.status, sourcePrepared: current?.sourcePrepared
			});
			return false;
		}
		await updateFileUploadSession(session.id, { targetInitiationStarted: true });
		return true;
	}));
}

/**
 * R-1 ("there is no destroy", owner directive 2026-08-26): repoint a session at a freshly minted
 * multipart target and return it to the pending pool, instead of deleting the intact local bytes a
 * server `UPLOAD_LOST` verdict made un-completable against the DEAD target. Every retained chunk
 * buffer survives untouched; only the per-chunk `isUploaded`/`etag` checkpoints are cleared, because
 * those describe parts of the upload that no longer exists.
 *
 * CAS'd on the caller's CAPTURED identity, exactly like `markFileUploadSessionPendingForResume`
 * above: a successor claim taken between the LOST verdict and this write is refused, never
 * clobbered. The buffer-presence re-check happens inside the same transaction so a chunk-data purge
 * racing this write cannot leave the row repointed at a target it can no longer fill.
 */
export async function reinitializeFileUploadSessionTarget(
	sessionId: string,
	expectedSessionIdentity: Pick<FileUploadSession, "uploadId" | "uploadOwnerId" | "uploadOwnerClaimId">,
	targetFields: FileUploadSessionTargetFields,
	initialAttachment?: { retainOwner: boolean }
): Promise<FileUploadSessionReinitializationOutcome> {
	const outcome = await withIdbReopen(() =>
		db.transaction("rw", [...fileUploadSessionControlTables, db.fileUploadChunks, db.fileUploadChunkData], async () => {
			const session = await readFileUploadSession(sessionId);
			if (!session) return { applied: false, reason: "session-missing" } as const;
			const refusalReason = initialAttachment && session.targetPending !== true
				? ("target-already-attached" as const)
				: hasInvalidUploadOwnerMismatch(expectedSessionIdentity.uploadOwnerId, session.uploadOwnerId)
				? ("invalid-owner-id" as const)
				: isTerminalFileUploadSessionStatus(session.status)
					? ("terminal-session" as const)
					: !hasValidExpectedFileUploadOwnerId(expectedSessionIdentity.uploadOwnerId) ||
						  session.uploadId !== expectedSessionIdentity.uploadId ||
						  session.uploadOwnerId !== expectedSessionIdentity.uploadOwnerId ||
						  session.uploadOwnerClaimId !== expectedSessionIdentity.uploadOwnerClaimId
						? ("identity-mismatch" as const)
						: // The endpoint family is immutable for a persisted row: a legacy-shaped target must never
							// overwrite an `/uploads` row's opaque identity, and vice versa.
							(session.endpointFamily ?? "legacy") !== (targetFields.endpointFamily ?? "legacy")
							? ("endpoint-family-mismatch" as const)
							: undefined;
			if (refusalReason) {
				warnUploadCasMismatch(
					"[Upload] target reinitialization refused",
					getSafeUploadOwnerMismatchContext(
						{
							sessionId,
							expectedUploadId: expectedSessionIdentity.uploadId,
							actualUploadId: session.uploadId,
							expectedOwnerClaimId: expectedSessionIdentity.uploadOwnerClaimId,
							actualOwnerClaimId: session.uploadOwnerClaimId,
							currentStatus: session.status,
							reason: refusalReason
						},
						expectedSessionIdentity.uploadOwnerId,
						session.uploadOwnerId
					)
				);
				return { applied: false, reason: refusalReason, session } as const;
			}
			// Cheap in-transaction presence proof (the caller has already verified byte integrity):
			// repointing a row whose buffers vanished in the meantime would strand it against a target
			// nothing can fill. Counting rows, not reading buffers, keeps this inside the writer fence's
			// transaction without materializing the whole file.
			const retainedBufferCount = await db.fileUploadChunkData.where({ sessionId }).count();
			if (retainedBufferCount < session.totalChunks) {
				console.warn("[Upload] target reinitialization refused — retained chunk buffers are incomplete", {
					sessionId,
					uploadId: session.uploadId,
					retainedBufferCount,
					totalChunks: session.totalChunks
				});
				return { applied: false, reason: "buffers-missing", session } as const;
			}
			const objectControl = readUploadKnownSizeControl(targetFields, true);
			uploadObjectControlColumns(objectControl ? { contractVersion: 2, objectUpload: objectControl } : undefined, session);
			await putFileUploadSession({
				...(initialAttachment?.retainOwner ? session : clearUploadOwnerFields(session)),
				...targetFields,
				...(initialAttachment ? { targetPending: false, targetReattached: false } : { targetReattached: true }),
				urlCreatedAt: Date.now(),
				status: initialAttachment?.retainOwner ? session.status : "pending",
				lastError: undefined,
				// R-0: a fresh target is genuine progress, not another failed attempt. The row re-enters the
				// normal scheduled pool immediately rather than inheriting the dead target's backoff.
				retryAt: null,
				retryAttempt: 0
			});
			// The etags belong to the dead multipart upload; keeping them would complete the NEW target
			// with parts it never received. The buffers themselves are deliberately left in place.
			await db.fileUploadChunks.where({ sessionId }).modify({ isUploaded: 0, etag: null });
			const reinitialized = await readFileUploadSession(sessionId);
			if (!reinitialized) return { applied: false, reason: "session-missing" } as const;
			return { applied: true, session: reinitialized } as const;
		})
	);
	if (outcome.applied && !initialAttachment?.retainOwner) forgetCurrentPageInitiatedFileUploadSessions(sessionId);
	return outcome;
}

/**
 * Reset orphaned "uploading"/"queued" sessions back to "pending" so the resume poller picks them up.
 * On startup, also requeue parked sessions for one fresh authoritative resume probe.
 * Called at startup and from the Web-Locks-protected resume cycle. Only reset rows this tab can
 * prove are abandoned: ownerless legacy rows, this tab's own rows not admitted by the current page
 * load (the admission check itself, isCurrentPageAdmittedRow below, is identical at startup and on
 * the recurring cycle — but for an un-admitted row this page's own held lock reclaims immediately
 * only at startup; on the recurring cycle it must additionally clear a grace window, since a live
 * duplicate tab can share this same owner id), rows with owner metadata but no heartbeat, or
 * foreign-owned rows whose owner lock is free after a grace period. Web Locks are the liveness
 * signal: a live owner keeps its lock even when WebKit throttles heartbeats stale, while a killed
 * tab releases the lock and can be recovered without waiting FILE_UPLOAD_SESSION_OWNER_STALE_MS.
 */
/**
 * LSP-19144: is THIS ROW's upload lane provably dead, as opposed to its owning PAGE being alive?
 *
 * A held Web Lock only ever answered the second question. It is keyed on the page's owner id, it is
 * released only when the page dies, and the owner id lives in sessionStorage — so a reload re-takes
 * it. A lane that wedges therefore leaves a live lock behind a frozen heartbeat and its row is
 * pinned for the life of the tab, which is the unbounded strand this predicate exists to close.
 *
 * The durable writer lease answers the first question, and it already exists per row. Once
 * `adminUploadLease`'s `durableOwnershipLost` is set, `performTouch` returns before its `renewWriter`
 * loop, so a wedged lane stops renewing and its lease lapses within `ADMIN_WRITER_LEASE_TTL_MS`.
 * The lock does not lapse. That difference is the whole signal.
 *
 * Both conjuncts below are load-bearing:
 *
 *  - The heartbeat bar stays, because the lease TTL (120s) sits BELOW the ~136s a healthy lane can
 *    legitimately go between touches (progress interval + chunk stall timeout + retry backoff). The
 *    lease alone would reclaim from a slow-but-live lane; `FILE_UPLOAD_SESSION_OWNER_STALE_MS` (300s)
 *    is what keeps that from happening.
 *  - An ABSENT record is not evidence of death — it means no lane ever durably claimed this row, so
 *    the lock stays the only authority. Only a record that exists AND has expired is affirmative
 *    proof that a lane took this row and stopped renewing. Anything unreadable fails closed.
 *
 * This is deliberately NOT the staleness ceiling that was implemented and reverted under LSP-19143.
 * Three tests pin that a stale heartbeat alone must never beat a held lock (fileUploadDb LSP-16326 /
 * LSP-17574 r1, appUploadRecoveryGuard LSP-17034); none of them writes a lease record, so all three
 * still take the keep path here.
 */
async function isUploadRowLaneProvenDead(session: FileUploadSession, now: number): Promise<boolean> {
	if (!session.uploadOwnerHeartbeatAt) return false;
	if (now - session.uploadOwnerHeartbeatAt <= FILE_UPLOAD_SESSION_OWNER_STALE_MS) return false;
	try {
		const record = await withIdbReopen(() => db.table("leases").get(writerLeaseKey("contact-admin", { itemId: session.id })));
		if (record === undefined) return false;
		return getAdminDurableWriterLeaseState(record) === "free";
	} catch (error) {
		// Never let an unreadable lease turn into a reclaim: that is how a live row gets stolen.
		console.warn(`[Upload] Could not inspect the durable writer lease for session ${session.id}; leaving the row alone`, error);
		return false;
	}
}

/**
 * LSP-22113: the same-owner lock proof only exists until this page takes the owner lock. Before then
 * a free lock is the existing orphan signal (no page with this owner id holds it), so a row this page
 * did not admit belongs to a dead page load. But the reset is fenced, and the dead page's writer lease
 * outlives the page by up to `ADMIN_WRITER_LEASE_TTL_MS`. Once the drain holds the lock, a later pass
 * can no longer tell that row from a live duplicated tab's (LSP-16326) and fell back to the 300 s
 * heartbeat bar, so rows that were uploading at a refresh sat on "resuming shortly" for over five
 * minutes.
 *
 * Remembering the verdict is safe only while nothing has touched the row since: a page that claims
 * or uploads it changes its claim id or heartbeat, and any change drops the proof for good.
 *
 * The refusing writer lease is checked the same way. A dead page cannot renew it; a live one renews it
 * on every progress touch. That catches the one case the lock proof misses — a duplicated tab that was
 * uploading before its own owner-lock request was granted, so the lock looked free (ls-ai-review).
 */
const PRIOR_LOAD_ORPHAN_PROOF_CELLS = ["uploadId", "status", "uploadOwnerId", "uploadOwnerHeartbeatAt", "uploadOwnerClaimId"] as const;

/**
 * LSP-22113 (ls-ai-review, 54d197503b): neither the cells nor the lease fingerprint can see a lease that
 * merely lapsed, and a live duplicated tab's lease can lapse between touches while its PUT still runs. A
 * live upload restamps the row's heartbeat at least every progress interval + first-byte timeout + stall
 * timeout + retry delay (~118 s today); the proof only acts once the heartbeat is older than that, so it
 * can never reset a row a live page is still touching. Kept below the 300 s bar, which it exists to beat.
 * `fileUploadDb.reload-orphan-proof.spec.ts` pins both bounds against the real constants.
 */
export const PRIOR_LOAD_PROOF_HEARTBEAT_FLOOR_MS = 150_000;

/**
 * The refusing writer lease's identity and expiry, compared and never parsed — or the read error.
 * An unreadable lease is "unknown this pass", never a fingerprint: it must not become a proof, and it
 * must not drop one either (ls-ai-review), mirroring `isUploadRowLaneProvenDead`.
 */
async function readWriterLeaseFingerprint(sessionId: string): Promise<{ fingerprint: string } | { unreadable: unknown }> {
	try {
		const record = (await withIdbReopen(() => db.table("leases").get(writerLeaseKey("contact-admin", { itemId: sessionId })))) as
			| Partial<DurableLeaseRecord>
			| undefined;
		if (record === undefined) return { fingerprint: "absent" };
		return { fingerprint: JSON.stringify([record.ownerId, record.epoch, record.expiresAt]) };
	} catch (error) {
		return { unreadable: error };
	}
}

async function rememberPriorLoadOrphanProof(
	session: FileUploadSession,
	reason: string | undefined,
	// Structural on purpose: this module only dynamic-imports adminUploadLease (AdminWriterFenceRefusal).
	fenced: { kind: "applied" } | { kind: "skipped-not-owned"; refusal: string }
): Promise<void> {
	// Only an observed FREE owner lock is proof. `same-owner-prior-load` at startup is decided while this
	// page already holds the lock, where a duplicated tab's live row looks the same (Codex review): its
	// writer lease can lapse between touches while its upload is still running, so it is never remembered.
	const isLockProof = reason === "same-owner-lock-free" || reason === "same-owner-prior-load-proven-earlier";
	// Only a live writer lease means "wait for it to lapse". Ownership lost before the commit means a live
	// writer just took the row; an unavailable fence says nothing about the row (ls-ai-review).
	if (!isLockProof || fenced.kind !== "skipped-not-owned" || fenced.refusal !== "writer-held") {
		provenPriorLoadOrphans.delete(session.id);
		return;
	}
	if (provenPriorLoadOrphans.has(session.id)) return;
	const writerLease = await readWriterLeaseFingerprint(session.id);
	if ("unreadable" in writerLease) {
		// Never let an unreadable lease become a reclaim: no proof, so the 300 s heartbeat rule decides.
		console.warn("[Upload] orphan proof not kept — the writer lease could not be read this pass", {
			sessionId: session.id,
			reason,
			error: writerLease.unreadable
		});
		return;
	}
	console.info("[Upload] orphan proof kept until the previous page's writer lease lapses", {
		sessionId: session.id,
		reason,
		heartbeatAgeMs: session.uploadOwnerHeartbeatAt ? Date.now() - session.uploadOwnerHeartbeatAt : undefined
	});
	provenPriorLoadOrphans.set(session.id, { session, writerLease: writerLease.fingerprint });
}

async function isStillProvenPriorLoadOrphan(session: FileUploadSession): Promise<boolean> {
	const proof = provenPriorLoadOrphans.get(session.id);
	if (!proof) return false;
	const changed: string[] = PRIOR_LOAD_ORPHAN_PROOF_CELLS.filter((cell) => session[cell] !== proof.session[cell]);
	if (changed.length === 0) {
		const writerLease = await readWriterLeaseFingerprint(session.id);
		if ("unreadable" in writerLease) {
			// Unknown this pass: neither reclaim on it nor forget a proof that may still hold.
			console.warn("[Upload] orphan proof kept; writer lease unreadable this pass", {
				sessionId: session.id,
				error: writerLease.unreadable
			});
			return false;
		}
		if (writerLease.fingerprint !== proof.writerLease) changed.push("writerLease");
	}
	if (changed.length === 0) return true;
	provenPriorLoadOrphans.delete(session.id);
	console.warn("[Upload] orphan proof dropped — the row's lease moved since it was proven", { sessionId: session.id, changed });
	return false;
}

export async function resetUploadingSessions(
	resetCurrentTabOwnedRows: boolean = true,
	_currentOwnerClaimId?: string,
	onOrphanAwaitingHeartbeat?: (session: FileUploadSession) => void
): Promise<void> {
	// LSP-16528: startup reset feeds the resume poller; reopen-and-retry on a dropped connection.
	// resetCurrentTabOwnedRows no longer single-handedly decides whether a same-owner active row is
	// reclaimed — that decision is isCurrentPageAdmittedRow (this page's own bookkeeping of sessions
	// it initiated), checked the same way at startup and on the recurring cycle. A row this page
	// admitted is always protected, whether or not resetCurrentTabOwnedRows is true. A same-owner row
	// this page has NOT admitted now falls through to the same Web-Locks-liveness/heartbeat fallback
	// used for foreign owners, on both startup and the recurring cycle (LSP-17574). Outside this
	// per-row decision, resetCurrentTabOwnedRows still controls whether user-actionable parked
	// rows are included in this pass, and whether completed sessions are purged. System-owned
	// Recovery-blocked rows are scanned so the terminal predicate can preserve and diagnose them;
	// they never join recurring recovery again.
	if (resetCurrentTabOwnedRows) {
		await withIdbReopen(() =>
			db.transaction("rw", [...fileUploadSessionControlTables, db.fileUploadChunks, db.fileUploadChunkData], async () => {
				const completedSessions = await listFileUploadSessionsByStatus("complete");
				for (const session of completedSessions) {
					await db.fileUploadChunkData.where({ sessionId: session.id }).delete();
					await db.fileUploadChunks.where({ sessionId: session.id }).delete();
					await deleteFileUploadSessionControl(session.id);
				}
			})
		);
	}
	const reclaimableStatuses: FileUploadSessionStatus[] = resetCurrentTabOwnedRows
		? ["pending", "uploading", "queued", "needs-attention", "recovery-blocked"]
		: ["pending", "uploading", "queued", "recovery-blocked"];
	const candidates = await withIdbReopen(() => listFileUploadSessionsByStatus(...reclaimableStatuses));
	if (resetCurrentTabOwnedRows) {
		console.info("[Upload] Startup upload retry inventory", {
			terminallyParked: candidates.filter(isTerminallyParkedFileUploadSession).length,
			highRetryAttempt: candidates.filter((session) => (session.retryAttempt ?? 0) > 10).length,
			retryAttemptThreshold: 10
		});
	}
	// A proven row that finished, was cancelled or was deleted no longer needs its proof.
	const candidateIds = new Set(candidates.map((session) => session.id));
	for (const sessionId of [...provenPriorLoadOrphans.keys()]) {
		if (!candidateIds.has(sessionId)) provenPriorLoadOrphans.delete(sessionId);
	}
	if (candidates.length === 0) {
		// Diagnostic (silent-failure rule): a cold iOS-Safari IndexedDB right after a tab kill can
		// return zero here before the store is warm; the warmup catch-up passes re-run this shortly.
		console.info(`[Upload] reset scan found 0 reclaimable (${reclaimableStatuses.join("/")}) session(s)`);
		return;
	}

	const currentOwnerId = getCurrentFileUploadSessionOwnerId();
	const now = Date.now();
	const resettable: FileUploadSession[] = [];
	// Which liveness branch judged each row orphaned — logged with the outcome, since the sweep can now delete.
	const reclaimReasons = new Map<string, string>();
	const reclaim = (session: FileUploadSession, reason: string) => {
		resettable.push(session);
		reclaimReasons.set(session.id, reason);
	};
	let liveOwnerLockHeldCount = 0;
	let terminallyParkedCount = 0;
	for (const session of candidates) {
		// LSP-19384: a row that has stopped being retried automatically must stay stopped across the
		// reload. This is the ONLY reason the reported symptom survives a reload and a re-login: this
		// pass used to write every parked row back to "pending" with `lastError: undefined`, which
		// resurrected the row AND destroyed the evidence that parked it — so the requeue loop's
		// terminal refusal, which reads exactly that field, could never hold past a page load.
		// Checked before every ownership branch: the verdict does not depend on who owns the row.
		if (isTerminallyParkedFileUploadSession(session)) {
			terminallyParkedCount++;
			continue;
		}
		// LSP-22008: an unprepared placeholder is never legitimately `pending` — only an earlier sweep or a
		// retry release puts it there — so it stays a candidate instead of waiting for a resume it cannot finish.
		if (
			session.status === "pending" &&
			(session.uploadOwnerId === undefined || isValidFileUploadOwnerId(session.uploadOwnerId)) &&
			!isUnpreparedPlaceholderSession(session)
		)
			continue;
		if (session.uploadOwnerId === undefined) {
			reclaim(session, "ownerless"); // ownerless legacy row
			continue;
		}
		if (session.uploadOwnerId === "") {
			warnUploadCasMismatch("[Upload] invalid upload owner reset decision", {
				sessionId: session.id,
				ownerIdLength: 0,
				action: "reclaim"
			});
			reclaim(session, "empty-owner"); // legacy empty owner is ownerless, not a live lease
			continue;
		}
		if (!isValidFileUploadOwnerId(session.uploadOwnerId)) {
			const usableHeartbeatAt = getUsableFileUploadOwnerHeartbeatAt(session.uploadOwnerHeartbeatAt, now);
			const reclaimsInvalidOwner = usableHeartbeatAt === undefined || now - usableHeartbeatAt > FILE_UPLOAD_SESSION_OWNER_STALE_MS;
			warnUploadCasMismatch("[Upload] invalid upload owner reset decision", {
				sessionId: session.id,
				ownerIdLength: getFileUploadOwnerIdLength(session.uploadOwnerId),
				action: reclaimsInvalidOwner ? "reclaim" : "skip-fresh-heartbeat"
			});
			if (reclaimsInvalidOwner) {
				reclaim(session, "invalid-owner-stale-heartbeat"); // invalid ownership cannot grant a lease, but a missing/stale exact observed lease can be released
			}
			continue;
		}
		if (session.uploadOwnerId === currentOwnerId) {
			// A duplicated tab copies sessionStorage and therefore shares this owner id. A held lock
			// identifies a prior-load orphan unless this page admitted this exact active row; that
			// live row must not reach any lock or heartbeat fallback that could reclaim it.
			const isCurrentPageAdmittedRow = hasCurrentPageInitiatedFileUpload() && isCurrentPageInitiatedFileUploadSession(session);
			if (isCurrentPageAdmittedRow) {
				try {
					if (!resetCurrentTabOwnedRows) {
						console.warn(
							`[Upload] recurring orphan reset skipped current-tab-owned session ${session.id} because it may still be active`
						);
					}
				} catch {
					// Diagnostics must never abort recovery of other orphan candidates.
				}
				continue;
			}
			if (acquiredUploadOwnerLivenessLocks.has(currentOwnerId)) {
				// At STARTUP, holding our own lock proves any un-admitted row under this id is a
				// prior-load orphan — this page hasn't created any sessions of its own yet, so reclaim
				// immediately. On the RECURRING cycle a duplicated tab shares this owner id while
				// genuinely live: only one of the two tabs can ever hold the named lock, so THIS page
				// holding it does NOT by itself prove a sibling tab sharing the same id is dead. Unlike
				// the foreign-owner branch's FILE_UPLOAD_OWNER_LOCK_GRACE_MS (60s) — which protects a
				// narrow, bounded lock-handoff race — a live sibling's heartbeat only refreshes at chunk
				// boundaries (architecture-gap review, LSP-17574) and a single healthy chunk PUT can run
				// well past 60s, so the 60s grace would false-positive-reclaim a slow-but-live sibling.
				// Require the full FILE_UPLOAD_SESSION_OWNER_STALE_MS staleness bar used everywhere else
				// in this function as the "no stronger proof available" fallback.
				const heartbeatPastStale =
					!session.uploadOwnerHeartbeatAt || now - session.uploadOwnerHeartbeatAt > FILE_UPLOAD_SESSION_OWNER_STALE_MS;
				// Known limitation: an offline wait or long completion can outlive this heartbeat bar; reclaim then causes a recoverable handoff without data loss.
				if (resetCurrentTabOwnedRows || heartbeatPastStale) {
					reclaim(session, "same-owner-prior-load");
				} else if (
					(await isStillProvenPriorLoadOrphan(session)) &&
					now - (session.uploadOwnerHeartbeatAt ?? now) > PRIOR_LOAD_PROOF_HEARTBEAT_FLOOR_MS
				) {
					// LSP-22113: an earlier pass proved this row's page load dead before this page took the
					// lock, and only the dead page's writer lease stopped the reset. Holding the lock now
					// does not undo that proof; the writer fence below still decides when the reset lands.
					// Below the heartbeat floor the row falls through to the hold-back below and the proof
					// is kept for the next pass.
					reclaim(session, "same-owner-prior-load-proven-earlier");
				} else {
					try {
						adminUploadTelemetry.logInfo("recording.fileUpload.resetPreservedSameOwner", {
							...getFileUploadDiagnosticContext().describe(session, undefined, "recurring-reset"),
							sessionId: session.id,
							condition: "same-owner-fresh-heartbeat",
							ownerLockHeld: true,
							heartbeatAgeMs: session.uploadOwnerHeartbeatAt ? now - session.uploadOwnerHeartbeatAt : undefined
						});
					} catch {
						// Diagnostics must never abort recovery of other orphan candidates.
					}
					onOrphanAwaitingHeartbeat?.(session);
				}
				continue;
			}
			// Neither an unadmitted recurring row nor a startup same-owner row is known to be live in
			// this page yet (this page may still be mid-acquiring its own liveness lock). When Web
			// Locks are available (below), only one instance can ever hold currentOwnerId's lock, so a
			// free lock is definitive proof — no grace window needed. When Web Locks are unavailable
			// (this branch), there is no such liveness signal at all, so this falls back to the same
			// heartbeat-staleness grace period used for foreign owners.
			if (!getUploadOwnerLockManager()) {
				if (!session.uploadOwnerHeartbeatAt || now - session.uploadOwnerHeartbeatAt > FILE_UPLOAD_SESSION_OWNER_STALE_MS) {
					reclaim(session, "same-owner-stale-heartbeat-no-web-locks");
				} else {
					// LSP-19766: the only same-owner liveness signal on a browser without Web Locks (Safari 15,
					// iOS Safari 15.0-15.1 — see browser-support-floor-baseline.json) is heartbeat staleness, so
					// every same-owner fresh-heartbeat hold-back on these browsers routes through here rather
					// than the acquiredUploadOwnerLivenessLocks branch above, which never populates without a
					// lock manager (ensureUploadOwnerLivenessLockHeldForOwner returns early). Must still notify
					// the caller so the tray can show a verifying-orphan row instead of silently dropping it.
					try {
						adminUploadTelemetry.logInfo("recording.fileUpload.resetPreservedSameOwner", {
							...getFileUploadDiagnosticContext().describe(session, undefined, "recurring-reset"),
							sessionId: session.id,
							condition: "same-owner-fresh-heartbeat-no-lock-manager",
							ownerLockHeld: false,
							heartbeatAgeMs: session.uploadOwnerHeartbeatAt ? now - session.uploadOwnerHeartbeatAt : undefined
						});
					} catch {
						// Diagnostics must never abort recovery of other orphan candidates.
					}
					onOrphanAwaitingHeartbeat?.(session);
				}
				continue;
			}
			const ownerLockFree = await isUploadOwnerLockFree(currentOwnerId);
			if (ownerLockFree === false) {
				if (!(await isUploadRowLaneProvenDead(session, now))) {
					liveOwnerLockHeldCount++;
					continue;
				}
				reclaim(session, "same-owner-lane-proven-dead");
				continue;
			}
			if (ownerLockFree === true) {
				reclaim(session, "same-owner-lock-free");
				continue;
			}
			// ownerLockFree === null: the Web Lock probe itself was indeterminate (already logged at
			// its source, isUploadOwnerLockFree's catch). Fall back to the same heartbeat-staleness
			// rule the foreign-owner branch uses below, instead of silently leaving an indeterminate
			// same-owner row stuck forever.
			if (session.uploadOwnerHeartbeatAt && now - session.uploadOwnerHeartbeatAt > FILE_UPLOAD_SESSION_OWNER_STALE_MS) {
				reclaim(session, "same-owner-probe-indeterminate-stale-heartbeat");
			} else {
				try {
					console.info(
						`[Upload] reset left session ${session.id} alone — same-owner Web Lock probe for ${currentOwnerId} was indeterminate and heartbeat is not yet stale`
					);
				} catch {
					// Diagnostics must never abort recovery of other orphan candidates.
				}
				onOrphanAwaitingHeartbeat?.(session);
			}
			continue;
		}
		if (!session.uploadOwnerHeartbeatAt) {
			const lockManager = getUploadOwnerLockManager();
			if (!lockManager) {
				reclaim(session, "foreign-no-heartbeat-no-web-locks"); // owned but never stamped a heartbeat, with no Web Locks fallback available
				continue;
			}
			const ownerLockFree = await isUploadOwnerLockFree(session.uploadOwnerId);
			if (ownerLockFree === false) {
				liveOwnerLockHeldCount++;
				continue;
			}
			if (ownerLockFree === true) reclaim(session, "foreign-no-heartbeat-lock-free");
			continue;
		}
		if (!getUploadOwnerLockManager()) {
			// No Web Locks in this browser: fall back to the original heartbeat-stale rule so a
			// foreign orphan still recovers (just on the slower 5-min threshold, as before).
			if (now - session.uploadOwnerHeartbeatAt > FILE_UPLOAD_SESSION_OWNER_STALE_MS) {
				reclaim(session, "foreign-stale-heartbeat-no-web-locks");
			}
			continue;
		}
		const ownerLockFree = await isUploadOwnerLockFree(session.uploadOwnerId);
		if (ownerLockFree === false) {
			if (!(await isUploadRowLaneProvenDead(session, now))) {
				liveOwnerLockHeldCount++;
				continue;
			}
			reclaim(session, "foreign-lane-proven-dead");
			continue;
		}
		if (ownerLockFree === true && now - session.uploadOwnerHeartbeatAt > FILE_UPLOAD_OWNER_LOCK_GRACE_MS) {
			reclaim(session, "foreign-lock-free-past-grace");
			continue;
		}
		if (ownerLockFree === null && now - session.uploadOwnerHeartbeatAt > FILE_UPLOAD_SESSION_OWNER_STALE_MS) {
			reclaim(session, "foreign-probe-indeterminate-stale-heartbeat");
		}
	}
	if (liveOwnerLockHeldCount > 0) {
		console.info(`[Upload] left ${liveOwnerLockHeldCount} in-flight session(s) alone because the owner's lock is still held`);
	}
	if (terminallyParkedCount > 0) {
		// Silent-failure rule: this pass declining to reclaim a row is the whole point of LSP-19384,
		// so it has to be legible in Datadog. Without it, "my upload never retried" and "the reset
		// never ran" produce identical log output.
		console.info(`[Upload] left ${terminallyParkedCount} session(s) parked because automatic retry has terminated for them`);
	}
	if (resettable.length === 0) return;

	// The row snapshot CAS above decides which candidates are eligible, but it does not fence a
	// durable writer that is acquired after the scan. Commit each reset under Admin's app-local
	// writer owner/epoch fence so a lost handoff leaves both the row and its chunk bytes untouched.
	const { commitAdminUploadWriterFence } = await import("@/services/uploads/adminUploadLease");
	let resetCount = 0;
	for (const observedSession of resettable) {
		let removedChunkCount: number | undefined;
		const resetOne = async (): Promise<
			"reset-to-pending" | "unprepared-placeholder-removed" | "unprepared-placeholder-kept-for-heartbeat" | undefined
		> => {
			const persistedSession = await readFileUploadSession(observedSession.id);
			// This CAS asks one question: has this row's LEASE changed since it was observed? For a row
			// whose owner is corrupt there is no lease to compare — and its owner, claim and heartbeat
			// cells can each hold a non-primitive that IndexedDB re-clones on every read, so `===` on
			// them is never true and the row would fail this CAS forever: the sweep skips it, claim()
			// refuses it, and the drain cannot cancel it. For those rows the CAS therefore checks that it
			// is still the same row and still not a valid lease, which is the only outcome that must not
			// be clobbered — a valid claim landing in the window makes this false and the reset is skipped.
			//
			// A row with a real owner keeps the strict identity comparison: fileUploadDb.test.ts pins that
			// an unadmitted SAME-owner row with a non-finite heartbeat is preserved rather than treated as
			// proven dead, and relaxing it there would let the sweep steal a possibly-live upload.
			const observedOwnerIsCorrupt =
				observedSession.uploadOwnerId !== undefined && !isValidFileUploadOwnerId(observedSession.uploadOwnerId);
			const leaseIsUnchanged =
				persistedSession?.uploadId === observedSession.uploadId &&
				persistedSession.status === observedSession.status &&
				// A corrupt row's owner, claim and heartbeat cells can each hold a non-primitive, so every one
				// of them goes through a comparison that survives IndexedDB's per-read clone. A valid owner
				// or a moved string claim still compares unequal, so a concurrent writer is still detected.
				(observedOwnerIsCorrupt
					? toComparableUploadOwnerCell(persistedSession.uploadOwnerId) ===
						toComparableUploadOwnerCell(observedSession.uploadOwnerId)
					: persistedSession.uploadOwnerId === observedSession.uploadOwnerId) &&
				(observedOwnerIsCorrupt
					? getUsableFileUploadOwnerHeartbeatAt(persistedSession.uploadOwnerHeartbeatAt, now) ===
						getUsableFileUploadOwnerHeartbeatAt(observedSession.uploadOwnerHeartbeatAt, now)
					: persistedSession.uploadOwnerHeartbeatAt === observedSession.uploadOwnerHeartbeatAt) &&
				// The claim cell is normalized UNCONDITIONALLY, unlike the owner and heartbeat cells above.
				// A corrupt claim id is reachable INDEPENDENTLY of a corrupt owner (ls-heroku-ci review):
				// an ownerless row whose claim cell holds a non-primitive took the `observedOwnerIsCorrupt
				// === false` path, compared two separate structured clones with `===`, and could never pass
				// this CAS — stranding the row in every sweep while claim() refused it too. Normalizing here
				// is a no-op for every well-formed row (toComparableUploadOwnerCell is identity for
				// `undefined` and for every string), and the refusal diagnostic below already normalized this
				// same cell, so leaving it strict made a refusal report `changed: []`. The owner leg stays
				// strict and remains the real guard: a successor lease writes a VALID owner id beside its
				// claim, so it still compares unequal and is still refused.
				toComparableUploadOwnerCell(persistedSession.uploadOwnerClaimId) ===
					toComparableUploadOwnerCell(observedSession.uploadOwnerClaimId);
			if (!leaseIsUnchanged) {
				// ls-heroku-ci review: naming WHICH of the five CAS fields moved is what makes a recurring
				// refusal (the strand this review found) visible in logs instead of invisible.
				warnUploadCasMismatch("[Upload] stale orphan reset ignored — persisted lease changed", {
					sessionId: observedSession.id,
					changed: [
						persistedSession?.uploadId !== observedSession.uploadId ? "uploadId" : undefined,
						persistedSession?.status !== observedSession.status ? "status" : undefined,
						toComparableUploadOwnerCell(persistedSession?.uploadOwnerId) !==
						toComparableUploadOwnerCell(observedSession.uploadOwnerId)
							? "uploadOwnerId"
							: undefined,
						persistedSession?.uploadOwnerHeartbeatAt !== observedSession.uploadOwnerHeartbeatAt
							? "uploadOwnerHeartbeatAt"
							: undefined,
						toComparableUploadOwnerCell(persistedSession?.uploadOwnerClaimId) !==
						toComparableUploadOwnerCell(observedSession.uploadOwnerClaimId)
							? "uploadOwnerClaimId"
							: undefined
					].filter(Boolean),
					observedStatus: observedSession.status,
					actualStatus: persistedSession?.status ?? "<row gone>",
					observedOwnerIdLength: getFileUploadOwnerIdLength(observedSession.uploadOwnerId),
					actualOwnerIdLength: getFileUploadOwnerIdLength(persistedSession?.uploadOwnerId)
				});
				return undefined;
			}

			// LSP-22008: the liveness checks above say this row's writer is gone, and a placeholder whose copy
			// never finished can only be refilled by that writer's File. Reviving it as `pending` handed the
			// drain a row it could only loop on against the API (LSP-21977). Remove it — with its partial
			// bytes, under this same fence and re-read — which is where a cleaned-up row already ends. Not a
			// `cancelled` tombstone: the dialog retires a file's re-add notice when it sees one, and the user
			// may not have been shown that notice yet. Nothing remote exists to abort (no target was requested).
			// That includes a row whose last chunk landed but whose `sourcePrepared` did not (reload between the
			// two writes): only finishPrePersist sets the flag, so no path can request its target, and its notice
			// was never marked persisted, so the post-refresh cleanup cancels it and asks for a re-add as well.
			if (isUnpreparedPlaceholderSession(persistedSession)) {
				// LSP-22113 (ls-ai-review): the remembered proof cannot tell a dead page from a live duplicated
				// tab whose writer lease lapsed without a renewal, and removal deletes bytes only that writer's
				// File can refill. On the proof alone, leave it for the 300 s heartbeat rule, which still
				// removes a dead page's placeholder — later.
				if (reclaimReasons.get(observedSession.id) === "same-owner-prior-load-proven-earlier") {
					console.info("[Upload] unprepared placeholder kept — only the remembered orphan proof says its page is dead", {
						sessionId: persistedSession.id,
						heartbeatAgeMs: persistedSession.uploadOwnerHeartbeatAt ? now - persistedSession.uploadOwnerHeartbeatAt : undefined
					});
					return "unprepared-placeholder-kept-for-heartbeat";
				}
				removedChunkCount = await db.fileUploadChunkData.where({ sessionId: persistedSession.id }).delete();
				await db.fileUploadChunks.where({ sessionId: persistedSession.id }).delete();
				await deleteFileUploadSessionControl(persistedSession.id);
				return "unprepared-placeholder-removed";
			}

			// ls-heroku-ci review: this raw put bypassed resolveNextLastError, so a stale lastError
			// survived the reset to "pending" instead of being cleared like every other
			// re-activation path (markFileUploadSessionPendingForResume, updateFileUploadSessionStatus).
			await putFileUploadSession({ ...clearUploadOwnerFields(persistedSession), status: "pending", lastError: undefined });
			return "reset-to-pending";
		};
		const fenced = await commitAdminUploadWriterFence(observedSession.id, resetOne, () =>
			withIdbReopen(() =>
				db.transaction("rw", [...fileUploadSessionControlTables, db.fileUploadChunks, db.fileUploadChunkData], resetOne)
			)
		);
		const outcome = (fenced.kind === "applied" && fenced.value) || "preserved";
		if (outcome === "reset-to-pending") resetCount++;
		await rememberPriorLoadOrphanProof(observedSession, reclaimReasons.get(observedSession.id), fenced);
		if (
			(fenced.kind === "skipped-not-owned" && provenPriorLoadOrphans.has(observedSession.id)) ||
			outcome === "unprepared-placeholder-kept-for-heartbeat"
		) {
			// Still waiting on the dead page's writer lease, or now on the heartbeat bar: keep the tray's
			// "resuming shortly" row, as the heartbeat hold-back branch does.
			onOrphanAwaitingHeartbeat?.(observedSession);
		}
		adminUploadTelemetry.logInfo("recording.fileUpload.orphanResetObserved", {
			...getFileUploadDiagnosticContext().describe(observedSession, undefined, "orphan-reset"),
			sessionId: observedSession.id,
			conversationId: observedSession.conversationId,
			outcome,
			reason: reclaimReasons.get(observedSession.id),
			...(fenced.kind === "skipped-not-owned" ? { fenceRefusal: fenced.refusal } : {}),
			totalChunks: observedSession.totalChunks,
			...(outcome === "unprepared-placeholder-removed" ? { removedChunkCount } : {}),
			heartbeatAgeMs: observedSession.uploadOwnerHeartbeatAt ? now - observedSession.uploadOwnerHeartbeatAt : undefined
		});
	}
	if (resetCount > 0) console.info(`[Upload] Resetting ${resetCount} orphaned session(s) to "pending"`);
}

/**
 * Get metadata for all unuploaded chunks (no buffer loaded).
 */
export async function getUnuploadedChunksMeta(sessionId: string): Promise<FileUploadChunkMeta[]> {
	// LSP-16528: resume reads chunk metadata to compute progress; reopen-and-retry on a dropped connection.
	return withIdbReopen(() => db.fileUploadChunks.where({ sessionId, isUploaded: 0 }).toArray());
}

/**
 * Get metadata for all chunks in a session (no buffer loaded).
 */
export async function getAllChunksMeta(sessionId: string): Promise<FileUploadChunkMeta[]> {
	return withIdbReopen(() => db.fileUploadChunks.where({ sessionId }).toArray());
}

/**
 * Read a single chunk's buffer from IDB. Returns null if the record doesn't exist.
 */
export async function getChunkBuffer(sessionId: string, chunkIndex: number): Promise<ArrayBuffer | null> {
	const record = await withIdbReopen(() => db.fileUploadChunkData.get([sessionId, chunkIndex]));
	return record?.buffer ?? null;
}

/**
 * Check whether a specific chunk's buffer data still exists in IDB.
 */
export async function hasChunkBuffer(sessionId: string, chunkIndex: number): Promise<boolean> {
	const count = await withIdbReopen(() => db.fileUploadChunkData.where("[sessionId+chunkIndex]").equals([sessionId, chunkIndex]).count());
	return count > 0;
}

export async function deleteFileUploadSession(
	sessionId: string,
	expectedUploadId?: string,
	expectedOwnerId?: string,
	expectedOwnerClaimId?: string,
	options?: {
		requireExactOwnerIdentity?: boolean;
		// Set by callers that delete WITHOUT an expected owner because they observed the row as
		// ownerless or corrupt-owned outside this transaction. Their read is not part of the
		// transaction, so a competing tab can commit a real lease in that window; this re-reads
		// ownership inside the transaction and refuses when it finds one.
		refuseValidOwnerLease?: boolean;
		expectedStatus?: FileUploadSessionStatus;
		notifyOtherTabs?: boolean;
		/** Deletes only the exact current cancellation writer, in the same transaction as the terminal row cleanup. */
		expectedWriterLease?: DurableLeaseToken;
		/** Explicit user cancellation also discards its pending capture-evidence sidecar. */
		clearCaptureEvidence?: boolean;
	}
): Promise<boolean> {
	// break-it: admin surface — every other IDB transaction in this file is wrapped in withIdbReopen
	// (stall-timeout + reopen-and-retry once). This delete was the one outlier calling db.transaction
	// directly: a genuinely wedged connection (e.g. a second stall right after an earlier read already
	// stalled and triggered this exact cleanup, as tryDeleteCorruptedSession does) would hang this
	// promise forever with no timeout to race against — tryDeleteCorruptedSession's try/catch only
	// catches a rejection, never an indefinitely-pending promise, so its caller would hang too.
	const deleted = await withIdbReopen(() =>
		db.transaction(
			"rw",
			[
				...fileUploadSessionControlTables,
				db.fileUploadChunks,
				db.fileUploadChunkData,
				db.table("leases"),
				db.captureEvidenceManifests
			],
			async () => {
				const session = await readFileUploadSession(sessionId);
				if (!session) return false;
				if (expectedUploadId !== undefined && session?.uploadId !== expectedUploadId) {
					warnUploadCasMismatch("[Upload] Cannot delete file upload session: uploadId mismatch", {
						sessionId,
						expectedUploadId,
						actualUploadId: session?.uploadId
					});
					return false;
				}
				if (options?.refuseValidOwnerLease && isValidFileUploadOwnerId(session?.uploadOwnerId)) {
					warnUploadCasMismatch("[Upload] Cannot delete file upload session: row gained a valid owner lease", {
						sessionId,
						expectedUploadId,
						actualOwnerClaimId: session?.uploadOwnerClaimId
					});
					return false;
				}
				if (options?.expectedStatus !== undefined && session?.status !== options.expectedStatus) {
					warnUploadCasMismatch("[Upload] Cannot delete file upload session: status mismatch", {
						sessionId,
						expectedStatus: options.expectedStatus,
						actualStatus: session?.status
					});
					return false;
				}
				// extended architecture campaign: successful completion cleanup has its own status-gated function.
				if (expectedUploadId !== undefined && session?.status === "complete") {
					warnUploadCasMismatch("[Upload] Cannot delete file upload session: session already complete", {
						sessionId,
						expectedUploadId
					});
					return false;
				}
				// LSP-17798 architecture-gap review: this generic uploadId-only delete had no needs-attention
				// guard, unlike its sibling deleteChunkDataForSession below (which explicitly protects every
				// OTHER terminal status and only allows needs-attention's bytes, not the row, to be cleared).
				// discardLostUploadSession funnels here on a confirmed UPLOAD_LOST classification with no
				// status check of its own — a session already marked needs-attention (e.g. by
				// markEntrySessionNeedsAttention) that later hits that path would silently lose both the
				// "please re-add this file" signal and its diagnostic evidence.
				if (expectedUploadId !== undefined && (session?.status === "needs-attention" || session?.status === "recovery-blocked")) {
					warnUploadCasMismatch("[Upload] Cannot delete retained file upload session", {
						sessionId,
						expectedUploadId
					});
					return false;
				}
				if (
					!hasValidExpectedFileUploadOwnerId(expectedOwnerId) ||
					((options?.requireExactOwnerIdentity || expectedOwnerId !== undefined) && session?.uploadOwnerId !== expectedOwnerId) ||
					((options?.requireExactOwnerIdentity || expectedOwnerClaimId !== undefined) &&
						session?.uploadOwnerClaimId !== expectedOwnerClaimId)
				) {
					warnUploadCasMismatch(
						"[Upload] Cannot delete file upload session: owner identity mismatch",
						getSafeUploadOwnerMismatchContext(
							{
								sessionId,
								expectedOwnerClaimId,
								actualOwnerClaimId: session?.uploadOwnerClaimId
							},
							expectedOwnerId,
							session?.uploadOwnerId
						)
					);
					return false;
				}
				const expectedWriterLease = options?.expectedWriterLease;
				const expectedWriterIdentity = expectedWriterLease?.kind === "writer" ? expectedWriterLease.identity : undefined;
				const writerLeaseKeyForSession = expectedWriterIdentity
					? writerLeaseKey(expectedWriterLease!.originKey, expectedWriterIdentity)
					: undefined;
				if (expectedWriterLease) {
					const durableLease = writerLeaseKeyForSession
						? ((await db.table("leases").get(writerLeaseKeyForSession)) as DurableLeaseRecord | undefined)
						: undefined;
					const exactWriterLease =
						writerLeaseKeyForSession !== undefined &&
						expectedWriterLease.originKey === "contact-admin" &&
						expectedWriterIdentity?.itemId === sessionId &&
						expectedWriterIdentity.takeId === undefined &&
						durableLease?.key === writerLeaseKeyForSession &&
						durableLease.kind === expectedWriterLease.kind &&
						durableLease.originKey === expectedWriterLease.originKey &&
						durableLease.itemId === expectedWriterIdentity?.itemId &&
						durableLease.takeId === expectedWriterIdentity?.takeId &&
						durableLease.ownerId === expectedWriterLease.ownerId &&
						durableLease.epoch === expectedWriterLease.epoch &&
						durableLease.acquiredAt === expectedWriterLease.acquiredAt &&
						durableLease.expiresAt === expectedWriterLease.expiresAt;
					if (!exactWriterLease) {
						warnUploadCasMismatch("[Upload] Cannot delete file upload session: durable writer identity mismatch", {
							sessionId,
							expectedOwnerId: expectedWriterLease.ownerId,
							expectedEpoch: expectedWriterLease.epoch,
							actualOwnerId: durableLease?.ownerId,
							actualEpoch: durableLease?.epoch
						});
						return false;
					}
				}
				await db.fileUploadChunkData.where({ sessionId }).delete();
				await db.fileUploadChunks.where({ sessionId }).delete();
				await deleteFileUploadSessionControl(sessionId);
				// LSP-18592: a session persisted with capture evidence off carries no sidecar, so it issues no
				// manifest query; rows written while the flag was on are still retired with their session.
				if (session.captureEvidence && (options?.clearCaptureEvidence || options?.expectedStatus === "cancelled")) {
					await db.captureEvidenceManifests.where("sourceClientId").equals(sessionId).delete();
				}
				if (writerLeaseKeyForSession) await db.table("leases").delete(writerLeaseKeyForSession);
				return true;
			}
		),
		{ operationName: "delete-session" }
	);
	if (deleted || (await getFileUploadSessionById(sessionId)) === undefined) {
		forgetCurrentPageInitiatedFileUploadSessions(sessionId);
	}
	if (deleted && options?.notifyOtherTabs !== false) publishFileUploadSessionInvalidation(sessionId, expectedUploadId);
	return deleted;
}

/** Only terminal history expires; unfinished source bytes never age out here. */
export async function pruneCompletedUploadBatchHistory(now = Date.now()): Promise<void> {
	await withIdbReopen(() =>
		db.transaction("rw", [...fileUploadSessionControlTables, db.fileUploadChunks, db.fileUploadChunkData], async () => {
			for (const session of await listFileUploadSessions()) {
				if (
					!session.retainBatchHistory ||
					session.status !== "complete" ||
					!session.completedAt ||
					now - session.completedAt < 7 * 24 * 60 * 60 * 1000
				)
					continue;
				await db.fileUploadChunkData.where({ sessionId: session.id }).delete();
				await db.fileUploadChunks.where({ sessionId: session.id }).delete();
				await deleteFileUploadSessionControl(session.id);
			}
		})
	);
}

export async function cleanupCompletedFileUploadSession(
	sessionId: string,
	expectedUploadId: string,
	expectedOwnerId?: string,
	expectedOwnerClaimId?: string
): Promise<boolean> {
	const deleted = await withIdbReopen(() =>
		db.transaction("rw", [...fileUploadSessionControlTables, db.fileUploadChunks, db.fileUploadChunkData], async () => {
			const session = await readFileUploadSession(sessionId);
			if (
				!hasValidExpectedFileUploadOwnerId(expectedOwnerId) ||
				!session ||
				session.uploadId !== expectedUploadId ||
				hasInvalidUploadOwnerMismatch(expectedOwnerId, session?.uploadOwnerId) ||
				session.uploadOwnerId !== expectedOwnerId ||
				session.uploadOwnerClaimId !== expectedOwnerClaimId ||
				session.status !== "complete"
			) {
				if (hasInvalidUploadOwnerMismatch(expectedOwnerId, session?.uploadOwnerId)) {
					warnUploadCasMismatch(
						"[Upload] stale completion cleanup ignored",
						getSafeUploadOwnerMismatchContext(
							{
								sessionId,
								expectedUploadId,
								actualUploadId: session?.uploadId,
								expectedOwnerClaimId,
								actualOwnerClaimId: session?.uploadOwnerClaimId,
								currentStatus: session?.status
							},
							expectedOwnerId,
							session?.uploadOwnerId
						)
					);
				} else {
					warnUploadCasMismatch(
						`[Upload] stale completion cleanup ignored for session ${sessionId}: ` +
							`expected owner ${expectedOwnerId ?? "<none>"}, actual owner ${session?.uploadOwnerId ?? "<missing>"}; ` +
							`expected complete session, actual status ${session?.status ?? "<missing>"}; persisted upload identity changed`,
						{ sessionId, expectedUploadId, expectedOwnerId, expectedOwnerClaimId }
					);
				}
				return false;
			}

			await db.fileUploadChunkData.where({ sessionId }).delete();
			await db.fileUploadChunks.where({ sessionId }).delete();
			if (!session.retainBatchHistory) await deleteFileUploadSessionControl(sessionId);
			return true;
		})
	);
	if (deleted || (await getFileUploadSessionById(sessionId)) === undefined) {
		forgetCurrentPageInitiatedFileUploadSessions(sessionId);
	}
	return deleted;
}

export async function deleteChunkDataForSession(
	sessionId: string,
	expectedSessionIdentity?: Pick<FileUploadSession, "uploadId" | "uploadOwnerId" | "uploadOwnerClaimId">
): Promise<void> {
	// break-it: admin surface — same stall-boundary gap as deleteFileUploadSession above (see that
	// function's comment); this was the other transaction call in this file with no withIdbReopen.
	await withIdbReopen(() =>
		db.transaction("rw", [...fileUploadSessionControlTables, db.fileUploadChunks, db.fileUploadChunkData], async () => {
			if (expectedSessionIdentity) {
				const session = await readFileUploadSession(sessionId);
				if (
					!hasValidExpectedFileUploadOwnerId(expectedSessionIdentity.uploadOwnerId) ||
					!session ||
					session.uploadId !== expectedSessionIdentity.uploadId ||
					hasInvalidUploadOwnerMismatch(expectedSessionIdentity.uploadOwnerId, session?.uploadOwnerId) ||
					session.uploadOwnerId !== expectedSessionIdentity.uploadOwnerId ||
					session.uploadOwnerClaimId !== expectedSessionIdentity.uploadOwnerClaimId ||
					// needs-attention is awaiting resolution, so an identity-matched user cancellation
					// may delete its bytes; other terminal evidence remains protected.
					(isTerminalFileUploadSessionStatus(session.status) &&
						session.status !== "needs-attention" &&
						session.status !== "recovery-blocked")
				) {
					warnUploadCasMismatch(
						"[Upload] Cannot delete chunk data: owner identity mismatch",
						getSafeUploadOwnerMismatchContext(
							{
								sessionId,
								expectedUploadId: expectedSessionIdentity.uploadId,
								actualUploadId: session?.uploadId,
								expectedOwnerClaimId: expectedSessionIdentity.uploadOwnerClaimId,
								actualOwnerClaimId: session?.uploadOwnerClaimId
							},
							expectedSessionIdentity.uploadOwnerId,
							session?.uploadOwnerId
						)
					);
					return;
				}
			}
			await db.fileUploadChunkData.where({ sessionId }).delete();
			await db.fileUploadChunks.where({ sessionId }).delete();
		})
	);
}

/**
 * LSP-19417: the legacy cancelled-upload drain's own TTL for the per-row cancellation writer it
 * holds across `abortMultipartUpload` + the terminal delete. Matches
 * adminUploadCancellation.ts's ADMIN_CANCEL_WRITER_TTL_MS: the window this lane needs to cover is
 * the same single S3 abort round-trip plus one IndexedDB transaction, so the two cancellation
 * lanes should not disagree on how long a stalled holder blocks its successor.
 */
const LEGACY_CANCELLED_DRAIN_WRITER_TTL_MS = 2 * 60 * 1000;

/**
 * LSP-19417: in-flight coalescing for [drainCancelledUploadSessions].
 *
 * "At most one pass at a time" is a property of the LANE, not of any one call site, so it is
 * enforced in the callee. Two overlapping passes would each list the same cancelled rows, each
 * issue `abortMultipartUpload` for the same MPU, and the loser's 404 branch would then delete a row
 * the winner is still working on. A second concurrent caller therefore JOINS the running pass
 * (awaits the same promise) rather than starting a competing one — so the next caller cannot
 * rediscover the race the way LSP-19384's bulk-cancellation caller did.
 */
let inFlightCancelledUploadDrain: Promise<void> | undefined;

/**
 * The three outcomes of asking for one legacy cancelled row's terminal-delete fence. This is the
 * answer to the design question LSP-19417 names — how the fence can be optional WITHOUT silently
 * degrading to "unfenced" — so the two "no token" causes are deliberately NOT collapsed:
 *
 * - `fenced`     — the row's cancellation writer is held; both deletes carry it.
 * - `contended`  — the fence mechanism exists and someone else holds this row's writer. DEFER the
 *                  row. Deleting here is the actual defect: the loser of two overlapping passes
 *                  would delete a row the winner is still working on.
 * - `unfenceable` — there is no fence mechanism at all. Today that is only the legacy unit harness
 *                  (`getDurableOwnership` returns undefined for `MODE === "test"` unless a test
 *                  opts in), and it is exactly the state the guarded sibling
 *                  `cancelAdminUploadSession` also runs in — it passes `expectedWriterLease:
 *                  writer?.token`, i.e. no fence, when it has no writer. Proceeding here therefore
 *                  keeps parity with the strongest sibling instead of inventing a stricter contract
 *                  for this lane alone, and it is announced in a log line rather than assumed.
 */
type LegacyCancelledDrainFence =
	| { kind: "fenced"; token: DurableLeaseToken; release: () => Promise<void> }
	| { kind: "contended" }
	| { kind: "unfenceable"; reason: string };

async function acquireLegacyCancelledDrainWriter(sessionId: string): Promise<LegacyCancelledDrainFence> {
	let ownership;
	try {
		const { getAdminDurableOwnershipForCoordinator } = await import("@/services/uploads/adminUploadLease");
		ownership = await getAdminDurableOwnershipForCoordinator();
	} catch (error) {
		// Ownership initialization itself failed. Mirrors adminUploadCancellation.ts's own catch, which
		// treats a failed coordinator init as "no writer" rather than as a drain failure.
		return { kind: "unfenceable", reason: `cancellation writer ownership initialization failed: ${String(error)}` };
	}
	if (!ownership) return { kind: "unfenceable", reason: "no durable ownership coordinator is available" };
	let token: DurableLeaseToken | undefined;
	try {
		token = await ownership.acquireWriter({
			originKey: "contact-admin",
			identity: { itemId: sessionId },
			ownerId: `admin-legacy-cancel-drain:${newUploadId()}`,
			ttlMs: LEGACY_CANCELLED_DRAIN_WRITER_TTL_MS
		});
	} catch (error) {
		// The coordinator EXISTS and the acquire threw, so we cannot conclude the row is free. Treat it
		// as contended — the conservative side, and the one that never deletes on an unknown holder.
		console.warn(
			`[Upload] Cancellation writer acquire failed for legacy cancelled session ${sessionId} — deferring the row to the next load rather than deleting it unfenced`,
			error
		);
		return { kind: "contended" };
	}
	if (!token) return { kind: "contended" };
	return {
		kind: "fenced",
		token,
		release: async () => {
			try {
				await ownership.releaseWriter(token!);
			} catch (releaseError) {
				// A failed release only means the successor waits out the TTL instead of taking the row
				// immediately — never a reason to fail the drain whose terminal work already landed.
				console.warn(
					`[Upload] Failed to release the legacy cancelled-drain writer for session ${sessionId}; the next pass may wait for lease expiry`,
					releaseError
				);
			}
		}
	};
}

/**
 * Retry S3 abort for any sessions left in "cancelled" state from a previous page load.
 * Called once at startup so that uploads cancelled while offline are cleaned up on next visit.
 *
 * LSP-19417: coalesced (one pass at a time) and writer-fenced (both deletes carry the row's
 * cancellation writer lease, matching the strongest sibling in this subsystem), so a second
 * concurrent caller can no longer race this one into a double abort and a cross-delete.
 *
 * Note what joining does NOT promise. A joiner resolves when the RUNNING pass finishes, and that
 * pass listed its rows before the joiner arrived, so rows cancelled after that listing are left to
 * the next pass. The only caller today is the once-per-page-load startup reclaim in
 * adminUploadDrain.ts, for which that is exactly right. A future mid-session caller that must see
 * its OWN just-cancelled row has to chain a fresh pass after this one rather than simply await it.
 */
export async function drainCancelledUploadSessions(): Promise<void> {
	// Join the running pass instead of racing it. Awaiting the SAME promise is what makes this a
	// coalesce rather than a queue: a second caller wants "the cancelled rows are drained", which the
	// in-flight pass is already delivering, not its own second pass over the same rows.
	if (inFlightCancelledUploadDrain) {
		console.info("[Upload] Cancelled-session drain already in flight — joining the running pass");
		return inFlightCancelledUploadDrain;
	}
	const pass = runCancelledUploadSessionDrain().finally(() => {
		inFlightCancelledUploadDrain = undefined;
	});
	inFlightCancelledUploadDrain = pass;
	return pass;
}

async function runCancelledUploadSessionDrain(): Promise<void> {
	const cancelled = (await listFileUploadSessionsByStatus("cancelled")).filter(
		(session) => session.endpointFamily !== "uploads" && session.registryId === undefined
	);
	if (cancelled.length === 0) return;
	console.info(`[Upload] Draining ${cancelled.length} cancelled session(s)`);
	for (const session of cancelled) {
		// Held across BOTH the remote abort and the terminal delete. Acquiring it before the abort is
		// load-bearing rather than tidy: the overlapping-pass failure this closes is two callers each
		// aborting the same MPU, which happens before either one reaches its delete.
		const fence = await acquireLegacyCancelledDrainWriter(session.id);
		if (fence.kind === "contended") {
			console.warn(
				`[Upload] Skipping legacy cancelled session ${session.id} — its cancellation writer is held by another cancellation; will retry next load`
			);
			continue;
		}
		if (fence.kind === "unfenceable") {
			// Named, not assumed. The whole point of LSP-19417 is that an unfenced destructive delete
			// must never happen by omission; if it happens at all, the reason is in the log.
			console.warn(
				`[Upload] Draining legacy cancelled session ${session.id} WITHOUT a writer fence — ${fence.reason} (same fallback as cancelAdminUploadSession)`
			);
		}
		const writerToken = fence.kind === "fenced" ? fence.token : undefined;
		try {
			try {
				// These sessions were explicitly cancelled by the user on a prior load; tombstone the
				// stub (cancelled:true) so the server-side recovery sweep never resurrects it.
				await api.abortMultipartUpload(session.uploadId, session.s3Key, session.bucket, true);
			} catch (error) {
				// 404 means S3 no longer knows about the upload (already aborted, completed, or expired by S3's
				// own multipart GC). The session row has nothing left to clean up — drop it so we stop retrying.
				const statusCode =
					(error as { code?: number; status?: number } | null)?.code ?? (error as { status?: number } | null)?.status;
				if (statusCode === 404) {
					console.info(
						`[Upload] Deferred abort for session ${session.id} returned 404 — upload no longer exists on S3, removing session`
					);
					// break-it: admin surface — this deferred cleanup can be in flight for a long time (a real
					// S3 round-trip via `await`); gate the delete on the uploadId this loop actually drained so
					// it can never delete a DIFFERENT session that has since taken over the same row id.
					// LSP-19417: plus the writer fence, so a row whose cancellation writer changed hands during
					// that round-trip is refused instead of deleted out from under its new owner.
					await deleteFileUploadSession(session.id, session.uploadId, undefined, undefined, {
						expectedStatus: "cancelled",
						expectedWriterLease: writerToken
					}).catch((idbError) => {
						console.warn(
							`[Upload] Remote abort returned 404 but local cleanup failed for session ${session.id} (upload ${session.uploadId}) — will retry next load`,
							idbError
						);
					});
					continue;
				}
				console.warn(`[Upload] Deferred abort failed for session ${session.id} — will retry next load`, error);
				continue;
			}
			await deleteFileUploadSession(session.id, session.uploadId, undefined, undefined, {
				expectedStatus: "cancelled",
				expectedWriterLease: writerToken
			}).catch((idbError) => {
				console.warn(
					`[Upload] Deferred abort succeeded for session ${session.id} but local cleanup failed — will retry next load`,
					idbError
				);
			});
			console.info(`[Upload] Deferred abort succeeded for session ${session.id}`);
		} finally {
			if (fence.kind === "fenced") await fence.release();
		}
	}
}

export async function getFileUploadSessionByUploadId(uploadId: string): Promise<FileUploadSession | undefined> {
	// LSP-16528: resume/reconcile looks up a session here; reopen-and-retry on a dropped connection.
	const observedSession = await withIdbReopen(() => readFileUploadSessionByUploadId(uploadId));
	if (observedSession?.status !== "cancelled" || observedSession.uploadCancellationGeneration !== undefined) {
		return observedSession;
	}
	return withIdbReopen(() =>
		db.transaction("rw", fileUploadSessionControlTables, async () => {
			const session = await readFileUploadSessionByUploadId(uploadId);
			// LSP-19963: an old bundle can write a generation-less cancellation after this tab's v7
			// upgrade has already run. Normalize the durable row before returning a fresh snapshot:
			// direct/resume callers can then deliberately replace it, while any older in-memory
			// snapshot remains generation-less and fails the lease's exact-generation fence.
			if (session?.status === "cancelled" && session.uploadCancellationGeneration === undefined) {
				const uploadCancellationGeneration = nextFileUploadCancellationGeneration(session);
				await updateFileUploadSession(session.id, { uploadCancellationGeneration });
				return { ...session, uploadCancellationGeneration };
			}
			return session;
		})
	);
}

export function areUrlsStillValid(session: FileUploadSession): boolean {
	return isPresignedUrlAgeValid(session.urlCreatedAt, Date.now());
}

/**
 * Best-effort cleanup of a session whose IDB data is corrupted.
 * Attempts to delete the session and its chunks; if IDB is totally
 * broken the delete may also fail — that's OK, we log and move on.
 */
export async function tryDeleteCorruptedSession(
	sessionId: string,
	expectedUploadId?: string,
	expectedOwnerId?: string,
	expectedOwnerClaimId?: string,
	options?: { requireExactOwnerIdentity?: boolean; notifyOtherTabs?: boolean }
): Promise<boolean> {
	try {
		return await deleteFileUploadSession(sessionId, expectedUploadId, expectedOwnerId, expectedOwnerClaimId, options);
	} catch (cleanupError) {
		console.warn(`[Upload] Could not delete corrupted session ${sessionId} from IDB — it may need manual cleanup`, cleanupError);
		return false;
	}
}

type AdminDurableWriterLeaseState = "free" | "live" | "indeterminate";

function getAdminDurableWriterLeaseState(record: unknown): AdminDurableWriterLeaseState {
	if (record === undefined) return "free";
	if (!record || typeof record !== "object") return "indeterminate";
	const candidate = record as Partial<DurableLeaseRecord>;
	if (
		candidate.kind !== "writer" ||
		candidate.originKey !== "contact-admin" ||
		typeof candidate.ownerId !== "string" ||
		typeof candidate.epoch !== "number" ||
		!Number.isSafeInteger(candidate.epoch) ||
		candidate.epoch <= 0 ||
		typeof candidate.acquiredAt !== "number" ||
		typeof candidate.expiresAt !== "number" ||
		!Number.isFinite(candidate.acquiredAt) ||
		!Number.isFinite(candidate.expiresAt) ||
		candidate.expiresAt < candidate.acquiredAt
	)
		return "indeterminate";
	return candidate.expiresAt > Date.now() ? "live" : "free";
}

// LSP-19561: lane-intent writer leases (key writer:contact-admin:["lane:<uuid>",null]) are written
// by FileUploadDialog.vue's ensureLaneLeaseAcquired (LSP-19537) once per page load that runs an
// upload batch, and nothing has ever deleted them — deleteFileUploadSession's expectedWriterLease
// path only targets a record keyed by a sessionId, never a lane id. This sweep bounds their growth.
//
// SAFETY_MARGIN: a lease exactly past its own expiresAt may still be in the brief window between
// "TTL lapsed" and "the owning tab's own cleanup/renewal loop next runs" — deleting it there is
// harmless (a foreign tab reading it will already see "free"/"dead" via getFileUploadLaneIntentLiveness's
// own expiresAt check, independent of whether this record still physically exists), but a wider
// margin avoids any possibility of racing a legitimate renewal in flight. 3x the TTL is deliberately
// generous relative to the 60s renew-throttle FileUploadDialog.vue uses against the 5-minute TTL.
const LANE_LEASE_GC_SAFETY_MARGIN_MS = ADMIN_LANE_INTENT_TTL_MS * 3;

export async function sweepStaleLaneLeases(now: number = Date.now()): Promise<{ deletedCount: number }> {
	const cutoff = now - LANE_LEASE_GC_SAFETY_MARGIN_MS;
	return withIdbReopen(() =>
		db.transaction("rw", db.table("leases"), async () => {
			const candidates = await db.table("leases").where("originKey").equals("contact-admin").toArray();
			const staleLaneLeaseKeys = candidates
				.filter(
					(record: DurableLeaseRecord) =>
						typeof record.itemId === "string" && record.itemId.startsWith("lane:") && record.expiresAt < cutoff
				)
				.map((record: DurableLeaseRecord) => record.key);
			const stillStaleLaneLeaseKeys: string[] = [];
			// Defense-in-depth only: Dexie's rw transaction prevents another IndexedDB transaction
			// from renewing a candidate between this sweep's query and delete.
			for (const leaseKey of staleLaneLeaseKeys) {
				const record = (await db.table("leases").get(leaseKey)) as DurableLeaseRecord | undefined;
				if (
					record?.originKey === "contact-admin" &&
					typeof record.itemId === "string" &&
					record.itemId.startsWith("lane:") &&
					record.expiresAt < cutoff
				)
					stillStaleLaneLeaseKeys.push(record.key);
			}
			if (stillStaleLaneLeaseKeys.length === 0) return { deletedCount: 0 };
			await db.table("leases").bulkDelete(stillStaleLaneLeaseKeys);
			return { deletedCount: stillStaleLaneLeaseKeys.length };
		})
	);
}

export type FileUploadLaneIntentLiveness = "none" | "own" | "alive" | "dead";

/**
 * LSP-22113: an `ifAvailable` probe takes a free lock for the length of its callback, so two probes of
 * one lane in flight together refuse each other and the loser reads a dead lane as alive. After a
 * refresh the drain claims many rows of the dead page's lane at once, and QA's run logged three
 * `lane-intent-held` refusals and one success within 25 ms against the same lane, each refusal costing
 * a retry cycle. Overlapping callers share one in-flight probe; a settled answer is never reused.
 */
function probeLaneIntentLockFree(laneIntentId: string): Promise<boolean | null> {
	const inFlight = inFlightLaneIntentLockProbes.get(laneIntentId);
	if (inFlight) return inFlight;
	const probe: Promise<boolean | null> = isUploadOwnerLockFree(laneIntentId).finally(() => {
		if (inFlightLaneIntentLockProbes.get(laneIntentId) === probe) inFlightLaneIntentLockProbes.delete(laneIntentId);
	});
	inFlightLaneIntentLockProbes.set(laneIntentId, probe);
	return probe;
}

/**
 * LSP-19537: is a LIVE lane in some tab still intending to upload this pending row?
 *
 * Every ambiguous verdict below resolves to "dead" (take), never "alive" (skip) — the deliberate
 * inverse of isUploadRowLaneProvenDead's owner-liveness bias. For an OWNER of an uploading row,
 * reclaiming mid-PUT is destructive, so that predicate fails toward "leave it alone". For a LANE
 * INTENT on a pending row the calculus is the opposite: a wrong take costs one refused claim (the
 * durable writer fence in claim() already handles two claimants safely); a wrong skip strands an
 * upload with nothing else to pick it up. See spec §3.
 */
export async function getFileUploadLaneIntentLiveness(session: FileUploadSession, now: number): Promise<FileUploadLaneIntentLiveness> {
	void now;
	if (session.laneIntentId === undefined) return "none";
	if (!isValidFileUploadOwnerId(session.laneIntentId)) {
		warnUploadCasMismatch("[Upload] lane-intent liveness ignored an invalid lane-intent identity", {
			sessionId: session.id,
			laneIntentIdLength: getFileUploadOwnerIdLength(session.laneIntentId)
		});
		return "none";
	}
	if (session.laneIntentId === getCurrentFileUploadLaneId()) return "own";
	try {
		const record = await withIdbReopen(() =>
			db.table("leases").get(writerLeaseKey("contact-admin", { itemId: `lane:${session.laneIntentId}` }))
		);
		const { getAdminDurableOwnershipForCoordinator } = await import("@/services/uploads/adminUploadLease");
		const ownership = await getAdminDurableOwnershipForCoordinator();
		if (ownership === undefined) return "none";
		if (getAdminDurableWriterLeaseState(record) !== "live") return "dead";
		const lockFree = await probeLaneIntentLockFree(session.laneIntentId);
		if (lockFree === null) return "dead"; // the probe errored, so take rather than strand the row.
		if (lockFree === true) return "dead";
		return "alive"; // false means genuinely held, or no lock manager per Design Decision #7.
	} catch (error) {
		console.warn("[Upload] Could not determine lane-intent liveness — taking the row", {
			sessionId: session.id,
			laneIntentId: session.laneIntentId,
			...uploadFailureLogDetails(error)
		});
		return "dead";
	}
}

function isActiveAdminDurableWriterLease(record: unknown): boolean {
	return getAdminDurableWriterLeaseState(record) !== "free";
}

async function getAdminDurableWriterLeaseStateForSession(sessionId: string): Promise<AdminDurableWriterLeaseState> {
	try {
		const record = await withIdbReopen(() => db.table("leases").get(writerLeaseKey("contact-admin", { itemId: sessionId })));
		const { getAdminDurableOwnershipForCoordinator } = await import("@/services/uploads/adminUploadLease");
		const ownership = await getAdminDurableOwnershipForCoordinator();
		if (ownership) {
			const probeToken: DurableLeaseToken = record
				? {
						kind: "writer",
						originKey: "contact-admin",
						identity: { itemId: sessionId },
						ownerId: (record as DurableLeaseRecord).ownerId,
						epoch: (record as DurableLeaseRecord).epoch,
						acquiredAt: (record as DurableLeaseRecord).acquiredAt,
						expiresAt: (record as DurableLeaseRecord).expiresAt
					}
				: {
						kind: "writer",
						originKey: "contact-admin",
						identity: { itemId: sessionId },
						ownerId: "migration-probe",
						epoch: 1,
						acquiredAt: 0,
						expiresAt: Date.now() + 1
					};
			if (await ownership.ownsWriter(probeToken)) return "live";
		}
		return getAdminDurableWriterLeaseState(record);
	} catch (error) {
		console.warn(`[Upload] Could not inspect durable writer lease for migration session ${sessionId}`, error);
		return "indeterminate";
	}
}

/**
 * Narrow bridge seam for WO-3's adopt-on-open migration. The current Admin adapter remains the
 * source of truth until the shared-engine adapter is explicitly enabled; this source only exposes
 * lease-verified, identity-stamped operations to the migrator and never opens a second upload path.
 */
export function createLegacyAdminUploadMigrationSource(): AdminUploadMigrationSource<FileUploadSession, FileUploadMigrationChunk> {
	return {
		listSessions: () => withIdbReopen(() => listFileUploadSessions()),
		getSession: (sessionId) => withIdbReopen(() => readFileUploadSession(sessionId)),
		async getChunks(session) {
			return withIdbReopen(async () => {
				const metadata = await db.fileUploadChunks.where({ sessionId: session.id }).toArray();
				return metadata.map((meta) => ({ meta }));
			});
		},
		async getLeaseState(session) {
			// Bridge admission must not try to adopt an active row that still carries
			// legacy owner metadata. Even when the heartbeat proves that owner stale,
			// markAdopted deliberately rejects the row until the normal fenced reset
			// clears those fields; report it as live here so admission skips it and the
			// drain can reclaim it through resetUploadingSessions instead.
			if (session.uploadOwnerId || session.uploadOwnerClaimId) return "live";
			const durableLeaseState = await getAdminDurableWriterLeaseStateForSession(session.id);
			if (durableLeaseState === "live" || durableLeaseState === "indeterminate") return durableLeaseState;
			const liveness = await getCompetingFileUploadOwnerLiveness(session);
			return liveness === "dead" ? "free" : liveness === "alive" ? "live" : "indeterminate";
		},
		async acquireWriterLease(session): Promise<AdminUploadMigrationWriterLease | undefined> {
			const { getAdminDurableOwnershipForCoordinator } = await import("@/services/uploads/adminUploadLease");
			const ownership = await getAdminDurableOwnershipForCoordinator();
			// TRANSITIONAL — the synthetic lease below is the migration-side twin of the MODE sniff in
			// adminUploadLease.getDurableOwnership: with no typed "durable ownership unavailable" state
			// to branch on, this asks whether it is a test build and then fabricates a lease that fences
			// nothing. It is not intended design and not a safe production fallback; it is not removable
			// today (removing the fork family was measured at 254 failing tests). Scheduled for deletion
			// by the registered "admin upload ownership seam" follow-up.
			if (!ownership) {
				if (import.meta.env?.MODE !== "test") return undefined;
				return { ownerId: "migration-test", epoch: 1, release: async () => undefined };
			}
			const token = await ownership.acquireWriter({
				originKey: "contact-admin",
				identity: { itemId: session.id },
				ownerId: `admin-upload-migration:${newUploadId()}`,
				ttlMs: 2 * 60 * 1000
			});
			if (!token) return undefined;
			return {
				ownerId: token.ownerId,
				epoch: token.epoch,
				release: () => ownership.releaseWriter(token).then(() => undefined)
			};
		},
		async markAdopted(sessionId, lease) {
			return withIdbReopen(() =>
				db.transaction("rw", [...fileUploadSessionControlTables, db.table("leases")], async () => {
					const current = await readFileUploadSession(sessionId);
					if (!current || current.uploadOwnerId || current.uploadOwnerClaimId || current.uploadAdoptionState === "adopted")
						return false as const;
					const durableLease = await db.table("leases").get(writerLeaseKey("contact-admin", { itemId: sessionId }));
					if (
						isActiveAdminDurableWriterLease(durableLease) &&
						(!lease || durableLease.ownerId !== lease.ownerId || durableLease.epoch !== lease.epoch)
					)
						return false as const;
					await updateFileUploadSession(sessionId, {
						// The adapter family is immutable recovery identity, not a
						// read-time default. Stamp pre-marker rows inside this same
						// adoption CAS so a flag flip cannot reroute them after reload.
						endpointFamily: current.endpointFamily ?? "legacy",
						uploadEngineFamily: "legacy",
						uploadSchemaVersion: 2,
						uploadAdapterContractVersion: 1,
						uploadAdoptionState: "adopted"
					});
					return {
						state: "adopted",
						engineFamily: "legacy",
						schemaVersion: 2,
						adapterContractVersion: 1
					} satisfies AdminUploadAdoptionFence;
				})
			);
		},
		async deleteSession(sessionId, expectedFence, lease) {
			return withIdbReopen(() =>
				db.transaction(
					"rw",
					[...fileUploadSessionControlTables, db.fileUploadChunks, db.fileUploadChunkData, db.table("leases")],
					async () => {
						const current = await readFileUploadSession(sessionId);
						const fenceMatches =
							current?.uploadAdoptionState === expectedFence.state &&
							current?.uploadEngineFamily === (expectedFence.engineFamily ?? current?.uploadEngineFamily) &&
							current?.uploadSchemaVersion === (expectedFence.schemaVersion ?? current?.uploadSchemaVersion) &&
							current?.uploadAdapterContractVersion ===
								(expectedFence.adapterContractVersion ?? current?.uploadAdapterContractVersion);
						// This is the final source fence. A late claim updates owner identity in this same
						// database, so it cannot pass this transaction and delete the source bytes.
						const durableLease = await db.table("leases").get(writerLeaseKey("contact-admin", { itemId: sessionId }));
						if (
							!current ||
							!fenceMatches ||
							current.uploadOwnerId ||
							current.uploadOwnerClaimId ||
							(isActiveAdminDurableWriterLease(durableLease) &&
								(!lease || durableLease.ownerId !== lease.ownerId || durableLease.epoch !== lease.epoch))
						)
							return false;
						await db.fileUploadChunkData.where({ sessionId }).delete();
						await db.fileUploadChunks.where({ sessionId }).delete();
						await deleteFileUploadSessionControl(sessionId);
						return true;
					}
				)
			);
		}
	};
}
